// ═══════════════════════════════════════════════
  // PERSONAPATH RESULTS v3.0 — SHOPIFY-PRICED
  // ═══════════════════════════════════════════════
  // Algorithm + product selection from Cloudflare Worker.
  // ALL prices pulled from Shopify product API so they
  // stay in sync with product pages & selling plans.
  console.log('[PersonaPath] Results JS v3.0 loaded');
  // __ppSkipDrawer is managed per-call by PP.Cart.add() — not set globally here.

  // ── API ENDPOINT ──
  var WORKER_URL = 'https://personapath-reviews-worker.personapath.workers.dev/api/quiz-results';
  var SNAPSHOT_URL = 'https://personapath-reviews-worker.personapath.workers.dev/api/quiz-snapshot';
  var LEAD_URL = 'https://personapath-reviews-worker.personapath.workers.dev/api/quiz-lead';

  // ── PHASE 2A: QUIZ SESSION ID ──────────────────────────────────────────────
  // Mint a stable UUID for this quiz session; reuse if already in localStorage.
  // Exposed on window so pp-cart.js can read it without a direct dependency.
  function ppQuizSessionId() {
    var id = localStorage.getItem('pp_quiz_session_id');
    if (!id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      id = crypto.randomUUID();
      localStorage.setItem('pp_quiz_session_id', id);
    }
    return id;
  }
  window.ppQuizSessionId = ppQuizSessionId;

  // ── PHASE 2A: CART ATTRIBUTE ATTACH ───────────────────────────────────────
  // Write the session ID as a cart attribute before every add-to-cart so the
  // order webhook can resolve the quiz snapshot for the order report.
  // Fire-and-forget: a failure here must never block the add-to-cart flow.
  async function ppAttachQuizSessionToCart() {
    try {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _quiz_session_id: ppQuizSessionId() } }),
      });
    } catch (err) {
      console.warn('[PP] cart attribute attach failed', err);
    }
  }
  window.ppAttachQuizSessionToCart = ppAttachQuizSessionToCart;

  // ── LIFE STAGE ────────────────────────────────────────────────────────────
  // 'none' | 'planning' | 'trying' | 'pregnant' | 'breastfeeding' | 'postnatal'. Mirrors lifeStageOf() in the
  // worker (src/algorithm_v3/extract-constraints.js): the quiz asks ONE question (pregnancy_phase) of every
  // woman, and answers given before that question existed are still read the way they used to be. The
  // stronger stage wins when answers disagree.
  function lifeStageFrom(a) {
    a = a || {};
    var phase = a.pregnancy_phase;
    var hormone = Array.isArray(a.hormone_changes_challenges) ? a.hormone_changes_challenges : [];
    if (phase === 'pregnant') return 'pregnant';
    // The old combined yes/no only said she was one of the two; the phase said which, so it is read first.
    if (phase === 'breastfeeding' || phase === 'postpartum' || a.pregnant_or_breastfeeding === 'breastfeeding') return 'breastfeeding';
    if (a.pregnant_or_breastfeeding === 'yes') return 'pregnant';
    if (hormone.indexOf('postpartum_breastfeeding') !== -1) return 'breastfeeding';
    if (phase === 'postnatal') return 'postnatal';
    if (phase === 'trying' || a.planning_pregnancy_12m === 'yes') return 'trying';
    if (phase === 'planning') return 'planning';
    return 'none';
  }

  // The value saved in the quiz snapshot (allowed: pregnant, breastfeeding, neither, trying, postpartum).
  function derivePregnantState(a) {
    return { pregnant: 'pregnant', breastfeeding: 'breastfeeding', postnatal: 'postpartum', trying: 'trying' }[lifeStageFrom(a)] || 'neither';
  }

  // ── PHASE 2A: QUIZ SNAPSHOT POST ──────────────────────────────────────────
  // Push a locked-subset snapshot to the worker when the results page first
  // loads. The worker stores it in KV (keyed by session ID) and writes the
  // session ID to the customer metafield when a customer is logged in.
  // Fire-and-forget: failure must never block the page render.
  async function ppPostQuizSnapshot(quizResults) {
    try {
      var answers = quizResults.answers || quizResults;
      var cartRes = await fetch('/cart.js').catch(function() { return null; });
      var cartToken = (cartRes && cartRes.ok) ? ((await cartRes.json()).token || null) : null;
      var customerId = (window.__pp && window.__pp.customer_id != null) ? String(window.__pp.customer_id) : null;
      // Send every focus area the customer picked. The quiz allows up to 16
      // ("pick as many or as few as you'd like"), so slicing to 3 here silently
      // dropped areas from the stored snapshot — and from everything built on
      // it downstream. The worker validates each id against its own allow-list.
      var focusAreas = Array.isArray(answers.focus_areas) ? answers.focus_areas.slice() : [];
      var body = {
        quiz_session_id: ppQuizSessionId(),
        completed_at: new Date().toISOString(),
        customer_id: customerId,
        cart_token: cartToken,
        // The name they typed, which until 2026-08-19 was the one thing the
        // quiz collected that never reached the snapshot. It sits at
        // quizResults.name, a SIBLING of `answers`, so `raw_answers: answers`
        // above has never carried it. The results page greets them with it and
        // Klaviyo gets it as first_name; only the report pipeline was blind to
        // it, and the report is the surface where being called the right thing
        // matters most.
        //
        // Deliberately preferred over the Shopify account name when writing a
        // report: an account can be named by whoever placed the order, and a
        // letter addressed to the billing name is not a letter to the person
        // who answered the questions.
        name: (quizResults.name || '').trim() || null,
        focus_areas: focusAreas,
        bio_sex: answers.bio_sex || null,
        age_group: answers.age_group || null,
        // Derived from the life-stage answer. This used to read answers.pregnant_state, which the quiz never
        // writes, so every member was saved as "neither" and the action plan never treated a pregnant or
        // breastfeeding member as expecting. "Planning" is not expecting, so it stays "neither".
        pregnant_state: derivePregnantState(answers),
        special_diet: answers.special_diet || 'none',
        raw_answers: answers,
      };
      var res = await fetch(SNAPSHOT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) console.warn('[PP] quiz snapshot failed', res.status, await res.text().catch(function() { return ''; }));
    } catch (err) {
      console.warn('[PP] quiz snapshot error', err);
    }
  }

  async function fetchQuizResults(answers) {
    var payload = { answers: answers };
    // Owner's own device (?pp_internal=1): tell the worker not to count this as a customer.
    // Added only when set, so every customer's request stays exactly as it was.
    if (window.ppInternal) payload.internal = true;
    // A Danish page (/da-dk/) asks for the Danish card texts, hero message and "good to know" row. Added only then, so every English request stays exactly as it was.
    if ((document.documentElement.lang || '').slice(0, 2) === 'da') payload.lang = 'da';
    var res = await fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      var errBody = await res.json().catch(function() { return {}; });
      throw new Error(errBody.error || 'Quiz results request failed');
    }
    return res.json();
  }

  // ── TRANSLATION PASS-THROUGH ──
  // Reasons are now returned in English from the server.
  // t() kept for backward compatibility with rendering code.
  function t(s) { return s || ''; }

  // HTML-escape for all user/API data interpolated into HTML strings
  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function loadResults() {
    const raw = localStorage.getItem('quizResults');
    if (!raw) { window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'pages/supplement-quiz'; return null; }
    try { return JSON.parse(raw); } catch (e) {
      console.warn('[PersonaPath] Corrupted quizResults in localStorage; redirecting to quiz', e);
      localStorage.removeItem('quizResults');
      window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'pages/supplement-quiz';
      return null;
    }
  }

  function joinList(items) {
    const arr = (items || []).filter(Boolean);
    if (arr.length <= 1) return arr[0] || "";
    if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
    return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
  }

  const FOCUS_LABELS = {
    heart: "Heart",
    sleep: "Sleep",
    energy: "Energy",
    immunity: "Immune support",
    bones_joints: "Bones & joints",
    skin: "Skin",
    hair: "Hair",
    nails: "Nails",
    digestion: "Digestion",
    brain: "Focus",
    vision: "Vision",
    stress: "Stress"
  };

  function buildHeroSummary(answers) {
    const focus = Array.isArray(answers.focus_areas) ? answers.focus_areas : (answers.focus_areas ? [answers.focus_areas] : []);
    const topAreas = focus.slice(0, 3).map(v => FOCUS_LABELS[v] || (v || "").replace(/_/g, " "));
    const areaText = joinList(topAreas);

    const signals = [];

    if (answers.special_diet && answers.special_diet !== "none") signals.push(`${answers.special_diet} diet`);
    if (answers.fish_seafood_per_week === "rarely") signals.push("low fish intake");
    if (["wake_often","struggle_fall_stay"].includes(answers.sleep_quality)) signals.push("interrupted sleep");
    if (answers.sleep_quality === "hard_to_fall_asleep") signals.push("trouble falling asleep");
    if (["sometimes_pressure","often_affects_daily"].includes(answers.stress_levels_lately)) signals.push("higher stress lately");
    if (["rarely","weekends"].includes(answers.sunshine_20min)) signals.push("limited sunlight");
    if (["afternoon_dip","low_most_day","low_morning"].includes(answers.energy_day)) signals.push("energy dips");

    const signalsText = signals.length ? `We also took into account ${joinList(signals)}.` : `We also looked at your day-to-day habits and routine.`;

    if (!areaText) {
      return `We’ve used your answers to build a plan that reflects what your body needs right now. ${signalsText}`;
    }

    return `You told us you’d like support with your ${areaText}, and we’ve used your answers to build a plan that reflects what your body needs right now. ${signalsText}`;
  }
  // 1) Handle fallbacks (fix vegan-omega-4 + probiotics edge cases)
  const HANDLE_ALIASES = {};

  const PRODUCT_CACHE = {};      // key: requested handle, value: product.js payload
  const RESOLVED_HANDLE = {};    // key: requested handle, value: actual handle used in fetch (alias)

  // key: handle, value: { group: 'main'|'secondary', position: n }
  // Populated when results render; read by the add-to-cart analytics event.
  // Declared with let rather than const because it is reassigned wholesale on
  // each render, and left empty (not null) so callers never need a guard.
  let PP_REC_GROUP = {};

  // Glass bottles (2026-10-23): a shopper who chose "Personal Bottles" in the quiz sees, links to and adds the glass twin of each recommended
  // product (window.PP_GLASS_TWINS, built in page.results.liquid). The glass product is tried first; the personalised-box handle stays the fallback.
  var _ppWantsGlass = null;
  function ppWantsGlass() {
    if (_ppWantsGlass === null) {
      _ppWantsGlass = false;
      try {
        var q = JSON.parse(localStorage.getItem('quizResults') || 'null');
        var a = q && (q.answers || q);
        _ppWantsGlass = !!(a && a.delivery_format === 'glass');
      } catch (e) {}
    }
    return _ppWantsGlass;
  }

  // The glass jar picture of a product (cut-out + the numbers pp-jar-names.js needs to write her name on it), only for a shopper who chose bottles.
  // window.PP_GLASS_JARS is built in page.results.liquid, keyed by the handle the worker recommends. Not there: the pill picture stays.
  function ppJarFor(handle) {
    return (ppWantsGlass() && window.PP_GLASS_JARS && window.PP_GLASS_JARS[handle]) || null;
  }

  function handleCandidates(handle) {
    var list = [handle].concat(HANDLE_ALIASES[handle] || []);
    var twin = window.PP_GLASS_TWINS && window.PP_GLASS_TWINS[handle];
    if (twin && ppWantsGlass()) list.unshift(twin);
    return list;
  }

  // Detect Shopify market root for multi-currency support
  var SHOPIFY_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  async function ensureProductCached(handle) {
    if (PRODUCT_CACHE[handle]) return true;

    const candidates = handleCandidates(handle);
    for (const h of candidates) {
      try {
        const res = await fetch(`${SHOPIFY_ROOT}products/${h}.js`);
        if (!res.ok) continue;
        const data = await res.json();
        PRODUCT_CACHE[handle] = data;
        RESOLVED_HANDLE[handle] = h;
        return true;
      } catch(e) {}
    }
    return false;
  }

  async function prefetchProducts(handles) {
    const unique = Array.from(new Set((handles || []).filter(Boolean)));
    await Promise.all(unique.map(h => ensureProductCached(h)));
  }

  function resolvedHandleFor(handle) {
    return RESOLVED_HANDLE[handle] || handle;
  }


  function normalizeSuffix(s) {
    const raw = (s || "").toString().trim();
    if (!raw) return "";
    if (raw.startsWith("/")) return ` ${raw}`;
    if (raw.startsWith(" /")) return raw;
    return ` ${raw}`;
  }

  // Read custom.price_suffix from PP_PRODUCT_META.
  // Three-step lookup: algorithm handle → actual Shopify handle from PRODUCT_CACHE → resolved handle.
  // PRODUCT_CACHE[handle].handle is the handle Shopify returned for this product and is
  // guaranteed to match a key in PP_PRODUCT_META (both originate from the same product object).
  function _rawSuffixFor(handle) {
    var META = window.PP_PRODUCT_META || {};
    // The product actually shown comes first: for a shopper who chose bottles that is the glass twin ("| 90 Days"), not the box product the worker recommended ("| 30 Days").
    var entry = META[resolvedHandleFor(handle)];
    if (entry && entry.priceSuffix) return entry.priceSuffix.toString().trim();
    entry = META[handle];
    if (entry && entry.priceSuffix) return entry.priceSuffix.toString().trim();
    var cached = PRODUCT_CACHE[handle];
    var shopifyHandle = cached && cached.handle;
    if (shopifyHandle && shopifyHandle !== handle) {
      entry = META[shopifyHandle];
      if (entry && entry.priceSuffix) return entry.priceSuffix.toString().trim();
    }
    var rh = resolvedHandleFor(handle);
    entry = META[rh];
    if (entry && entry.priceSuffix) return entry.priceSuffix.toString().trim();
    return '';
  }

  function getPriceSuffix(handle) {
    return normalizeSuffix(_rawSuffixFor(handle));
  }

  function getPriceDays(handle) {
    return _rawSuffixFor(handle);
  }
  // 4) Pull dose + short blurb from product description (product.js -> description HTML)
  const DESC_CACHE = {}; // per requested handle

  function stripAndNormalize(txt) {
    return (txt || "").replace(/\s+/g, " ").trim();
  }

  function looksLikeDoseLine(line) {
    const l = line.toLowerCase();
    const hasUnit = /(capsule|capsules|tablet|tablets|softgel|softgels|scoop|scoops|serving|servings|kapsel|kapsler|tablet|tabletter)/i.test(l);
    const hasFreq = /(per day|daily|\/day|a day|pr\.? dag|dagligt|om dagen)/i.test(l);
    const hasNumber = /\d/.test(l);
    return hasUnit && (hasFreq || hasNumber);
  }

  function pickDose(lines) {
    for (const line of lines) {
      if (looksLikeDoseLine(line)) return stripAndNormalize(line);
    }
    return "";
  }

  function pickBlurb(lines) {
    // pick a short paragraph-like line that is NOT dosage and is reasonably short
    const candidates = lines
      .map(stripAndNormalize)
      .filter(Boolean)
      .filter(l => l.length >= 30 && l.length <= 220)
      .filter(l => !looksLikeDoseLine(l))
      .filter(l => !/(ingredients|næringsindhold|nutrition|directions|dosage|anvendelse|opbevaring|warning|advarsel)/i.test(l));
    return candidates[0] || "";
  }

  function parseProductDescription(handle) {
    if (DESC_CACHE[handle]) return DESC_CACHE[handle];

    const p = PRODUCT_CACHE[handle];
    const html = p && (p.description || p.body_html) ? (p.description || p.body_html) : "";
    if (!html) {
      DESC_CACHE[handle] = { dose: "", blurb: "" };
      return DESC_CACHE[handle];
    }

    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const nodes = Array.from(doc.querySelectorAll("p, li"));
      const lines = nodes
        .map(el => stripAndNormalize(el.textContent))
        .filter(Boolean)
        .slice(0, 12);

      const dose = pickDose(lines);
      const blurb = pickBlurb(lines);

      DESC_CACHE[handle] = { dose, blurb };
      return DESC_CACHE[handle];
    } catch(e) {
      DESC_CACHE[handle] = { dose: "", blurb: "" };
      return DESC_CACHE[handle];
    }
  }

  // 5) Added-state: delegated to PP.Cart (see assets/pp-cart.js)
  // PP.Cart persists handle→{lineKey,variantId,sellingPlanId} and syncs cross-tab.

  function setButtonAdded(button) {
    if (!button) return;
    delete button.dataset.ppBusy;
    button.disabled = false;
    button.classList.add('is-added');
    button.classList.remove('btn-error');
    while (button.firstChild) button.removeChild(button.firstChild);
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '13');
    svg.setAttribute('height', '13');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var poly = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    poly.setAttribute('points', '20 6 9 17 4 12');
    svg.appendChild(poly);
    button.appendChild(svg);
    button.appendChild(document.createTextNode(modalButtonLabel(button, 'Added')));
  }

  function setButtonIdle(button) {
    if (!button) return;
    delete button.dataset.ppBusy;
    button.disabled = false;
    while (button.firstChild) button.removeChild(button.firstChild);
    // The modal's bar draws its plus as an icon, the way "Added" draws its check; the card keeps the typed "+ Add".
    if (button.hasAttribute('data-pp-modal-add')) button.appendChild(modalPlusIcon());
    button.appendChild(document.createTextNode(modalButtonLabel(button, '+ Add')));
    button.classList.remove('is-added');
    button.classList.remove('btn-error');
  }

  function modalPlusIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '13');
    svg.setAttribute('height', '13');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M12 5v14M5 12h14');
    svg.appendChild(path);
    return svg;
  }

  // The recommendation modal's footer button (data-pp-modal-add) is a full-width bar that says "Add · 229,00 kr" and
  // "Added · 229,00 kr" instead of the card's "+ Add" and "Added". The price comes from refreshWhyAddButton().
  function modalButtonLabel(button, cardLabel) {
    if (!button.hasAttribute('data-pp-modal-add')) return cardLabel;
    var word = cardLabel === 'Added' ? 'Added' : 'Add';
    var price = button.getAttribute('data-pp-price');
    return price ? word + ' · ' + price : word;
  }

  // After an add or a remove, bring every button for that product (the card behind the modal, the modal's own bar) in step.
  function syncAddButtonsFor(handle) {
    var added = PP.Cart.has(handle);
    document.querySelectorAll('.pp-add-btn[data-handle]').forEach(function (btn) {
      if (btn.getAttribute('data-handle') !== handle || btn.dataset.ppBusy) return;
      if (added) setButtonAdded(btn); else setButtonIdle(btn);
    });
  }

  // ── Toast notification ──────────────────────────
  // Single timer ref; if the toast is already visible we just reset the timer
  // rather than running the rAF chain again (avoids flicker on rapid adds).
  var _notifTimer = null;

  function _getOrCreateNotif() {
    var el = document.getElementById('ppCartNotif');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'ppCartNotif';
    el.className = 'pp-cart-notif';
    var check = document.createElement('span');
    check.className = 'pp-cart-notif__check';
    check.textContent = '✓';
    var msgEl = document.createElement('span');
    msgEl.className = 'pp-cart-notif__msg';
    var closeBtn = document.createElement('button');
    closeBtn.className = 'pp-cart-notif__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', function () { el.classList.remove('is-visible'); });
    el.appendChild(check);
    el.appendChild(msgEl);
    el.appendChild(closeBtn);
    document.body.appendChild(el);
    return el;
  }

  function showCartNotification(type) {
    var msg = type === 'removed' ? 'Removed from cart' : 'Added to cart';
    var el = _getOrCreateNotif();
    el.querySelector('.pp-cart-notif__msg').textContent = msg;

    clearTimeout(_notifTimer);

    if (el.classList.contains('is-visible')) {
      // Already visible: update message and extend the timer — no re-animation needed
      _notifTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 2500);
      return;
    }

    // Not yet visible: force a reflow to arm the CSS transition, then show
    void el.offsetHeight;
    el.classList.add('is-visible');
    _notifTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 2500);
  }

  // ── Remove a rec-card product from the cart ─────
  // PP.Cart.remove() uses the stored line key from the add() response,
  // so we never accidentally target the wrong line for the same variant.
  async function _ppRecCardRemove(handle, button) {
    button.disabled = true;
    button.dataset.ppBusy = '1';
    while (button.firstChild) button.removeChild(button.firstChild);
    button.appendChild(document.createTextNode('Removing…'));
    button.classList.remove('is-added');

    try {
      var entry = PP.Cart.getEntry(handle);
      var cart = await PP.Cart.remove(handle, entry ? entry.variantId : null);
      setButtonIdle(button);
      syncAddButtonsFor(handle);
      updateCartCount(cart);
      showCartNotification('removed');
      if (window.ppTrack) {
        var rmMeta = PP_REC_GROUP[handle] || {};
        ppTrack('product_removed_from_cart', {
          product: handle,
          rec_group: rmMeta.group || 'unknown',
          rec_position: rmMeta.position || null,
          source: 'results_page'
        });
      }
    } catch (err) {
      console.error('[PP] Cart remove error:', err);
      setButtonIdle(button);
    }
  }

  // ── Sync rec-card button states with live cart ──
  // Calls PP.Cart.sync() which verifies each stored handle against
  // the live cart and only removes entries it can confirm are absent.
  // Handles without a known variantId are left untouched (safe for
  // partial-load states where product data isn't cached yet).
  async function syncRecCardButtons() {
    var cart = await PP.Cart.sync();
    if (!cart) return;
    document.querySelectorAll('.pp-add-btn[data-handle]').forEach(function (btn) {
      if (btn.dataset.ppBusy) return; // skip mid-operation buttons
      var handle = btn.getAttribute('data-handle');
      if (PP.Cart.has(handle)) {
        setButtonAdded(btn);
      } else {
        setButtonIdle(btn);
      }
    });
    updateCartCount(cart);
  }

  // ───────────────────────────────────────────────
  // Price + subscription helpers
  // ───────────────────────────────────────────────

  // Format cents to display string, respecting the active Shopify currency
  // DKK: "435 kr" or "435,30 kr"  |  EUR: "€59,75"
  function centsToDisplay(cents) {
    var amount = Math.round(cents) / 100;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'DKK';
    if (currency === 'EUR') {
      if (amount % 1 === 0) return '€' + amount;
      return '€' + amount.toFixed(2).replace('.', ',');
    }
    // Default: DKK
    if (amount % 1 === 0) return amount + ' kr';
    return amount.toFixed(2).replace('.', ',') + ' kr';
  }
  // Backward compat alias
  var centsToKr = centsToDisplay;

  // Returns null when Shopify has not given us a price for this handle. Callers must
  // render nothing rather than substitute a number.
  //
  // There used to be a fallback here to a price hardcoded in the worker. It was wrong
  // twice over. The number was in kroner but got printed with whatever symbol the active
  // market uses, so an EU customer saw "435 kr" as "EUR 435" instead of about EUR 58, the
  // krone being pegged near 7.46 to the euro. And the hardcoded numbers had drifted from
  // the real ones anyway (Skin+ 199 against a live 249), so it was wrong in kroner too.
  //
  // It only fired when the product fetch failed, which nothing surfaced: ensureProductCached
  // swallows a 404 with `continue`, swallows a throw with an empty catch, and returns false
  // to a caller that discards it. A quietly wrong price is worse than a missing one, so the
  // price is now simply absent when we do not know it.
  function getCachedPriceKr(handle) {
    const p = PRODUCT_CACHE[handle];
    if (p && typeof p.price === "number") {
      return centsToDisplay(p.price);
    }
    return null;
  }

  function defaultDoseFromFormat(format) {
    if (format === "tablet") return "1 Tablet per day";
    if (format === "powder") return "1 Scoop per day";
    if (format === "bottle") return "1 Serving per day";
    return "1 Capsule per day";
  }

  function getProductImage(handle) {
    if (PRODUCT_IMAGE_BY_HANDLE[handle]) return PRODUCT_IMAGE_BY_HANDLE[handle];
    const p = PRODUCT_CACHE[handle];
    return (p && p.featured_image) ? p.featured_image : "";
  }

  // ── Selling plan helpers (reads discount from Shopify product data) ──

  function getSellingPlanInfo(handle) {
    var p = PRODUCT_CACHE[handle];
    var groups = p && p.selling_plan_groups ? p.selling_plan_groups : [];
    for (var i = 0; i < groups.length; i++) {
      var plans = groups[i] && groups[i].selling_plans ? groups[i].selling_plans : [];
      if (plans.length && plans[0] && plans[0].id) {
        var adj = plans[0].price_adjustments && plans[0].price_adjustments[0];
        var discountPct = 0;
        if (adj && adj.value_type === 'percentage') {
          discountPct = Number(adj.value) || 0;
        }
        return { id: plans[0].id, discountPct: discountPct };
      }
    }
    return null;
  }

  function getSubscriptionPlanId(handle) {
    var info = getSellingPlanInfo(handle);
    return info ? info.id : null;
  }

  function hasSubscription(handle) {
    return !!getSubscriptionPlanId(handle);
  }

  function getWarningsForProduct(warnings, productId) {
    return (warnings || []).filter(w => w.product === productId);
  }

  /**
   * The subset of a product's warnings worth showing the customer in the
   * why-modal. The worker emits these only when the user actually declared the
   * medication or health flag behind them, so reaching this point already means
   * the note is relevant to this person.
   *
   * Two kinds are filtered out:
   *   POSITIVE — BOOST notes ("statins deplete CoQ10, so this may help more").
   *              They already raised the score and read as encouragement, so
   *              they do not belong under a caution heading.
   *   EXCLUDE  — describes a product removed from the plan, whose modal is
   *              therefore unreachable. Filtered defensively.
   *
   * Identical messages are collapsed: two declared medications can produce the
   * same timing note for one product.
   */
  function getDisplayWarnings(warnings, productId) {
    var seen = Object.create(null);
    return getWarningsForProduct(warnings, productId).filter(function(w) {
      if (!w || !w.message) return false;
      if (w.severity === 'POSITIVE' || w.action === 'EXCLUDE') return false;
      if (seen[w.message]) return false;
      seen[w.message] = true;
      return true;
    });
  }

  /**
   * Build stock availability notice banner.
   * Shows a warm message when products were swapped or removed
   * due to inventory.
   */

  function iconSvgs() {
    return `
      <span class="pp-card-icons" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none"><path d="M12 2l8 4v6c0 5-3.5 9.5-8 10-4.5-.5-8-5-8-10V6l8-4Z" stroke-width="1.8" stroke-linejoin="round"/></svg>
        <svg viewBox="0 0 24 24" fill="none"><path d="M10 2v5l-5 9a4 4 0 0 0 3.5 6h7A4 4 0 0 0 19 16l-5-9V2" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 13h7" stroke-width="1.8" stroke-linecap="round"/></svg>
        <svg viewBox="0 0 24 24" fill="none"><path d="M12 21c5-3 8-7 8-12V5l-8 4-8-4v4c0 5 3 9 8 12Z" stroke-width="1.8" stroke-linejoin="round"/></svg>
      </span>
    `;
  }
// ═══════════════════════════════════
  // HERO NUTRIENT OVERVIEW BOX
  // ═══════════════════════════════════

  // Map nutrient names to a simplified base name for grouping
  var NUTRIENT_GROUP_MAP = {
    'magnesium bisglycinate': 'Magnesium',
    'magnesium citrate': 'Magnesium',
    'magnesium oxide': 'Magnesium',
    'magnesium taurate': 'Magnesium',
    'magnesium glycinate': 'Magnesium',
    'magnesium malate': 'Magnesium',
    'magnesium l-threonate': 'Magnesium',
    'zinc citrate': 'Zinc',
    'zinc sulfate': 'Zinc',
    'zinc glycinate': 'Zinc',
    'zinc picolinate': 'Zinc',
    'vitamin b1': 'Vitamin B1',
    'vitamin b-1 (thiamine)': 'Vitamin B1',
    'vitamin b-1': 'Vitamin B1',
    'thiamine': 'Vitamin B1',
    'vitamin b2': 'Vitamin B2',
    'vitamin b-2': 'Vitamin B2',
    'riboflavin': 'Vitamin B2',
    'vitamin b3': 'Vitamin B3',
    'vitamin b-3': 'Vitamin B3',
    'niacin': 'Vitamin B3',
    'vitamin b5': 'Vitamin B5',
    'vitamin b-5': 'Vitamin B5',
    'pantothenic acid': 'Vitamin B5',
    'vitamin b6': 'Vitamin B6',
    'vitamin b-6': 'Vitamin B6',
    'pyridoxine': 'Vitamin B6',
    'vitamin b9': 'Vitamin B9',
    'vitamin b-9': 'Vitamin B9',
    'folate': 'Vitamin B9',
    'folic acid': 'Vitamin B9',
    'vitamin b12': 'Vitamin B12',
    'vitamin b-12': 'Vitamin B12',
    'methylcobalamin': 'Vitamin B12',
    'cobalamin': 'Vitamin B12',
    'vitamin d3': 'Vitamin D',
    'vitamin d-3': 'Vitamin D',
    'vitamin d': 'Vitamin D',
    'cholecalciferol': 'Vitamin D',
    'vitamin c': 'Vitamin C',
    'ascorbic acid': 'Vitamin C',
    'vitamin e': 'Vitamin E',
    'vitamin k2': 'Vitamin K2',
    'vitamin k-2': 'Vitamin K2',
    'vitamin a': 'Vitamin A',
    'vitamin a (retinol)': 'Vitamin A',
    'retinol': 'Vitamin A',
    'beta-carotene': 'Vitamin A',
    'betacarotene': 'Vitamin A',
    'calcium': 'Calcium',
    'calcium citrate': 'Calcium',
    'iron': 'Iron',
    'iron bisglycinate': 'Iron',
    'selenium': 'Selenium',
    'iodine': 'Iodine',
    'chromium': 'Chromium',
    'copper': 'Copper',
    'manganese': 'Manganese',
    'coq10': 'CoQ10',
    'coenzyme q10': 'CoQ10',
    'd-biotin': 'Biotin',
    'biotin': 'Biotin',
    'collagen': 'Collagen',
    'hyaluronic acid': 'Hyaluronic Acid'
  };

  function getNutrientGroupKey(name) {
    var lower = name.toLowerCase().trim();
    // Exact match first
    if (NUTRIENT_GROUP_MAP[lower]) return NUTRIENT_GROUP_MAP[lower];
    // Substring match: if the name contains a known key, group it
    var keys = Object.keys(NUTRIENT_GROUP_MAP);
    for (var i = 0; i < keys.length; i++) {
      if (lower.indexOf(keys[i]) !== -1 || keys[i].indexOf(lower) !== -1) {
        return NUTRIENT_GROUP_MAP[keys[i]];
      }
    }
    // Fallback: check if name starts with a common base word
    var baseWords = [
      ['magnesium', 'Magnesium'], ['zinc', 'Zinc'], ['iron', 'Iron'],
      ['calcium', 'Calcium'], ['selenium', 'Selenium'], ['iodine', 'Iodine'],
      ['chromium', 'Chromium'], ['copper', 'Copper'], ['manganese', 'Manganese'],
      ['collagen', 'Collagen'], ['biotin', 'Biotin'], ['folate', 'Vitamin B9'],
      ['coq10', 'CoQ10'], ['coenzyme', 'CoQ10']
    ];
    for (var j = 0; j < baseWords.length; j++) {
      if (lower.indexOf(baseWords[j][0]) !== -1) return baseWords[j][1];
    }
    return name;
  }

  // ── PROFILE BLOCK (top of the page) ──
  // Her first name, up to three words about who she is (assets/pp-profile-traits.js), the focus areas she picked (names
  // only, in her pick order) and the opening message of her plan. The names of the areas come from window.PP_PROFILE_I18N
  // (set in the template, English and Danish); the English list below is only a safety net.
  var PROFILE_AREA_NAMES = {
    immunity: 'Immunity', energy: 'Energy', heart: 'Heart', brain: 'Brain', vision: 'Vision', sleep: 'Sleep',
    digestion: 'Digestion', womens_health: "Women's Health", mens_health: "Men's Health", beauty: 'Hair, Skin & Nails',
    mood_stress: 'Mood & Stress', sport: 'Muscles & Recovery', bones_joints: 'Bones & Joints',
    hormonal_balance: 'Hormonal Balance', longevity: 'Longevity', maternity: 'Maternity'
  };

  function profileFill(listEl, labels) {
    if (!listEl) return;
    listEl.textContent = '';
    labels.forEach(function(label) {
      var li = document.createElement('li');
      li.textContent = label;
      listEl.appendChild(li);
    });
    listEl.hidden = !labels.length;
  }

  // The greeting in front of her name ("Hey Anna", "Good evening Anna"). It comes from a small pool (window.PP_PROFILE_I18N.greetings)
  // that depends on her age group (younger = more casual, older = a little more formal), lightly on sex, plus a time-of-day greeting.
  // The pick is a stable hash of her name and answers, so one person sees the same greeting on every reload while different people
  // see different ones.
  function profileGreeting(answers, firstName, g) {
    g = g || {};
    var age = answers.age_group;
    var band = (age === '18_24' || age === '25_34') ? 'young' : (age === '50_64' || age === '65_plus') ? 'older' : 'middle';
    var pool = (g[band] || ['Hi']).slice();
    var tilt = g.tilt && g.tilt[answers.bio_sex === 'male' ? 'male' : answers.bio_sex === 'female' ? 'female' : 'none'];
    if (band !== 'older' && tilt && tilt.length) pool = pool.concat(tilt);   // "Hey" would be out of tune for the older group
    var hour = new Date().getHours();
    var timeWord = hour >= 5 && hour < 12 ? g.morning : hour >= 12 && hour < 18 ? g.afternoon : hour >= 18 && hour < 23 ? g.evening : '';
    if (timeWord) pool.push(timeWord);
    var key = String(firstName).toLowerCase() + '|' + (age || '') + '|' + (answers.bio_sex || '');
    var h = 0;
    for (var i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return pool[h % pool.length];
  }

  function buildProfileBlock(results, answers, name) {
    var root = document.getElementById('ppProfile');
    if (!root) return;
    var i18n = window.PP_PROFILE_I18N || {};
    var areaNames = i18n.areas || {};
    answers = answers || {};

    // Name: the first word she typed. Without one the heading says "Your profile".
    var firstName = String(name || _lastName || '').trim().split(/\s+/)[0];
    var helloEl = document.getElementById('ppProfileHello');
    var firstEl = document.getElementById('ppProfileFirst');
    if (firstEl) firstEl.textContent = firstName || i18n.fallback_title || 'Your profile';
    if (helloEl) helloEl.textContent = firstName ? profileGreeting(answers, firstName, i18n.greetings) : '';

    // Words about her.
    var traits = [];
    if (window.PPProfileTraits) {
      try { traits = window.PPProfileTraits.derive(answers, { stage: lifeStageFrom(answers), max: 3 }).traits; } catch (e) { traits = []; }
    }
    profileFill(document.getElementById('ppProfileTraits'), traits);

    // Focus areas she picked: names only, each once.
    var picked = Array.isArray(answers.focus_areas) ? answers.focus_areas : (answers.focus_areas ? [answers.focus_areas] : []);
    var seen = {};
    var areas = [];
    // Men get two separate tiles in the quiz, "Hair" and "Skin & Nails", where women get one, "Hair, Skin & Nails":
    // the bubbles show the tiles that were actually ticked.
    var menTiles = { beauty_hair: areaNames.beauty_hair || 'Hair', beauty_skin_nails: areaNames.beauty_skin_nails || 'Skin & Nails' };
    var ticked = Array.isArray(answers.beauty_tiles) ? answers.beauty_tiles.filter(function(t) { return menTiles[t]; }) : [];
    picked.forEach(function(id) {
      if (!id || seen[id]) return;
      seen[id] = true;
      if (id === 'beauty' && answers.bio_sex === 'male' && ticked.length) {
        ticked.forEach(function(t) { areas.push(menTiles[t]); });
        return;
      }
      areas.push(areaNames[id] || PROFILE_AREA_NAMES[id] || String(id).replace(/_/g, ' '));
    });
    // Fewer than three bubbles: top up to three with areas her other answers point to (assets/pp-profile-traits.js).
    // They look exactly like her own picks (David's choice) and are only added when an answer really points there.
    if (areas.length < 3 && window.PPProfileTraits && window.PPProfileTraits.suggestAreas) {
      try {
        window.PPProfileTraits.suggestAreas(answers, { picked: picked, max: 3 - areas.length, stage: lifeStageFrom(answers) }).forEach(function(id) {
          areas.push(areaNames[id] || PROFILE_AREA_NAMES[id] || String(id).replace(/_/g, ' '));
        });
      } catch (e) { /* the bubbles she picked are enough */ }
    }
    profileFill(document.getElementById('ppProfileAreas'), areas);

    // The opening message: composed by the worker from pre-written parts (src/results-copy/hero.js) and delivered as
    // results.heroMessage = { paragraphs: [...] }. The old one-sentence template is only a fallback for a response without it.
    var textEl = document.getElementById('ppProfileText');
    if (textEl) {
      var paragraphs = results && results.heroMessage && Array.isArray(results.heroMessage.paragraphs) ? results.heroMessage.paragraphs : [];
      textEl.textContent = '';
      if (paragraphs.length) {
        paragraphs.forEach(function(text) {
          var para = document.createElement('p');
          para.textContent = text;
          textEl.appendChild(para);
        });
      } else if (window.PPResultsHeaderCopy) {
        var fallback = document.createElement('p');
        fallback.textContent = window.PPResultsHeaderCopy.buildSummary(answers);
        textEl.appendChild(fallback);
      }
      textEl.hidden = !textEl.childNodes.length;
    }

    // Nothing to recommend at all: the message ends by pointing at the whole collection, so there is a button to it
    // (the Vitamins page, the same one as the header's "Vitamins" link; the label and the market-aware address come from the template).
    var cta = document.getElementById('ppProfileCta');
    if (cta) {
      var recs = (results && results.recommendations) || {};
      var nothing = !(recs.main || []).length && !(recs.secondary || []).length;
      if (nothing && i18n.collection_url) {
        cta.href = i18n.collection_url;
        cta.textContent = i18n.collection_cta || 'Browse all vitamins';
        cta.hidden = false;
      } else {
        cta.hidden = true;
      }
    }
  }

  function parseDoseNumber(doseStr) {
    if (!doseStr) return 0;
    var match = doseStr.match(/([\d.,]+)/);
    if (!match) return 0;
    return parseFloat(match[1].replace(',', '.')) || 0;
  }

  function parseDoseUnit(doseStr) {
    if (!doseStr) return '';
    var match = doseStr.match(/[\d.,]+\s*(.+)/);
    if (!match) return '';
    return match[1].trim();
  }

  function toggleHeroNutrients() {
    var grid = document.getElementById('heroNutrientsGrid');
    var toggleBtn = document.getElementById('heroNutrientsToggle');
    var toggleText = document.getElementById('heroNutrientsToggleText');
    if (!grid || !toggleBtn) return;

    var isExpanded = toggleBtn.classList.contains('is-expanded');
    var allItems = grid.querySelectorAll('.pp-hero-nutrient');

    if (isExpanded) {
      // Collapse: hide items beyond 9
      allItems.forEach(function(el, idx) {
        if (idx >= 9) el.classList.add('is-hidden');
      });
      toggleBtn.classList.remove('is-expanded');
      if (toggleText) toggleText.textContent = 'See all ' + allItems.length + ' nutrients';
    } else {
      // Expand: show all
      grid.querySelectorAll('.pp-hero-nutrient.is-hidden').forEach(function(el) {
        el.classList.remove('is-hidden');
      });
      toggleBtn.classList.add('is-expanded');
      if (toggleText) toggleText.textContent = 'Show less';
    }
  }

  // ═══════════════════════════════════
  // RECOMMENDATION CARDS
  // ═══════════════════════════════════

  var AREA_ICONS = {
    energy: '&#9889;',
    sleep: '&#9790;',
    heart: '&#9829;',
    immunity: '&#128737;',
    bones_joints: '&#129471;',
    skin: '&#10024;',
    hair: '&#10024;',
    nails: '&#10024;',
    digestion: '&#127793;',
    brain: '&#129504;',
    vision: '&#128065;',
    stress: '&#127807;',
    maternity: '&#128118;',
    pregnancy: '&#128118;'
  };

  function getAreaIcon(area) {
    var lower = (area || '').toLowerCase();
    var keys = Object.keys(AREA_ICONS);
    for (var i = 0; i < keys.length; i++) {
      if (lower.indexOf(keys[i]) !== -1) return AREA_ICONS[keys[i]];
    }
    return '&#9672;';
  }

  // ── CARD TAGS ──
  // The little bubbles under a recommendation card: one or two areas that are HERS and that the card speaks to. The worker sends
  // every card p.areas = { primary: id|null, product: [ids] } (the area of the card's own reason, and the areas the product speaks
  // to). A tag is shown when the area is one she picked or one her other answers point to (the same suggestAreas() rule that tops
  // up the bubbles in her profile): the card's own reason area first, then her picks in her order, but only areas the product
  // really speaks to, so a tag never contradicts the text beside it. A card with nothing of hers behind it (a diet, sun or habit
  // reason) gets no area tag, or only its own reason's area when that is not a private one (and a kind label, see below). Without
  // p.areas (an older response, the dev gallery) the card keeps its old pill.
  var CARD_TAG_PRIVATE = ['mens_health', 'womens_health', 'maternity', 'hormonal_balance', 'longevity'];
  var _cardTagCtx = null;

  function makeCardTagContext(answers) {
    if (!answers) return null;
    var names = (window.PP_PROFILE_I18N && window.PP_PROFILE_I18N.areas) || {};
    var picked = Array.isArray(answers.focus_areas) ? answers.focus_areas : (answers.focus_areas ? [answers.focus_areas] : []);
    var suggested = [];
    if (window.PPProfileTraits && window.PPProfileTraits.suggestAreas) {
      try { suggested = window.PPProfileTraits.suggestAreas(answers, { picked: picked, max: 16, stage: lifeStageFrom(answers) }); } catch (e) { suggested = []; }
    }
    // Men get "Hair" and "Skin & Nails" as two tiles where women get one, so a men's beauty tag names the tiles that were ticked.
    var menTiles = [];
    if (answers.bio_sex === 'male' && Array.isArray(answers.beauty_tiles)) {
      answers.beauty_tiles.forEach(function(t) {
        if (t === 'beauty_hair' || t === 'beauty_skin_nails') menTiles.push(names[t] || (t === 'beauty_hair' ? 'Hair' : 'Skin & Nails'));
      });
    }
    return { mine: picked.concat(suggested), names: names, menTiles: menTiles };
  }

  // The labels for one card, at most two, or null when the card has no area data (the caller then keeps the old pill).
  function cardTagLabels(p) {
    var ctx = _cardTagCtx;
    var areas = p && p.areas;
    if (!ctx || !areas || !Array.isArray(areas.product)) return null;
    var ids = [];
    function add(id) { if (id && ids.indexOf(id) === -1) ids.push(id); }
    // A card's women's-health area is her hormonal-balance pick when that is the one she made.
    function hers(id) {
      if (ctx.mine.indexOf(id) !== -1) return id;
      return id === 'womens_health' && ctx.mine.indexOf('hormonal_balance') !== -1 ? 'hormonal_balance' : null;
    }
    function product(id) {
      return areas.product.indexOf(id) !== -1 || (id === 'hormonal_balance' && areas.product.indexOf('womens_health') !== -1);
    }
    // 1. the area of the card's own reason, 2. her areas that are also among the reasons behind the card, 3. the rest of hers in her
    // order. All of them only when the product speaks to the area, so the same product carries different tags for different shoppers.
    if (areas.primary) add(hers(areas.primary));
    (Array.isArray(areas.reason) ? areas.reason : []).forEach(function(id) { var h = hers(id); if (h && product(h)) add(h); });
    ctx.mine.forEach(function(id) { if (product(id)) add(id); });
    // Nothing of hers among the product's areas: only the card's own reason area (her answers triggered it, so it is hers to see), and
    // never a private one. No tag at all beats a tag that has nothing to do with her.
    if (!ids.length && areas.primary && CARD_TAG_PRIVATE.indexOf(areas.primary) === -1) add(areas.primary);
    var labels = [];
    ids.forEach(function(id) {
      if (id === 'beauty' && ctx.menTiles.length) ctx.menTiles.forEach(function(t) { labels.push(t); });
      else labels.push(ctx.names[id] || PROFILE_AREA_NAMES[id] || String(id).replace(/_/g, ' '));
    });
    labels = labels.slice(0, 2);
    // A product no authorised claim can be linked to (herbs, an amino acid, live bacteria) says what it IS beside its area tag, or
    // alone when none of her areas fits: "Botanical", "Traditional herbs" (the worker sends areas.kind).
    if (labels.length < 2 && areas.kind) {
      var kinds = (window.PP_PROFILE_I18N && window.PP_PROFILE_I18N.kinds) || {};
      labels.push(kinds[areas.kind] || CARD_KIND_NAMES[areas.kind] || String(areas.kind).replace(/_/g, ' '));
    }
    return labels;
  }

  // English safety net for the names of areas.kind (the template carries both languages in PP_PROFILE_I18N.kinds).
  var CARD_KIND_NAMES = { botanical: 'Botanical', amino_acid: 'Amino acid', probiotic: 'Probiotic' };

  // ── Card rows: tags, text and the price / Add row line up across the cards of a row ──
  // The cards of a row are the same height and the price and Add button sit at the bottom (CSS). The name, the tag row and the
  // description each get the height of the tallest in their row, so the tags and the text start at the same place on every card even
  // when a name wraps to two lines; a shorter text leaves its space below it. Line counts depend on the width, so this is measured, not guessed: on first
  // render, when the cards become visible, and whenever the width changes. A stacked list (phones, main tier) needs none.
  // ── Card layout: v1 (before 2026-10-17) or v2 (the trial) ──
  // 'v2' puts the class .pp-recs-v2 on #ppRecommendations, which switches on the v2 CSS in templates/page.results.liquid.
  // ?card_layout=v1 or ?card_layout=v2 in the address overrides this for that visit (to compare the two).
  // TO ROLL BACK FOR EVERYONE: change 'v2' to 'v1' and push this file. Backup of the old files: docs/rollbacks/results-cards-layout-v1-2026-10-17/
  var CARD_LAYOUT = 'v2';
  function cardLayout() {
    try {
      var q = new URLSearchParams(window.location.search).get('card_layout');
      if (q === 'v1' || q === 'v2') return q;
    } catch (e) { /* no URLSearchParams: use the constant */ }
    return CARD_LAYOUT;
  }

  // v2: a product name stays on ONE line. A name wider than its place shrinks (never below 13px) instead of wrapping; only a name that
  // is still too wide at 13px (a long name on a very narrow phone) is allowed to wrap after all.
  function fitRecCardNames() {
    var cont = document.getElementById('ppRecommendations');
    if (!cont || !cont.classList.contains('pp-recs-v2')) return;
    Array.prototype.forEach.call(cont.querySelectorAll('.pp-rec-card__name'), function(el) {
      el.style.fontSize = '';
      el.style.whiteSpace = '';
      if (window.getComputedStyle(el).whiteSpace !== 'nowrap') return;   // a name that may wrap (the phone carousel) is left alone
      var size = parseFloat(window.getComputedStyle(el).fontSize) || 20;
      while (el.scrollWidth > el.clientWidth + 0.5 && size > 13) { size -= 0.5; el.style.fontSize = size + 'px'; }
      if (el.scrollWidth > el.clientWidth + 0.5) { el.style.fontSize = ''; el.style.whiteSpace = 'normal'; }
    });
  }

  // v2 on phones, main row: where the pill is inside its picture. The pictures have a lot of empty space around the pill and it is
  // different in each, so the picture is measured once (cached): { ar, l, r, t, b } = aspect ratio and the pill's edges as fractions.
  // Only pixels darker than 190 count, so the faint edge of the shadow is not the pill's edge (it may bleed past the left edge, see the CSS).
  // Resolves to null when the picture cannot be read (then the CSS fallback, picture at the left of its box, stays).
  var _inkCache = {};
  function measureInk(src) {
    if (_inkCache[src]) return _inkCache[src];
    _inkCache[src] = fetch(src, { mode: 'cors' }).then(function(r) { return r.blob(); }).then(function(b) { return createImageBitmap(b); }).then(function(bm) {
      var W = 160, H = Math.max(1, Math.round(bm.height * W / bm.width));
      var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
      var cx = cv.getContext('2d', { willReadFrequently: true });
      cx.drawImage(bm, 0, 0, W, H);
      var d = cx.getImageData(0, 0, W, H).data, l = W, r = -1, t = H, b = -1;
      for (var y = 0; y < H; y++) {
        for (var x = 0; x < W; x++) {
          var i = (y * W + x) * 4;
          if (d[i + 3] > 100 && (d[i] < 190 || d[i + 1] < 190 || d[i + 2] < 190)) { if (x < l) l = x; if (x > r) r = x; if (y < t) t = y; if (y > b) b = y; }
        }
      }
      if (r < 0) return null;
      return { ar: bm.width / bm.height, l: l / W, r: (r + 1) / W, t: t / H, b: (b + 1) / H };
    }).catch(function() { return null; });
    return _inkCache[src];
  }

  // v2 on phones, main row: draw the pill larger inside its (unchanged) box and put its left edge on the left edge of the text.
  var PILL_SHARE_OF_BOX = 0.59;   // the pill is this share of the box height ...
  var PILL_MAX_WIDTH_SHARE = 0.36;   // ... but never wider than this share of the box width
  var PILL_NUDGE_RIGHT = 18;   // px: the pill's left edge sits this far right of the text's left edge
  function fitRecCardPictures() {
    var cont = document.getElementById('ppRecommendations');
    if (!cont || !cont.classList.contains('pp-recs-v2')) return;
    Array.prototype.forEach.call(cont.querySelectorAll('.pp-recs__tier--primary .pp-rec-card__image'), function(box) {
      if (box.classList.contains('is-jar')) return;   // a glass-jar picture is centred in its box by the CSS (the pill fitting would move it to the text's left edge)
      var img = box.querySelector('img');
      if (!img) return;
      var bw = box.clientWidth, bh = box.clientHeight;
      if (!bw || !bh) return;
      measureInk(img.currentSrc || img.src).then(function(ink) {
        if (!ink) return;
        var inkW = ink.r - ink.l, inkH = ink.b - ink.t;
        // A picture where almost nothing counts as the pill (a white tablet on white, a photo tile) cannot be measured: leave it as it is
        if (inkW < 0.08 || inkH < 0.3) return;
        var h = bh * PILL_SHARE_OF_BOX / inkH;          // the picture's height in px, so that the pill is the wanted share of the box
        var w = h * ink.ar;
        if (inkW * w > bw * PILL_MAX_WIDTH_SHARE) { w = bw * PILL_MAX_WIDTH_SHARE / inkW; h = w / ink.ar; }
        if (h > bh * 2.4) return;                        // a scale this large means the measurement is not to be trusted
        img.style.setProperty('--fit-w', w.toFixed(1) + 'px');
        img.style.setProperty('--fit-h', h.toFixed(1) + 'px');
        img.style.setProperty('--fit-l', (-ink.l * w + PILL_NUDGE_RIGHT).toFixed(1) + 'px');
        img.style.setProperty('--fit-t', ((bh - inkH * h) / 2 - ink.t * h).toFixed(1) + 'px');
        box.classList.add('is-fitted');
      });
    });
  }

  function alignRecCardRows() {
    fitRecCardNames();     // v2 only: a name's size can change its height, so this comes before the heights are measured
    fitRecCardPictures();  // v2 only, phones
    var grids = document.querySelectorAll('#ppRecommendations .pp-recs__grid');
    Array.prototype.forEach.call(grids, function(grid) {
      ['.pp-rec-card__name', '.pp-rec-card__tags', '.pp-rec-card__desc'].forEach(function(sel) {
        var els = grid.querySelectorAll(sel);
        Array.prototype.forEach.call(els, function(el) { el.style.minHeight = ''; });
        if (window.getComputedStyle(grid).flexDirection === 'column') return;
        // fractional heights, rounded UP: a rounded-down minimum would leave the tallest card a fraction of a pixel taller than the rest
        var tallest = 0;
        Array.prototype.forEach.call(els, function(el) { tallest = Math.max(tallest, el.getBoundingClientRect().height); });
        tallest = Math.ceil(tallest);
        if (tallest) Array.prototype.forEach.call(els, function(el) { el.style.minHeight = tallest + 'px'; });
      });
    });
  }

  var _alignObserver = null;
  function watchRecCardRows() {
    alignRecCardRows();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(alignRecCardRows);
    var container = document.getElementById('ppRecommendations');
    if (!container || typeof ResizeObserver === 'undefined') { window.addEventListener('resize', alignRecCardRows); return; }
    if (_alignObserver) _alignObserver.disconnect();
    var lastWidth = -1;
    _alignObserver = new ResizeObserver(function() {
      var w = container.clientWidth;
      if (w === lastWidth) return;   // only a new width (or the cards becoming visible) needs a new measurement
      lastWidth = w;
      alignRecCardRows();
    });
    _alignObserver.observe(container);
  }

  // The little arrow that says a link opens in a new window (the same one the product name has in the "See why" panel).
  var EXTERNAL_ARROW_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17L17 7M8 7h9v9"/></svg>';

  // opts.row: a card of the "we thought you'd like to know" row. Same card, but no price and no cart: a "See product" button that
  // opens the product's own page in a new window, and the same button at the foot of the panel the card opens.
  function buildRecCard(p, warnings, results, focusAreas, opts) {
    var isRow = !!(opts && opts.row);
    if (isRow) p.__row = true;
    registerWhyModalData(p, results, focusAreas, warnings);
    var nutrients = PRODUCT_NUTRIENTS[p.id] || [];
    var handle = p.product.handle;
    var productImg = getProductImage(handle);
    if (!productImg && nutrients.length > 0) {
      productImg = getIngredientImage(nutrients[0].metaHandle) || getIngredientImage(nutrients[0].matchName || '');
    }

    // Primary focus area for the pill badge
    var firstNutrient = nutrients[0] ? (NUTRIENT_DETAIL[nutrients[0].name] || {}) : {};
    var areas = (firstNutrient.areas || []).slice(0, 3);
    var areaPill = areas.length > 0 ? areas[0] : '';

    // Card text: the worker composes it from pre-written parts (src/results-copy) and returns it as p.copy = { line, full }.
    // `line` is one complete sentence, so it is shown whole and never cut. The hand-written product copy below is only a
    // fallback for a card the worker sent without copy (and for the dev image gallery, whose cards are not from the worker).
    // Only the Hair, Skin & Nails parts are passed for the fallback: it is the one area whose copy is not about the
    // product in general (Skin+ for hair is not Skin+ for skin). Other areas keep the default.
    var cardAreas = onlyBeautyParts(focusAreas);
    var reasonIndication = p.reason && p.reason.indication;
    var shortDesc = p.copy && p.copy.line ? p.copy.line : '';
    if (!shortDesc) {
      shortDesc = getConversationalWhy(p.id, cardAreas, reasonIndication);
      if (!shortDesc && p.reasons && p.reasons[0]) shortDesc = p.reasons[0].reason || '';
      if (!shortDesc) shortDesc = '';
      if (shortDesc.length > 120) shortDesc = shortDesc.substring(0, 117) + '...';
    }

    // Image — use per-product rec image, fallback to test image, then product image
    var recImg = (typeof PP_REC_IMAGES !== 'undefined' && PP_REC_IMAGES[handle]) ? PP_REC_IMAGES[handle] : ((typeof PP_TEST_IMG !== 'undefined') ? PP_TEST_IMG : productImg);
    // A guidance card (a need we cannot meet, no product to buy or link to) shows the product's own picture, chosen exactly like every
    // other card's (the worker sends only the picture's handle): the results-page picture, else the generic pill, else the ingredient's.
    var jar = p.__guide ? null : ppJarFor(handle);
    if (jar) recImg = jar.src;
    if (p.__guide) {
      var gh = p.__guide.imageHandle;
      recImg = (gh && typeof PP_REC_IMAGES !== 'undefined' && PP_REC_IMAGES[gh]) || ((typeof PP_TEST_IMG !== 'undefined') ? PP_TEST_IMG : productImg) || '';
      p.__image = recImg;
    }

    // "Why" content (collapsed)
    var whyText = (p.copy && p.copy.full) || getConversationalWhy(p.id, cardAreas, reasonIndication);
    if (!whyText && p.reasons && p.reasons[0]) whyText = p.reasons[0].reason || '';
    var whyId = 'ppWhy_' + p.id.replace(/[^a-zA-Z0-9]/g, '_');

    var arrowSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>';
    // the long arrow of the v2 phone layout (hidden by the CSS everywhere else)
    var longArrowSvg = '<svg class="pp-learn-long" viewBox="0 0 26 10" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 5h23"/><path d="M20 1.5L24 5l-4 3.5"/></svg>';

    var priceStr = isRow ? '' : getCachedPriceKr(handle);
    var priceDays = isRow ? '' : getPriceDays(handle);
    var isAdded = isRow ? false : PP.Cart.has(handle);
    var addBtnClass = 'pp-add-btn pp-rec-card__add-btn' + (isAdded ? ' is-added' : '');

    // Render "+ Add" always — syncRecCardButtons() corrects the SVG/text state
    // immediately after render for any items that are already in cart.
    // The price block is omitted entirely when we have no Shopify price: the customer
    // sees the product and the add button, and finds the price on the product page,
    // rather than being quoted a figure we are not sure of.
    // The product the "See product" button opens. A guidance card shows no product of its own in the panel, but the picture's handle is
    // its product's handle, so its button opens that product's page like every other card's.
    var linkHandle = p.__guide ? (p.__guide.imageHandle || '') : handle;
    var footerHtml = (isRow && !linkHandle)
      ? '<div class="pp-rec-card__footer"></div>'
      : isRow
      // the row's card: no price, and a link that looks like the Add button (it carries no .pp-add-btn, which the cart handlers listen for)
      ? '<div class="pp-rec-card__footer"><span class="pp-rec-card__price"></span>' +
          '<a class="pp-rec-card__add-btn pp-rec-card__see-btn pp-see-btn" href="' + esc(SHOPIFY_ROOT + 'products/' + encodeURIComponent(linkHandle)) + '" target="_blank" rel="noopener">' +
            esc((window.PP_YML_I18N && window.PP_YML_I18N.see_product) || 'See product') + ' ' + EXTERNAL_ARROW_SVG +
          '</a></div>'
      : '<div class="pp-rec-card__footer">' +
      (priceStr
        ? '<span class="pp-rec-card__price">' +
            '<span class="pp-rec-card__price-val">' + esc(priceStr) + '</span>' +
            (priceDays ? '<span class="pp-rec-card__price-meta">' + esc(priceDays) + '</span>' : '') +
          '</span>'
        : '<span class="pp-rec-card__price"></span>') +
      '<button class="' + addBtnClass + '"' +
        ' data-handle="' + esc(handle) + '"' +
        ' data-product-id="' + esc(String(p.id)) + '"' +
        '>' +
        '+ Add' +
      '</button>' +
    '</div>';

    // The tags under the name: her own areas the card speaks to (cardTagLabels), or the old nutrient pill when the card has no area data.
    var tagLabels = cardTagLabels(p);
    var tagsHtml = tagLabels === null
      ? (areaPill ? '<div class="pp-rec-card__area">' + esc(areaPill) + '</div>' : '')
      : '<div class="pp-rec-card__tags">' + tagLabels.map(function(t) { return '<span class="pp-rec-card__area">' + esc(t) + '</span>'; }).join('') + '</div>';

    var rawName = p.product.name;
    var escapedNameHtml = esc(rawName);

    // Numeric product ID used in why-modal trigger — strip non-digits as a safety measure
    var safeModalId = String(p.id).replace(/[^a-zA-Z0-9\-_]/g, '');

    return '<div class="pp-rec-card' + (isRow ? ' pp-rec-card--yml' : '') + (jar ? ' pp-rec-card--jar' : '') + '" data-handle="' + esc(handle) + '" onclick="if(!event.target.closest(\'.pp-add-btn, .pp-see-btn\'))window.ppOpenWhyModal(\'' + safeModalId + '\')">' +
      '<div class="pp-rec-card__image' + (jar ? ' is-jar' + (jar.map && jar.map.src < 2200 ? ' is-jar-big' : '') : '') + '"' + (jar ? ' style="--jar-h:' + Math.round((jar.map && jar.map.src < 2200 ? 165 : 138) / ((jar.map && jar.map.jar_frac) || 0.9)) + 'px;--jar-gap:' + ((jar.map && jar.map.jar_gap) || 0).toFixed(4) + '"' : '') + '>' + (recImg ? '<img src="' + recImg + '"' + (jar ? ' data-pp-jar="' + esc(handle) + '" crossorigin="anonymous"' : '') + ' alt="' + esc(p.product.name) + '" loading="lazy">' : '') + '</div>' +
      '<div class="pp-rec-card__content">' +
        '<div class="pp-rec-card__name">' + escapedNameHtml + '</div>' +
        '<div class="pp-rec-card__bottom">' +
          '<div class="pp-rec-card__mobile-name">' + esc(rawName) + '</div>' +
          tagsHtml +
          (shortDesc ? '<div class="pp-rec-card__desc">' + esc(shortDesc) + '</div>' : '') +
          // a little "Learn more" under the text, on every screen (the whole card is the button; this only says so)
          (whyText ? '<div class="pp-rec-card__learn">' + ((document.documentElement.lang || '').slice(0, 2) === 'da' ? 'Læs mere' : 'Learn more') + ' ' + arrowSvg + longArrowSvg + '</div>' : '') +
          footerHtml +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function buildRecommendationCards(results, warnings) {
    var container = document.getElementById('ppRecommendations');
    if (!container) return;

    var focusAreas = [];
    _cardTagCtx = null;
    try {
      var quizRaw = localStorage.getItem('quizResults');
      if (quizRaw) {
        var qr = JSON.parse(quizRaw);
        focusAreas = (qr && qr.answers && qr.answers.focus_areas) || [];
        if (!Array.isArray(focusAreas)) focusAreas = [focusAreas];
        focusAreas = expandBeautyAreas(focusAreas, qr && qr.answers && qr.answers.beauty_gateway);
        _cardTagCtx = makeCardTagContext(qr && qr.answers);
      }
    } catch (err) { /* ignore */ }

    var groupConfig = [
      {
        key: 'main',
        cssModifier: 'primary',
        getTitleLight: function (count) { return 'Your ' + count; },
        titleBold: 'Main Recommendations',
        description: 'We think these supplements would give you the most benefit based on your answers.',
      },
      {
        key: 'secondary',
        cssModifier: 'extras',
        getTitleLight: function () { return 'Your Optional'; },
        titleBold: 'Recommendations',
        description: 'Additional supplements that can support secondary goals based on your answers.',
      },
    ];

    var html = '';
    groupConfig.forEach(function(cfg) {
      var products = (results.recommendations && results.recommendations[cfg.key]) || [];
      if (products.length === 0) return;

      var cardsHtml = products.map(function(p) {
        return buildRecCard(p, warnings, results, focusAreas);
      }).join('');

      var titleLight = cfg.getTitleLight(products.length);
      html += '<div class="pp-recs__tier pp-recs__tier--' + cfg.cssModifier + '">' +
        '<h2 class="pp-recs__tier-title">' +
          '<span class="pp-recs__tier-title__light">' + titleLight + ' </span>' +
          '<span class="pp-recs__tier-title__bold">' + cfg.titleBold + '</span>' +
        '</h2>' +
        (cfg.description ? '<p class="pp-recs__tier-desc">' + cfg.description + '</p>' : '') +
        '<div class="pp-recs__grid">' + cardsHtml + '</div>' +
      '</div>';
    });

    // Same DOM-injection pattern the existing function used. cardsHtml is built
    // by buildRecCard() which escapes all dynamic values; titleLight/titleBold/
    // description are static literals.
    container.classList.toggle('pp-recs-v2', cardLayout() === 'v2');
    container.classList.toggle('pp-recs--glass', ppWantsGlass());   // bottle shoppers: more room between the name and the tags, for the jar pictures
    container.innerHTML = html;
    container.style.display = 'block';
    watchRecCardRows();

    // V3 response shape additions (no-op for V1 responses where these fields are absent).
    // All use safe DOM APIs: createElement + textContent.
    renderYouMightLikeRow(results.youMightLikeRow, container);
    renderEmptyMainEmpathy(results, container);
    renderEmptySecondaryNote(results, container);
    renderReviewsCarousel(container);
    renderScienceSection(container);
  }

  // ═══════════════════════════════════
  // V3 RESPONSE RENDERERS (Plan 4)
  // ═══════════════════════════════════
  //
  // V3 returns three new fields the renderer needs to surface:
  //   - youMightLikeRow: products that scored high pre-filter but got excluded
  //     by safety constraints (pregnancy V-A, vegan, allergens, drug interactions)
  //   - empathyMessage: shown when main bucket is empty (healthy user)
  //   - (empty-secondary note): brief copy when secondary is empty
  //
  // All renderers are no-ops when their corresponding field is absent (V1 fallback).
  // CSS for these elements lives in templates/page.results.liquid (.pp-yml-row, etc.)

  function renderYouMightLikeRow(rowItems, container) {
    if (!Array.isArray(rowItems) || rowItems.length === 0) return;

    // Copy comes from the template so it can be translated. Falls back to the previous hardcoded English if the block is missing.
    var ymlI18n = window.PP_YML_I18N || {};
    var heading = ymlI18n.heading || 'Good to know';
    var intro = ymlI18n.intro || "Your answers pointed to a few things we couldn't put in your plan. Here's why.";

    // Every card of the row is built exactly like a plan card (image, tags, short line, the panel with the long text) and has no price.
    //  - a product card has a "See product" button that opens the product's page in a new window;
    //  - a guidance card names a NEED we cannot meet and has no product behind it (the worker strips the product from it on purpose),
    //    so it has no button: its words come from the translated guidance copy, its picture is the ingredient's, and the panel lists
    //    what to look for on a label elsewhere.
    var firstSentence = function(text) { var m = String(text || '').match(/^.*?[.!?](\s|$)/); return (m ? m[0] : String(text || '')).trim(); };
    var cardsHtml = rowItems.map(function(item) {
      if (!item || typeof item !== 'object') return '';
      var p;
      if (item.guidance) {
        var guide = ymlI18n.guidance ? ymlI18n.guidance[item.guidance] : null;
        var title = guide ? guide.title : (item.name || item.id);
        var lead = guide ? guide.lead : (item.message || '');
        p = {
          id: item.id,
          product: { name: title, handle: '' },
          copy: { line: firstSentence(lead), full: lead },
          areas: item.areas,
          reasons: [],
          __guide: { lead: lead, points: guide && Array.isArray(guide.points) ? guide.points : [], imageHandle: item.imageHandle || '' }
        };
      } else if (item.name && item.handle) {
        p = {
          id: item.id,
          product: { name: item.name, handle: item.handle, format: item.format, vegan: item.vegan, vegetarian: item.vegetarian, pregnancySafe: item.pregnancySafe, containsFish: item.containsFish },
          copy: item.copy || { line: firstSentence(item.message), full: item.message },
          areas: item.areas,
          reasons: []
        };
      } else {
        return '';   // nothing to show or to link to
      }
      return buildRecCard(p, [], null, [], { row: true });
    }).join('');
    if (!cardsHtml) return;

    var section = document.createElement('section');
    section.className = 'pp-recs__tier pp-recs__tier--primary pp-recs__tier--yml';
    section.setAttribute('aria-label', heading);

    // Title in the same two weights as the other rows: all but the last two words light, the last two bold.
    var words = heading.split(' ');
    var cut = Math.max(1, words.length - 2);
    section.innerHTML =
      '<h2 class="pp-recs__tier-title">' +
        '<span class="pp-recs__tier-title__light">' + esc(words.slice(0, cut).join(' ') + ' ') + '</span>' +
        '<span class="pp-recs__tier-title__bold">' + esc(words.slice(cut).join(' ')) + '</span>' +
      '</h2>' +
      '<p class="pp-recs__tier-desc">' + esc(intro) + '</p>' +
      '<div class="pp-recs__grid">' + cardsHtml + '</div>';

    // Insert at top of recommendations container, above main + secondary
    container.insertBefore(section, container.firstChild);
    alignRecCardRows();

    // PostHog: track V3 you-might-like-row exposure so we can measure engagement
    if (typeof window.ppTrack === 'function') {
      try {
        window.ppTrack('v3_yml_rendered', {
          count: rowItems.length,
          product_ids: rowItems.map(function(i) { return i && i.id; }).filter(Boolean),
        });
      } catch (e) { /* analytics non-fatal */ }
    }
  }

  function renderEmptyMainEmpathy(results, container) {
    var main = (results.recommendations && results.recommendations.main) || [];
    var message = results.empathyMessage;
    // Only render when main is genuinely empty AND we have a V3 empathy message
    if (main.length > 0 || !message || typeof message !== 'string') return;

    // The worker distinguishes "nothing targeted, but here are some options"
    // from "nothing at all". The second cannot use the heading "Good news" above
    // an empty page, and the copy must not promise supplements that are not
    // there. Falls back to the worker's English string if the block is missing.
    var empathyI18n = window.PP_EMPATHY_I18N || {};
    var copy = empathyI18n[results.empathyKey] || null;

    var section = document.createElement('section');
    section.className = 'pp-empathy';
    if (results.empathyKey === 'empty_plan') section.classList.add('pp-empathy--nothing');
    section.setAttribute('aria-label', (copy && copy.heading) || 'Good news');

    var heading = document.createElement('h2');
    heading.className = 'pp-empathy__heading';
    heading.textContent = (copy && copy.heading) || 'Good news';
    section.appendChild(heading);

    var msgEl = document.createElement('p');
    msgEl.className = 'pp-empathy__message';
    msgEl.textContent = (copy && copy.body) || message;
    section.appendChild(msgEl);

    // Insert before any secondary section (or at the end if none).
    // The empty-secondary note renderer runs after this and will append below.
    container.insertBefore(section, container.firstChild);

    if (typeof window.ppTrack === 'function') {
      try { window.ppTrack('v3_empathy_rendered', {}); } catch (e) { /* non-fatal */ }
    }
  }

  function renderEmptySecondaryNote(results, container) {
    var recs = results.recommendations || {};
    var secondary = recs.secondary || [];
    // Only render the note when V3 has explicitly set the recommendations shape
    // AND secondary is empty. V1 always populates secondary, so this is a no-op for V1.
    if (secondary.length > 0) return;
    if (!results.meta || results.meta.version !== 'v3') return;

    var noteEl = document.createElement('div');
    noteEl.className = 'pp-empty-secondary';

    var msgEl = document.createElement('p');
    msgEl.textContent = "Your plan is focused on what's safe and most relevant for you right now.";
    noteEl.appendChild(msgEl);

    container.appendChild(noteEl);

    if (typeof window.ppTrack === 'function') {
      try { window.ppTrack('v3_empty_secondary_rendered', {}); } catch (e) { /* non-fatal */ }
    }
  }

  // ═══════════════════════════════════
  // REVIEW CAROUSEL
  // ═══════════════════════════════════

  var PP_REVIEWS = [
    { name: 'Anna M.', rating: 5, text: 'I was skeptical about personalized supplements, but after 3 months my energy levels have genuinely improved. The quiz felt thorough and the recommendations made sense for my lifestyle.' },
    { name: 'Sofie L.', rating: 5, text: 'The glass bottles look beautiful on my shelf and the quality feels premium. I started with just the Omega-3 and Magnesium individually, and I could tell the difference within weeks.' },
    { name: 'Mette K.', rating: 5, text: 'The refill pouches are such a smart system. I keep the glass bottles and just restock with the biodegradable pouches each month. Less waste, less guilt, same great products.' },
    { name: 'Louise H.', rating: 5, text: 'I got a personalized report with my first order that explained exactly why each supplement was chosen for me. It referenced actual research. That level of detail made me trust the brand immediately.' },
    { name: 'Camilla R.', rating: 5, text: 'I had a question about timing my supplements around my medication and their team got back to me within hours with a thoughtful, detailed answer. Best customer service I have experienced from a supplement brand.' },
    { name: 'Ida N.', rating: 5, text: 'Been taking my personalized plan for 6 weeks now. My digestion feels more settled and the brain fog I used to get in the afternoon is mostly gone. The check-in email they sent was a nice touch too.' },
    { name: 'Katrine S.', rating: 5, text: 'I love that part of my subscription goes toward environmental projects through their donation program. It feels good knowing my purchase contributes to something beyond my own health.' },
    { name: 'Emilie J.', rating: 5, text: 'The daily sachets make everything so simple. No more opening five different bottles every morning. I just grab my sachet and go. My partner started his own plan after seeing how easy mine was.' },
    { name: 'Nanna T.', rating: 4, text: 'Great product quality and the personalization feels genuine. Took about 4 weeks to notice changes but my hair and nails are definitely stronger now. The glass bottles are a keeper.' },
    { name: 'Sarah B.', rating: 5, text: 'They sent me a follow-up email a few weeks in asking how I was feeling and whether anything had changed. It felt like someone actually cared, not just another automated message.' },
    { name: 'Maja P.', rating: 5, text: 'The quiz took about 10 minutes and felt like a proper health assessment. My plan is simple, just 3 supplements, and each one has a clear purpose. No unnecessary extras.' },
    { name: 'Cecilie D.', rating: 5, text: 'I contacted support to adjust my plan after my doctor visit and they helped me rework everything same day. That kind of flexibility and responsiveness is rare. Genuinely impressed.' },
  ];

  function renderReviewsCarousel(container) {
    var section = document.createElement('section');
    section.className = 'pp-reviews-carousel';
    section.setAttribute('aria-label', 'Customer reviews');

    var viewport = document.createElement('div');
    viewport.className = 'pp-reviews-carousel__viewport';

    var track = document.createElement('div');
    track.className = 'pp-reviews-carousel__track';

    // Four reviews per slide (a 2x2 grid) on tablets and desktops, ONE per slide on phones (<= 600px): on a phone the
    // reviews are a single row that slides, instead of four cards stacked under each other. The slides are rebuilt if the
    // window crosses that width (a phone turned sideways, a resized desktop window).
    var phoneQuery = window.matchMedia ? window.matchMedia('(max-width: 600px)') : null;
    var perSlide = 4;
    var totalSlides = 3;
    var currentSlide = 0;

    function buildSlides() {
      perSlide = (phoneQuery && phoneQuery.matches) ? 1 : 4;
      totalSlides = Math.ceil(PP_REVIEWS.length / perSlide);
      currentSlide = 0;
      track.textContent = '';
      for (var s = 0; s < totalSlides; s++) {
        var slide = document.createElement('div');
        slide.className = 'pp-reviews-carousel__slide' + (s === 0 ? ' is-active' : '');
        var grid = document.createElement('div');
        grid.className = 'pp-reviews-carousel__grid';

        for (var r = 0; r < perSlide; r++) {
          var review = PP_REVIEWS[s * perSlide + r];
          if (!review) continue;
          var card = document.createElement('div');
          card.className = 'pp-reviews-carousel__card';

          var stars = '';
          for (var i = 0; i < 5; i++) {
            stars += i < review.rating
              ? '<span class="pp-reviews-carousel__star pp-reviews-carousel__star--filled">★</span>'
              : '<span class="pp-reviews-carousel__star">★</span>';
          }

          card.innerHTML =
            '<div class="pp-reviews-carousel__stars">' + stars + '</div>' +
            '<div class="pp-reviews-carousel__author">' + review.name + '</div>' +
            '<p class="pp-reviews-carousel__text">' + review.text + '</p>';
          grid.appendChild(card);
        }
        slide.appendChild(grid);
        track.appendChild(slide);
      }
    }
    buildSlides();

    viewport.appendChild(track);
    section.appendChild(viewport);

    container.appendChild(section);

    /* The track used to be a fixed 420px with overflow:hidden — sized for
       the 2-column desktop grid, where each card only needs to be as tall as
       itself. Mobile drops to a single column (see the max-width:600px
       rule), so two cards stack and need roughly double the height; the
       fixed box just clipped whatever didn't fit, cutting the second
       review's text and bottom border off mid-card. Measuring the active
       slide's actual content height and applying it to the track handles
       any combination of review length and column count instead of
       guessing a number that only works for one of them. */
    function syncTrackHeight(slideEl) {
      var grid = slideEl && slideEl.querySelector('.pp-reviews-carousel__grid');
      if (!grid) return;
      track.style.height = grid.offsetHeight + 'px';
    }

    function goToSlide(idx) {
      var slides = section.querySelectorAll('.pp-reviews-carousel__slide');
      var leaving = slides[currentSlide];
      var entering = slides[idx];
      if (!leaving || !entering || idx === currentSlide) return;

      leaving.classList.remove('is-active');
      leaving.classList.add('is-leaving');
      entering.classList.add('is-active');
      syncTrackHeight(entering);

      setTimeout(function() { leaving.classList.remove('is-leaving'); }, 650);
      currentSlide = idx;
    }

    syncTrackHeight(section.querySelector('.pp-reviews-carousel__slide.is-active'));
    window.addEventListener('resize', function() {
      syncTrackHeight(section.querySelectorAll('.pp-reviews-carousel__slide')[currentSlide]);
    });
    // Crossing the phone width changes how many reviews a slide holds: rebuild the slides from the first one.
    if (phoneQuery) {
      var onPhoneChange = function() {
        buildSlides();
        syncTrackHeight(section.querySelector('.pp-reviews-carousel__slide.is-active'));
      };
      if (phoneQuery.addEventListener) phoneQuery.addEventListener('change', onPhoneChange);
      else if (phoneQuery.addListener) phoneQuery.addListener(onPhoneChange);
    }

    setInterval(function() {
      goToSlide((currentSlide + 1) % totalSlides);
    }, 6000);
  }

  // ═══════════════════════════════════
  // SCIENCE CREDIBILITY SECTION
  // ═══════════════════════════════════

  function renderScienceSection(container) {
    var stats = [
      { target: 6053, suffix: '', label: 'Peer-reviewed studies', desc: 'in our internal research catalog across 78 ingredients. Each ingredient enters the formula only when the evidence is consistent.' },
      { target: 78, suffix: '', label: 'Nutrients and ingredients', desc: 'carefully selected and dosed within Nordic Nutrition Recommendations. Built for Nordic adults, Nordic light levels, Nordic diet patterns.' },
      { target: 30, suffix: '-day', label: 'Money-back guarantee', desc: 'no questions asked. If you are not satisfied with your personalized plan, we will refund your purchase in full.' },
    ];

    var section = document.createElement('section');
    section.className = 'pp-science';
    section.setAttribute('aria-label', 'Behind the science');

    var inner = document.createElement('div');
    inner.className = 'pp-science__inner';

    var content = document.createElement('div');
    content.className = 'pp-science__content';

    var title = document.createElement('h2');
    title.className = 'pp-science__title';
    title.innerHTML = 'Behind our <em>Science.</em>';
    content.appendChild(title);

    var subtitle = document.createElement('p');
    subtitle.className = 'pp-science__subtitle';
    subtitle.textContent = 'Your plan is built on the research catalog we maintain, and on the regulatory frameworks that keep wellness guidance honest in Europe.';
    content.appendChild(subtitle);

    var statsList = document.createElement('div');
    statsList.className = 'pp-science__stats';

    stats.forEach(function(s) {
      var row = document.createElement('div');
      row.className = 'pp-science__stat';

      var left = document.createElement('div');
      left.className = 'pp-science__stat-left';

      var numEl = document.createElement('div');
      /* ph-no-capture: PostHog's session recorder skips this subtree (the number is rewritten every frame while it counts,
         and the recorder reads layout for every change it sees, which made the page stutter) */
      numEl.className = 'pp-science__number ph-no-capture';
      numEl.setAttribute('data-clarity-mask', 'True');
      numEl.dataset.target = s.target;
      numEl.dataset.suffix = s.suffix;
      numEl.textContent = '0' + s.suffix;
      left.appendChild(numEl);

      var labelEl = document.createElement('div');
      labelEl.className = 'pp-science__stat-label';
      labelEl.textContent = s.label;
      left.appendChild(labelEl);

      row.appendChild(left);

      var textEl = document.createElement('div');
      textEl.className = 'pp-science__stat-text';
      textEl.textContent = s.desc;
      row.appendChild(textEl);

      statsList.appendChild(row);
    });

    content.appendChild(statsList);
    inner.appendChild(content);

    var visual = document.createElement('div');
    visual.className = 'pp-science__visual';
    inner.appendChild(visual);

    section.appendChild(inner);
    container.appendChild(section);

    function runCountUp() {
      var els = section.querySelectorAll('.pp-science__number');
      var maxTarget = 0;
      els.forEach(function(el) {
        var t = parseInt(el.dataset.target, 10);
        if (t > maxTarget) maxTarget = t;
      });
      var totalDuration = 3200;
      var nf; try { nf = new Intl.NumberFormat(); } catch (e) { nf = null; }   /* one formatter, not one per frame */
      els.forEach(function(el) {
        var target = parseInt(el.dataset.target, 10);
        var suffix = el.dataset.suffix || '';
        el.textContent = '0' + suffix;
        var textNode = el.firstChild;   /* update this text node in place, only when the shown number changes */
        var lastShown = 0;
        var ratio = target / maxTarget;
        var delay = (1 - ratio) * totalDuration * 0.55;
        var duration = totalDuration - delay;
        setTimeout(function() {
          var start = performance.now();
          function tick(now) {
            var elapsed = now - start;
            var progress = Math.max(0, Math.min(elapsed / duration, 1));   /* the frame time can precede start: no negatives */
            var eased = 1 - Math.pow(1 - progress, 8);
            var current = Math.round(eased * target);
            if (current !== lastShown) {
              lastShown = current;
              var txt = (nf ? nf.format(current) : current.toLocaleString()) + suffix;
              if (textNode && textNode.parentNode === el) textNode.nodeValue = txt; else el.textContent = txt;
            }
            if (progress < 1) requestAnimationFrame(tick);
          }
          requestAnimationFrame(tick);
        }, delay);
      });
    }

    var wasVisible = false;
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !wasVisible) {
          wasVisible = true;
          runCountUp();
        } else if (!entry.isIntersecting) {
          wasVisible = false;
        }
      });
    }, { threshold: 0.3 });
    observer.observe(section);
  }

  // ═══════════════════════════════════
  // DYNAMIC PRICING ENGINE
  // ═══════════════════════════════════

function renderResultsPage(results, answers, name, warnings) {
    buildProfileBlock(results, answers, name);
    buildRecommendationCards(results, warnings);
  }

  async function addSingleProduct(handle) {
    try {
      await ensureProductCached(handle);
      var product = PRODUCT_CACHE[handle];
      if (!product || !product.variants || !product.variants[0]) return;
      var variantId = product.variants[0].id;
      // Phase 2a: stamp session ID on the cart before adding.
      await ppAttachQuizSessionToCart();
      await fetch(SHOPIFY_ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      updateCartCount();
      if (typeof window.theme !== 'undefined' && window.theme.cart && typeof window.theme.cart.updateCart === 'function') {
        window.theme.cart.updateCart();
      }
    } catch(e) {
      console.error('Failed to add product:', e);
    }
  }

  async function addProductToCart(handle, productId, button) {
    // PP.Cart is the single source of truth — check it, not the button class
    if (PP.Cart.has(handle)) {
      _ppRecCardRemove(handle, button);
      return;
    }

    button.disabled = true;
    button.dataset.ppBusy = '1';
    while (button.firstChild) button.removeChild(button.firstChild);
    button.appendChild(document.createTextNode('Adding…'));
    button.classList.remove('btn-error');

    try {
      const ok = await ensureProductCached(handle);
      if (!ok) throw new Error('Product not found: ' + handle);

      const product = PRODUCT_CACHE[handle];
      if (!product.variants || !product.variants.length) throw new Error('No variants found');

      const variantId = product.variants[0].id;

      // Plan-view purchase cards have a subscription radio; rec cards do not.
      // Rec cards always add as one-time — subscription is via the plan CTA.
      const radio = document.querySelector('input[name="purchase_' + productId + '"]:checked');
      const sellingPlanId = (radio && radio.value === 'sub') ? getSubscriptionPlanId(handle) : null;

      const { cart } = await PP.Cart.add(handle, variantId, {
        openDrawer: false,
        sellingPlanId: sellingPlanId
      });

      setButtonAdded(button);
      syncAddButtonsFor(handle);
      updateCartCount(cart); // cart returned from add() — no extra round-trip
      showCartNotification('added');
      if (window.ppTrack) {
        var recMeta = PP_REC_GROUP[handle] || {};
        ppTrack('product_added_to_cart', {
          product: handle,
          product_id: productId || null,
          product_title: (product && product.title) || null,
          price: product && typeof product.price === 'number' ? product.price / 100 : null,
          // Which half of the plan the customer actually bought into.
          rec_group: recMeta.group || 'unknown',
          rec_position: recMeta.position || null,
          is_subscription: !!sellingPlanId,
          source: 'results_page'
        });
      }

    } catch (error) {
      console.error('[PP] Cart add error:', error);
      while (button.firstChild) button.removeChild(button.firstChild);
      button.appendChild(document.createTextNode('✗ Failed'));
      button.classList.add('btn-error');
      setTimeout(function() { setButtonIdle(button); }, 2000);
    } finally {
      delete button.dataset.ppBusy;
      button.disabled = false;
    }
  }

  async function sendResultsToIntegrations(quizResults, algoResults) {
    // Idempotency guard: only fire integration events once per quiz submission.
    // Without this, every reload of /pages/results re-sends "Quiz Results Viewed"
    // to Klaviyo (re-triggering email flows) and re-logs to Google Sheets.
    // Key is the quiz timestamp; falls back to email then to a constant marker.
    var sentKey = String(quizResults.timestamp || quizResults.email || 'sent');
    if (localStorage.getItem('ppIntegrationsSent') === sentKey) {
      return;
    }
    // Write the key BEFORE attempting integrations. If something below throws,
    // we accept losing this one quiz's events rather than retry-firing them
    // on every subsequent page reload (which is much worse for users).
    try { localStorage.setItem('ppIntegrationsSent', sentKey); } catch (e) { /* quota / private mode */ }

    try {
      const recProducts = (arr) => (Array.isArray(arr) ? arr : []).map(p => p.product.name).join(', ');

      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        var answers = quizResults.answers || {};
        var focusAreas = Array.isArray(answers.focus_areas) ? answers.focus_areas : [];
        var mainProducts = Array.isArray(algoResults.recommendations.main)
          ? algoResults.recommendations.main.map(function(p) { return p.product.handle; })
          : [];

        window.klaviyo.push(['identify', {
          email: quizResults.email,
          first_name: quizResults.name,
          quiz_completed: true,
          main_products: recProducts(algoResults.recommendations.main),
          secondary_products: recProducts(algoResults.recommendations.secondary),
          total_recommendations: (algoResults.meta.mainCount || 0) + (algoResults.meta.secondaryCount || 0),
          diet: algoResults.meta.diet,
          is_pregnant: algoResults.meta.isPregnant,
          personapath_focus_areas: focusAreas.join(','),
          personapath_plan_supplements: mainProducts.join(','),
          personapath_checkin_count: 0,
          personapath_quiz_completed_date: new Date().toISOString().split('T')[0]
        }]);

        // The top recommendation cards, for the Klaviyo emails (the "how it is made for you" block): the same name, picture, short text
        // and tags as the cards on this page, at most four. Never allowed to break the event: any trouble leaves the list empty.
        var mainCards = [];
        try {
          var ownTagCtx = false;
          if (!_cardTagCtx) { _cardTagCtx = makeCardTagContext(quizResults.answers); ownTagCtx = true; }
          var emailMain = Array.isArray(algoResults.recommendations.main) ? algoResults.recommendations.main : [];
          var emailSecondary = Array.isArray(algoResults.recommendations.secondary) ? algoResults.recommendations.secondary : [];
          (emailMain.length ? emailMain : emailSecondary).slice(0, 4).forEach(function(p) {
            var h = p.product.handle;
            var img = (window.PP_REC_IMAGES && window.PP_REC_IMAGES[h]) || getProductImage(h) || '';
            if (img.indexOf('//') === 0) img = 'https:' + img;
            if (img.indexOf('cdn.shopify.com') !== -1 && img.indexOf('width=') === -1) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=520&height=500&crop=center&format=jpg';
            var tags = [];
            try { tags = cardTagLabels(p) || []; } catch (e) { tags = []; }
            mainCards.push({
              name: p.product.name,
              handle: h,
              image: img,
              text: (p.copy && p.copy.line) || '',
              tags: tags,
              url: window.location.origin + SHOPIFY_ROOT + 'products/' + encodeURIComponent(h)
            });
          });
          if (ownTagCtx) _cardTagCtx = null;
        } catch (e) { mainCards = []; }

        // Her focus areas as one lowercase list for the email line "You have your own priorities: ...": one to three labels joined with
        // commas and "and", and "and more" after the first three when she picked four or more. Draft wording, English only. Unknown ids
        // are skipped; an empty phrase makes the email fall back to its generic line.
        var focusPhrase = '';
        try {
          var EMAIL_FOCUS_WORDS = {
            immunity: 'immunity', energy: 'energy', heart: 'heart', brain: 'brain', vision: 'vision', sleep: 'sleep',
            digestion: 'digestion', womens_health: 'women’s health', mens_health: 'men’s health', beauty: 'beauty',
            mood_stress: 'mood & stress', sport: 'sport', bones_joints: 'bones & joints', hormonal_balance: 'hormonal balance',
            longevity: 'longevity', maternity: 'pregnancy'
          };
          var focusWords = focusAreas.map(function(a) { return EMAIL_FOCUS_WORDS[a]; }).filter(Boolean);
          var focusMore = focusWords.length > 3;
          focusWords = focusWords.slice(0, 3);
          if (focusMore) focusPhrase = focusWords.join(', ') + ' and more';
          else focusPhrase = focusWords.length > 1 ? focusWords.slice(0, -1).join(', ') + ' and ' + focusWords[focusWords.length - 1] : (focusWords[0] || '');
        } catch (e) { focusPhrase = ''; }

        window.klaviyo.push(['track', 'Quiz Results Viewed', {
          focus_phrase: focusPhrase,
          main_cards: mainCards,
          main_products: recProducts(algoResults.recommendations.main),
          secondary_products: recProducts(algoResults.recommendations.secondary),
          main_count: algoResults.meta.mainCount || 0,
          secondary_count: algoResults.meta.secondaryCount || 0,
          total_products: (algoResults.meta.mainCount || 0) + (algoResults.meta.secondaryCount || 0),
          focus_areas: focusAreas.join(', '),
          hero_summary: algoResults.heroSummary || '',
          warnings_count: algoResults.warnings.length,
          quiz_timestamp: quizResults.timestamp,
          algorithm_version: algoResults.meta.version
        }]);
      }

      // A Shopify customer record for someone who finished the quiz and gave an email + the health consent, so the admin can see what she
      // answered and what was recommended even if she never buys (David, 2026-10-26). Once per quiz (this whole function is guarded above),
      // not awaited, never blocks the page. The worker only ever CREATES: an existing customer is left alone (worker src/orders/quiz-lead.js).
      try {
        if (quizResults.email && quizResults.consentHealth) {
          var leadRecs = function(arr) {
            return (Array.isArray(arr) ? arr : []).map(function(p) { return { name: p.product.name, handle: p.product.handle }; });
          };
          // The plan as the admin reads it: each product with the card's one-line reason, the optional ones, and the
          // "Good to know" row (products and guidance cards), in the language she saw them in.
          var leadReadableRecs = function(res) {
            var out = [];
            var line = function(name, c, fallback) {
              var t = String((c && (c.line || c.full)) || fallback || '').replace(/\s+/g, ' ').trim();
              return name + (t ? ': ' + t : '');
            };
            var group = function(title, arr) {
              var rows = (Array.isArray(arr) ? arr : []).filter(function(p) { return p && p.product && p.product.name; });
              if (!rows.length) return;
              out.push(title);
              rows.forEach(function(p) { out.push('- ' + line(p.product.name, p.copy)); });
            };
            group('MAIN RECOMMENDATIONS', res.recommendations && res.recommendations.main);
            group('OPTIONAL RECOMMENDATIONS', res.recommendations && res.recommendations.secondary);
            var yml = Array.isArray(res.youMightLikeRow) ? res.youMightLikeRow : [];
            var i18n = window.PP_YML_I18N || {};
            var ymlRows = yml.map(function(item) {
              if (!item) return '';
              if (item.guidance) {
                var g = i18n.guidance ? i18n.guidance[item.guidance] : null;
                return '- ' + line(g ? g.title : (item.name || item.id || ''), null, g ? g.lead : item.message);
              }
              return item.name ? '- ' + line(item.name, item.copy, item.message) : '';
            }).filter(Boolean);
            if (ymlRows.length) { out.push('WE THOUGHT YOU WOULD LIKE TO KNOW'); out = out.concat(ymlRows); }
            return out;
          };
          var leadBody = {
            email: quizResults.email,
            name: quizResults.name || '',
            consent_health: true,
            consent_marketing: !!quizResults.consentMarketing,
            quiz_session_id: ppQuizSessionId(),
            answers_readable: Array.isArray(quizResults.answersReadable) ? quizResults.answersReadable : [],
            recommendations_readable: leadReadableRecs(algoResults),
            recommendations: { main: leadRecs(algoResults.recommendations.main), secondary: leadRecs(algoResults.recommendations.secondary) }
          };
          if (window.ppInternal) leadBody.internal = true;
          fetch(LEAD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadBody)
          }).catch(function() { /* the page never depends on this */ });
        }
      } catch (leadErr) { /* same */ }

      // Klaviyo list subscription and Google Sheets logging — marketing consent required
      if (window.ppConsent && window.ppConsent.marketing) {
        await fetch('https://a.klaviyo.com/client/subscriptions?company_id=Wc9bXd', {
          method: 'POST',
          headers: { 'content-type': 'application/vnd.api+json', 'revision': '2026-01-15' },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: "quiz_results",
                profile: {
                  data: {
                    type: "profile",
                    attributes: { email: quizResults.email, first_name: quizResults.name }
                  }
                }
              },
              relationships: { list: { data: { type: "list", id: "Xj7SwK" } } }
            }
          })
        });

        await fetch('https://script.google.com/macros/s/AKfycbw4mY4sFW1l_zPQ3HEDTVYpgqAFNGK3OVe_SlUgLLd3es7Ymre-pUslqiwwALIsS49V/exec', {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timestamp: quizResults.timestamp,
            email: quizResults.email,
            name: quizResults.name,
            main: recProducts(algoResults.recommendations.main),
            secondary: recProducts(algoResults.recommendations.secondary),
            warnings: algoResults.warnings.map(w => `${w.product}:${w.action}`).join('; '),
            algorithm_version: algoResults.meta.version
          })
        });
      }

    } catch (error) {
      console.error('Integration error (non-blocking):', error);
    }
  }

// ═══════════════════════════════════════════════
  // TRANSITION TESTIMONIALS
  // ═══════════════════════════════════════════════

  const TESTIMONIALS_BY_AREA = {
    energy: [
      { quote: "I used to hit a wall every afternoon. After a month on B-complex and magnesium, my energy stays steady all day.", author: "Line K." },
      { quote: "I was skeptical, but within two weeks I noticed I wasn't reaching for my third coffee anymore. Real difference.", author: "Sofie M." }
    ],
    immunity: [
      { quote: "I used to catch every cold going around the office. Since starting vitamin D and zinc, I've stayed healthy all winter.", author: "Mads T." },
      { quote: "My kids bring home everything from school. This winter I've actually stayed on my feet — that's a first.", author: "Camilla R." }
    ],
    sleep: [
      { quote: "I'd lie awake for hours. Sleep+ with magnesium changed my nights completely — I fall asleep faster and wake refreshed.", author: "Anna B." },
      { quote: "I was waking up at 3 AM every night. After three weeks, I'm finally sleeping through. Game changer.", author: "Jonas P." }
    ],
    digestion: [
      { quote: "The bloating after every meal was exhausting. Probiotics made a noticeable difference within the first two weeks.", author: "Maria L." },
      { quote: "My stomach finally feels calm after meals. I didn't realize how much discomfort I'd been putting up with.", author: "Katrine H." }
    ],
    heart: [
      { quote: "My doctor noticed my numbers improving at my last check-up. Omega-3 and CoQ10 have become part of my daily routine.", author: "Henrik J." },
      { quote: "I wanted to be proactive about heart health. Knowing my plan is evidence-based gives me real peace of mind.", author: "Birgitte S." }
    ],
    brain: [
      { quote: "My focus at work was all over the place. B-vitamins and omega-3 helped me get through the afternoon without brain fog.", author: "Frederik N." },
      { quote: "I can actually concentrate through long meetings now. The difference in my mental clarity surprised me.", author: "Ida V." }
    ],
    skin: [
      { quote: "I noticed fine lines diminishing. My skin doesn't feel dry in some spots and oily in others anymore — it feels nourished.", author: "Flip M." },
      { quote: "After years of trying topical products, supporting my skin from the inside finally made the difference I was looking for.", author: "Louise D." }
    ],
    mens_skin: [
      { quote: "My skin was always rough and irritated after shaving. Collagen and zinc made it noticeably smoother.", author: "Nikolaj H." },
      { quote: "I never thought about skincare supplements. But the oiliness and breakouts are genuinely under control now.", author: "Emil R." }
    ],
    hair: [
      { quote: "My hair was thinning and breaking constantly. After two months on biotin and collagen, I can see new growth coming in.", author: "Signe A." },
      { quote: "My hairdresser actually asked what I'd changed. The thickness and shine are back.", author: "Nadia F." }
    ],
    mens_hair: [
      { quote: "I noticed my hair thinning at the temples. After three months, the shedding has slowed down significantly.", author: "Kasper M." },
      { quote: "I wish I'd started earlier. My barber noticed the difference before I did.", author: "Christian L." }
    ],
    nails: [
      { quote: "My nails used to peel and snap constantly. They're finally growing strong and smooth — I can't believe the difference.", author: "Emilie W." },
      { quote: "I stopped needing to file down cracks every week. Biotin really works when you stick with it.", author: "Trine G." }
    ],
    mood_stress: [
      { quote: "I was constantly on edge. Magnesium and B-vitamins helped take the edge off — I feel more like myself again.", author: "Sara K." },
      { quote: "I didn't expect supplements to help with stress, but I genuinely feel calmer and more balanced.", author: "Mikkel O." }
    ],
    sport: [
      { quote: "Recovery after training used to take me days. With magnesium and omega-3, I'm back at it much faster.", author: "Rasmus E." },
      { quote: "I train 4–5 times a week and the soreness was brutal. The difference in recovery time has been remarkable.", author: "Oliver H." }
    ],
    vision: [
      { quote: "My eyes felt strained and dry after every workday. Omega-3 helped more than any eye drops I'd tried.", author: "Jakob L." },
      { quote: "As someone who stares at screens 8+ hours a day, the reduction in eye fatigue has been really noticeable.", author: "Mathilde C." }
    ],
    bones_joints: [
      { quote: "My knees were stiff every morning. After a few weeks on collagen and vitamin D, getting out of bed feels different.", author: "Kirsten P." },
      { quote: "I'm 58 and I feel more flexible now than I did two years ago. Small daily habits really add up.", author: "Jørgen B." }
    ],
    hormonal_balance: [
      { quote: "My cycles were unpredictable and I felt out of balance. A targeted plan helped me feel more in control of my body.", author: "Helle N." },
      { quote: "Hot flashes and poor sleep were wearing me down. Magnesium and D-vitamin made the transition so much smoother.", author: "Dorthe A." }
    ],
    longevity: [
      { quote: "I'm investing in my health for the long run. Knowing each supplement is backed by science makes me confident.", author: "Lars F." },
      { quote: "At 52, I feel sharper and more energized than I did at 45. It's the small daily habits that compound.", author: "Susanne T." }
    ],
    womens_health: [
      { quote: "PMS used to knock me out for days. Since starting magnesium and B6, my cycles feel so much more manageable.", author: "Julie R." },
      { quote: "I finally feel like my body is working with me, not against me. The personalized approach made all the difference.", author: "Christina E." }
    ],
    mens_health: [
      { quote: "I didn't think supplements would make a difference for me. But my energy and drive genuinely improved.", author: "Thomas V." },
      { quote: "Prostate health wasn't on my radar until my doctor mentioned it. Glad I'm being proactive now.", author: "Anders M." }
    ],
    maternity: [
      { quote: "Having a clear prenatal plan took so much stress off. I know exactly what I'm taking and why.", author: "Stine B." },
      { quote: "Postpartum was tough, but having the right nutritional support made the recovery feel more manageable.", author: "Maja K." }
    ],
    _general: [
      { quote: "I loved all of it. I noticed an improvement at first use and have been very pleased. This is the first time I've felt genuinely supported.", author: "Adrian V." },
      { quote: "I was buying random supplements before. Now I actually know what I need and why — no more guesswork.", author: "Pernille J." }
    ]
  };

  function pickTransitionTestimonials(answers) {
    const focusAreas = Array.isArray(answers.focus_areas) ? answers.focus_areas : [];
    const pool = [];

    focusAreas.forEach(area => {
      const items = TESTIMONIALS_BY_AREA[area];
      if (items && items.length) {
        pool.push(items[Math.floor(Math.random() * items.length)]);
      }
    });

    // Always include one general testimonial
    const general = TESTIMONIALS_BY_AREA._general;
    pool.push(general[Math.floor(Math.random() * general.length)]);

    // Deduplicate by author, limit to 3
    const seen = new Set();
    const unique = [];
    for (const t of pool) {
      if (!seen.has(t.author)) {
        seen.add(t.author);
        unique.push(t);
      }
      if (unique.length >= 3) break;
    }

    // Pad to at least 2
    while (unique.length < 2) {
      const fallback = general.find(g => !seen.has(g.author));
      if (fallback) { unique.push(fallback); seen.add(fallback.author); }
      else break;
    }

    return unique;
  }

  // ═══════════════════════════════════
  // PHASE TRANSITIONS
  // ═══════════════════════════════════

  function transitionPhase(hideId, showId, onShow) {
    var hideEl = document.getElementById(hideId);
    var showEl = document.getElementById(showId);

    if (hideEl) {
      hideEl.style.transition = 'opacity 0.4s ease';
      hideEl.style.opacity = '0';
    }

    setTimeout(function() {
      if (hideEl) hideEl.style.display = 'none';
      if (showEl) {
        showEl.style.display = 'block';
        showEl.style.opacity = '0';
        showEl.style.transition = 'opacity 0.4s ease';
        window.scrollTo(0, 0);
        requestAnimationFrame(function() {
          showEl.style.opacity = '1';
          if (onShow) onShow();
        });
      }
    }, 400);
  }

  var currentPhase = 'transitionState';

  function setFloatNav(nextLabel, nextAction, backAction) {
    var nextWrap = document.getElementById('ppFloatNext');
    var backWrap = document.getElementById('ppFloatBack');
    var nextBtn = document.getElementById('ppNextBtn');
    var nextLbl = document.getElementById('ppNextLabel');
    var backBtn = document.getElementById('ppBackBtn');

    if (nextLabel && nextAction) {
      nextLbl.textContent = nextLabel;
      nextBtn.disabled = false;
      nextWrap.style.display = '';
      nextBtn.onclick = nextAction;
    } else {
      nextWrap.style.display = 'none';
    }

    if (backAction) {
      backWrap.style.display = '';
      backBtn.onclick = backAction;
    } else {
      backWrap.style.display = 'none';
    }
  }

  function showHealthSnapshot() {
    transitionPhase('transitionState', 'healthSnapshotState', function() {
      currentPhase = 'healthSnapshotState';
      // The nutrients screen is disabled (see showNutrients), so the snapshot
      // now leads straight to the plan. Floating next button restored to its
      // original behaviour on 2026-10-06 — only the floating BACK button was
      // meant to go (David's call), not this one. backAction stays null so
      // the back pill doesn't reappear.
      setFloatNav('See My Results', showFinalResults, null);
    });
  }

  function showTestimonials() {
    transitionPhase('healthSnapshotState', 'transitionState', function() {
      currentPhase = 'transitionState';
      setFloatNav('See My Results', showHealthSnapshot, null);
    });
  }

  /* DISABLED 2026-08-13 — the "Each product is made with ingredients tailored
     to your needs" screen is out of the flow for now. Nothing calls these two;
     the snapshot goes straight to the plan. To put it back, point the snapshot's
     next action at showNutrients again and restore its "Continue" label. The
     builder (buildNutrientsScreen) and its markup are untouched. */
  function showNutrients() {
    transitionPhase('healthSnapshotState', 'nutrientsState', function() {
      currentPhase = 'nutrientsState';
      setFloatNav('See Your Plan', showFinalResults, showSnapshotFromNutrients);
    });
  }

  function showSnapshotFromNutrients() {
    transitionPhase('nutrientsState', 'healthSnapshotState', function() {
      currentPhase = 'healthSnapshotState';
      setFloatNav('Continue', showNutrients, showTestimonials);
    });
  }

  function showFinalResults() {
    var transHeader = document.getElementById('ppTransHeader');
    if (transHeader) transHeader.style.display = 'none';
    document.getElementById('ppFloatNext').style.display = 'none';
    document.getElementById('ppFloatBack').style.display = 'none';

    // Sourced from whichever screen is showing rather than a hardcoded
    // 'nutrientsState', since the snapshot now leads here directly.
    transitionPhase(currentPhase || 'healthSnapshotState', 'resultsContent', async function() {
      currentPhase = 'resultsContent';
      var header = document.getElementById('ppCustomHeader');
      if (header) header.classList.add('is-visible');
      /* The header is see-through at the top of the page and white once she has scrolled. */
      if (header && !header.__ppScrollBound) {
        header.__ppScrollBound = true;
        var ppHdrScroll = function () { header.classList.toggle('is-scrolled', (window.pageYOffset || document.documentElement.scrollTop) > 8); };
        window.addEventListener('scroll', ppHdrScroll, { passive: true });
        ppHdrScroll();
      }
      updateCartCount();
      setFloatNav(null, null, backToSnapshot);      /* back to the snapshot page */

      // AI summary is now rendered inside the hero via buildHeroSummary
    });
  }

  // Accepts the cart object directly (from add/remove responses) to avoid
  // an extra /cart.js round-trip. Falls back to fetching when called without args.
  function updateCartCount(cart) {
    function _apply(c) {
      var el = document.getElementById('ppCartCount');
      if (!el) return;
      var count = (c && c.item_count) || 0;
      el.textContent = count;
      el.classList.toggle('has-items', count > 0);
    }
    if (cart) { _apply(cart); return; }
    fetch(SHOPIFY_ROOT + 'cart.js').then(function (r) { return r.json(); }).then(_apply).catch(console.warn);
  }

  // ═══════════════════════════════════
  // HEALTH SNAPSHOT BUILDER
  // ═══════════════════════════════════

  /* A life-stage or circumstance worth naming plainly on the snapshot, even
     when it isn't a "watching" or "support" score — pregnancy, postpartum,
     trying to conceive, menopause and a diagnosed thyroid condition all
     change what we'd recommend, and someone in one of these stages should
     see that acknowledged rather than inferred only from the plan further
     down. Checked in priority order; first match wins, since the stages are
     mutually exclusive in practice (postpartum beats a stale "trying"
     answer from earlier in the quiz, etc). */
  function getNotableSituationNote(answers) {
    var hormoneChanges = Array.isArray(answers.hormone_changes_challenges) ? answers.hormone_changes_challenges : [];

    // Every stage note ends with the same gentle line: we are not a substitute for her own midwife or doctor.
    var CONSULT = ' Always confirm any new supplement with your midwife or doctor first.';
    var lowMood = Array.isArray(answers.postpartum_feeling) && answers.postpartum_feeling.indexOf('low_mood') !== -1;
    var LOW_MOOD_NOTE = ' If you’re feeling low or anxious, please talk to your own midwife or doctor.';
    var stage = lifeStageFrom(answers);

    if (stage === 'pregnant') {
      var weeks = answers.pregnancy_weeks;
      var lead = weeks === 'first_trimester'
        ? 'You’re pregnant and in your first trimester, so we’ve kept that in mind throughout. Folate matters most in these first weeks, and needs for iron, choline and vitamin D typically rise as pregnancy goes on, while certain herbal ingredients and high-dose actives are best set aside.'
        : (weeks === 'second_trimester' || weeks === 'third_trimester')
          ? 'You’re pregnant and in your second or third trimester, so we’ve kept that in mind throughout. DHA, iron and calcium come more into focus now, and needs for choline and vitamin D typically stay raised, while certain herbal ingredients and high-dose actives are best set aside.'
          : 'You’re pregnant, so we’ve kept that in mind throughout. Needs for folate, iron, choline and vitamin D typically rise during pregnancy, while certain herbal ingredients and high-dose actives are best set aside.';
      return {
        title: 'Pregnancy',
        questions: 'Which best describes you right now?' + (weeks ? ' · How far along are you?' : ''),
        text: lead + CONSULT
      };
    }
    if (stage === 'breastfeeding') {
      return {
        title: 'Breastfeeding',
        questions: 'Which best describes you right now?' + (answers.baby_age ? ' · How old is your baby?' : ''),
        text: 'You’re breastfeeding, a season where your body is recovering and supporting another person too. Iron, vitamin D, choline and omega-3 needs often stay elevated through this stage, and we’ve weighted your results with that in mind.' + CONSULT + (lowMood ? LOW_MOOD_NOTE : '')
      };
    }
    if (stage === 'postnatal') {
      return {
        title: 'After Birth',
        questions: 'Which best describes you right now?' + (answers.baby_age ? ' · How old is your baby?' : ''),
        text: 'You’re recovering after birth, a season of rebuilding. Iron, folate and B12 are worth a closer look for blood and energy, and we’ve kept that in mind.' + CONSULT + (lowMood ? LOW_MOOD_NOTE : '')
      };
    }
    if (stage === 'trying') {
      return {
        title: 'Trying to Conceive',
        questions: 'Which best describes you right now?' + (answers.trying_how_long ? ' · How long have you been trying?' : ''),
        text: 'You’re trying to conceive. Folate is one of the few nutrients with good evidence for starting well before conception, so it’s something we’ve weighed carefully here.' + CONSULT
          + (answers.trying_how_long === 'over_12m' ? ' You mentioned it has been more than a year, so it may be worth talking things through with your own doctor.' : '')
      };
    }
    if (stage === 'planning') {
      return {
        title: 'Planning a Pregnancy',
        questions: 'Which best describes you right now?',
        text: 'You’re planning a pregnancy. Folate is one of the few nutrients with good evidence for starting well before conception, so we’ve included a prenatal for you to consider.' + CONSULT
      };
    }
    if (answers.partner_trying_baby === 'yes') {
      return {
        title: 'Trying for a Baby',
        questions: 'Are you and your partner trying for a baby?',
        text: 'You mentioned you and your partner are trying for a baby. The nutrition that matters most here is hers, but general diet quality, zinc and stress levels can all play a part on your side too.'
      };
    }
    if (hormoneChanges.indexOf('peri_meno') !== -1) {
      return {
        title: 'Perimenopause & Menopause',
        questions: 'Are you currently experiencing any hormone-related changes or challenges? · Which stage of menopause are you in?',
        text: 'You mentioned perimenopause or menopause-related changes. Calcium, vitamin D and magnesium needs often shift through this stage, and B-vitamins or omega-3 are worth a closer look too. We’ve factored that into your results.'
      };
    }
    if (hormoneChanges.indexOf('autoimmune_thyroid_hashi_graves') !== -1) {
      return {
        title: 'Thyroid Condition',
        questions: 'Are you currently experiencing any hormone-related changes or challenges?',
        text: 'You mentioned a diagnosed thyroid condition. Minerals like iodine and selenium interact closely with thyroid health, so we’re more conservative with those here. This is general guidance, not a substitute for your own care team.'
      };
    }
    if (answers.bio_sex === 'male' && hormoneChanges.indexOf('low_testosterone') !== -1) {
      return {
        title: 'Male Hormonal Changes',
        questions: 'Are you currently experiencing any hormone-related changes or challenges?',
        text: 'You mentioned low testosterone or hormonal changes. Sleep, stress and training load all shape how you feel here, and zinc and vitamin D are the two nutrients worth a closer look, so we\u2019ve kept them in mind. If these changes have lasted a while, a blood test with your doctor will tell you far more than a quiz can.'
      };
    }
    return null;
  }

  /* One short personal line per category, appended to its explanation. The
     first line follows sex (bio_sex), the second follows age band, and only
     for 35 and over, where nutrient needs and life load actually shift.
     Written to explain, not to alarm; every nutrient named has an EFSA
     function claim or a Nordic Nutrition Recommendations basis. */
  var SNAPSHOT_PERSONAL = {
    'Energy Levels': {
      female: 'For many women, iron is one of the first things worth a look when energy dips, especially around the monthly cycle.',
      male: 'For men, energy usually has more to do with sleep, stress and how much you train than with any single nutrient.',
      mid: 'These are often the busiest years, with work, family and short nights, and energy is usually the first place that shows.',
      later: 'From 50, many people absorb B12 less easily, which is why we look at it more closely at this stage.'
    },
    'Sleep Quality': {
      female: 'Hormonal shifts through the month can nudge sleep for many women, so the odd rough night is common.',
      male: 'For men, sleep is closely tied to stress, late screens and evening habits, so small routine changes often count as much as nutrition.',
      mid: 'This is often the stretch of life with the most going on, and sleep tends to be the first thing to give.',
      later: 'Sleep naturally gets lighter with age, so a calm evening routine and plenty of daylight matter more than ever.'
    },
    'Nutrient Coverage': {
      female: 'Before menopause women need more iron than men, so it is one of the nutrients we watch most closely.',
      male: 'Men tend to need a little more zinc and magnesium than women, so we look at those two with extra care.',
      mid: 'Busy years often mean meals on the run, and that is usually where small gaps appear.',
      later: 'After 50, calcium, vitamin D and B12 tend to matter more, partly because the body absorbs less of some of them.'
    },
    'Digestion': {
      female: 'Many women notice digestion changing through the month, so patterns tell us more than a single bad day.',
      male: 'Men often notice digestion first through stress, fast meals and alcohol, which is why we read habits alongside symptoms.',
      mid: 'Stress and irregular mealtimes tend to show up in the gut more during these years.',
      later: 'Digestion slows gradually with age, and the gut also absorbs some nutrients, like B12, less easily.'
    },
    'Stress & Mood': {
      female: 'Hormonal changes through the month and across life can influence mood, so it helps to read this alongside your cycle.',
      male: 'Stress often shows up in the body first, as tension, poor sleep or a shorter fuse, before it feels like a mood problem.',
      mid: 'This is often the stretch with the most demands at once, so steady support can matter more than a quick fix.',
      later: 'Big life transitions often land around this age, and it is normal for mood to move with them.'
    },
    'Immunity': {
      female: 'Iron and vitamin D both contribute to normal immune function, and both are worth a look for many women.',
      male: 'Zinc and vitamin D both contribute to normal immune function, and zinc is one men tend to need a little more of.',
      mid: 'Busy years with little rest are often behind the colds that keep coming back.',
      later: 'Immune defences change gradually with age, so vitamin D, especially through the dark months, matters more.'
    },
    'Muscles & Recovery': {
      female: 'Recovery is closely tied to iron and protein intake for many women, so we look at both alongside your activity.',
      male: 'Men tend to carry more muscle, so protein and magnesium usually matter most for recovery.',
      mid: 'Recovery slows a little through these years, so rest days and protein count for more than they used to.',
      later: 'After 50, keeping muscle and bones strong leans on protein, vitamin D and calcium, and recovery takes a bit longer.'
    }
  };

  function getPersonalLine(label, answers) {
    var set = SNAPSHOT_PERSONAL[label];
    if (!set) return '';
    var lines = [];
    if (answers.bio_sex === 'female' || answers.bio_sex === 'male') lines.push(set[answers.bio_sex]);
    if (answers.age_group === '35_49') lines.push(set.mid);
    else if (answers.age_group === '50_64' || answers.age_group === '65_plus') lines.push(set.later);
    return lines.join(' ');
  }

  /* ── The snapshot's cards ────────────────────────────────────────────────────
     One card per focus area she selected, in the order she picked them. Nutrient Coverage is always there: everyone
     answers the diet questions and they shape the base of every plan. A baseline area she did NOT pick (Energy, Sleep,
     Digestion, Stress & Mood, Immunity, Focus) gets a card only when her baseline answer flags something (Worth watching
     or worse), so the snapshot can explain why the plan carries, say, magnesium for a poor-sleep baseline; when her
     baseline is fine there is no card to say so. To go strict (her picks and Nutrient Coverage only), empty
     SNAPSHOT_BASELINE_CARDS. A card is a reading of
     her answers to that area's own questions, not a measurement: a score from a base of 82, a bar and one of three
     words (Looking good / Worth watching / Could use support). Until 2026-10-10 the page showed a fixed set of seven
     cards whatever she picked, so Heart, Focus, Eyes, Bones & Joints, Hair, Skin & Nails, Women's and Men's Health
     and Hormonal Balance never had one, and the Energy, Sleep and Digestion cards ignored the baseline questions
     that stand in for the area when she did not pick it. Maternity is covered by the life-stage note. */
  var SNAPSHOT_GENERAL = ['Nutrient Coverage'];
  var SNAPSHOT_BASELINE_CARDS = ['Energy Levels', 'Sleep Quality', 'Digestion', 'Stress & Mood', 'Immunity', 'Focus & Brain', 'Eye Health'];
  var SNAPSHOT_AREA_CARD = {
    energy: 'Energy Levels', sleep: 'Sleep Quality', digestion: 'Digestion', mood_stress: 'Stress & Mood',
    immunity: 'Immunity', sport: 'Muscles & Recovery', heart: 'Heart', brain: 'Focus & Brain', vision: 'Eye Health',
    bones_joints: 'Bones & Joints', beauty: 'Hair, Skin & Nails', womens_health: 'Women’s Health',
    mens_health: 'Men’s Health', hormonal_balance: 'Hormonal Balance', longevity: 'Healthy Ageing'
  };

  function snapList(v) { return Array.isArray(v) ? v : (v ? [v] : []); }
  function snapHas(answers, key, value) { return snapList(answers[key]).indexOf(value) !== -1; }
  function snapAnswered(answers, key) {
    var v = answers[key];
    return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
  }
  // The points taken off for each value she chose, capped. `table` maps an answer id to its points.
  function snapPoints(answers, key, table, cap) {
    var total = 0;
    snapList(answers[key]).forEach(function(v) { total += (table[v] || 0); });
    return cap ? Math.min(total, cap) : total;
  }

  /* Score = 82 minus points for what she told us. Where the area's own question was never asked because she did not
     pick the area, its baseline question stands in; and the menopause symptom list stands in where it replaced the
     baseline (the quiz skips the baseline once she ticks that symptom). Causes ("what do you think is behind it?") are
     explanations, not symptoms, so they are listed under "based on your answers" but not scored. */
  var SNAPSHOT_SCORES = {
    'Energy Levels': function(a) {
      var s = 82;
      if (a.energy_day) s -= snapPoints(a, 'energy_day', { afternoon_dip: 14, low_morning: 28, low_most_day: 38 });
      else if (a.baseline_energy) s -= snapPoints(a, 'baseline_energy', { dips_sometimes: 14, low_often: 28 });
      else if (snapHas(a, 'menopause_symptoms', 'meno_energy')) s -= 14;
      s -= snapPoints(a, 'tiredness_sleep_related', { yes_sleep_not_great: 10, no_daytime_fatigue: 8 });
      s -= snapPoints(a, 'caffeine_daily', { '3_4_cups': 5, '5_plus_cups': 10 });
      return s;
    },
    'Sleep Quality': function(a) {
      var s = 82;
      if (a.sleep_quality) s -= snapPoints(a, 'sleep_quality', { wake_often: 14, hard_to_fall_asleep: 14, struggle_fall_stay: 28 });
      else if (a.baseline_sleep) s -= snapPoints(a, 'baseline_sleep', { mixed: 14, poor: 28 });
      else if (snapHas(a, 'menopause_symptoms', 'meno_sleep')) s -= 14;
      s -= snapPoints(a, 'wake_refreshed', { sometimes_groggy: 12, tired_most_mornings: 26 });
      return s;
    },
    'Nutrient Coverage': function(a) {
      var s = 82;
      if (['rarely', '1_2'].indexOf(a.fruits_veg_per_day) !== -1) s -= 11;
      if (a.fish_seafood_per_week === 'rarely') s -= 11;
      if (['never', 'occasionally'].indexOf(a.dairy_calcium_foods_per_day) !== -1) s -= 9;
      s -= snapPoints(a, 'nuts_seeds_legumes_per_week', { never: 6, occasionally: 4, '1_2': 2 });
      s -= snapPoints(a, 'whole_grains_per_day', { never: 6, occasionally: 4, '1_per_day': 2 });
      s -= snapPoints(a, 'sunshine_20min', { rarely: 11, weekends: 7, few_times_week: 3 });
      s -= snapPoints(a, 'sun_exposure_coming_months', { no: 4 });
      s -= snapPoints(a, 'daily_multivitamin', { no: 7, occasionally: 4, ran_out: 4 });
      s -= snapPoints(a, 'special_diet', { vegan: 7, vegetarian: 4 });
      return s;
    },
    'Digestion': function(a) {
      var s = 82;
      if (a.digestion_most_days) s -= snapPoints(a, 'digestion_most_days', { occasional_issues: 14, frequent_issues: 28 });
      else s -= snapPoints(a, 'baseline_digestion', { occasional: 14, frequent: 28 });
      s -= snapPoints(a, 'bloated_after_meals', { sometimes: 8, often: 16 });
      s -= snapPoints(a, 'bowel_regular', { sometimes_irregular: 7, often_irregular: 14 });
      if (a.antibiotics_last_2y === 'twice_or_more') s -= 7;
      if (a.fermented_foods === 'rarely') s -= 5;
      return s;
    },
    'Stress & Mood': function(a) {
      var s = 82;
      if (a.stress_levels_lately) s -= snapPoints(a, 'stress_levels_lately', { sometimes_pressure: 14, often_affects_daily: 28 });
      else if (a.baseline_stress) s -= snapPoints(a, 'baseline_stress', { sometimes: 14, high: 28 });
      else if (snapHas(a, 'menopause_symptoms', 'meno_mood')) s -= 14;
      s -= snapPoints(a, 'mood_dips', { sometimes: 9, often_challenge: 17 });
      return s;
    },
    'Immunity': function(a) {
      var s = 82;
      if (a.colds_frequency) s -= snapPoints(a, 'colds_frequency', { few_times_year: 14, very_often: 28 });
      else s -= snapPoints(a, 'baseline_immunity', { some_winters: 14, often: 28 });
      if (a.illness_recovery_time === '7_plus_days') s -= 16;
      if (a.smoking === 'yes_regularly') s -= 12;
      return s;
    },
    'Muscles & Recovery': function(a) {
      var s = 82;
      s -= snapPoints(a, 'recovery_soreness', { sometimes: 10, often: 20 });
      if (a.joints_feel) s -= snapPoints(a, 'joints_feel', { stiff_eases: 9, less_flexible: 7, soreness_slows: 16 });
      else if (snapHas(a, 'menopause_symptoms', 'meno_joints')) s -= 9;
      if (a.protein_intake === 'low') s -= 9;
      if (a.activity_level_weeks === 'intense' && a.recovery_soreness !== 'dont_get_sore') s -= 6;
      // What slows her down after training. Sore muscles are already the soreness question above, so they only count
      // for someone who said she never gets sore.
      var slow = { cramps: 5, achy_joints: 7, drained: 5, colds: 4 };
      if (a.recovery_soreness === 'dont_get_sore' || !a.recovery_soreness) slow.sore_muscles = 5;
      s -= snapPoints(a, 'recovery_slowdowns', slow, 14);
      return s;
    },
    'Heart': function(a) {
      var s = 82;
      s -= snapPoints(a, 'heart_daily_signs', { winded_easily: 12, high_cholesterol: 10, blood_pressure: 10, family_history: 6 }, 26);
      s -= snapPoints(a, 'heart_fitness', { could_be_fitter: 8, avoid_effort: 16 });
      return s;
    },
    'Focus & Brain': function(a) {
      var s = 82;
      if (a.focus_through_day) s -= snapPoints(a, 'focus_through_day', { fine_then_fades: 10, comes_and_goes: 16, struggle_most_time: 26 });
      else s -= snapPoints(a, 'baseline_focus', { fades: 10, struggles: 26 });
      s -= snapPoints(a, 'focus_feels_like', { foggy_start: 3, distracted: 3, forgetful: 3, drained_evening: 3 }, 9);
      return s;
    },
    'Eye Health': function(a) {
      var s = 82;
      s -= snapPoints(a, 'eye_comfort', { strained_screens: 12, dry_uncomfortable: 18, vision_changing: 22 });
      // She answers one screen question or the other. Asked on top of her eye answers (a Vision pick) the hours add a little;
      // asked alone (no Vision pick) they are the whole signal, so they weigh what her eye answers would: 8 hours or more is
      // Worth watching, and the plan carries omega-3 for the same reason.
      if (a.screen_time_hours) s -= ({ '5_8h': 6, '8_plus': 12 })[a.screen_time_hours] || 0;
      else s -= ({ '5_8h': 8, '8_plus': 20 })[a.baseline_screen_time] || 0;
      return s;
    },
    'Bones & Joints': function(a) {
      var s = 82;
      if (a.joints_feel) s -= snapPoints(a, 'joints_feel', { stiff_eases: 9, less_flexible: 9, soreness_slows: 18 });
      else if (snapHas(a, 'menopause_symptoms', 'meno_joints')) s -= 9;
      s -= snapPoints(a, 'bone_concerns', { family_history: 6, past_fracture: 6, past_menopause: 6 }, 12);
      if (a.calcium_from_food === 'not_much') s -= 8;
      return s;
    },
    'Hair, Skin & Nails': function(a) {
      // Hair, skin and nails are one card, so the penalties add up, to a cap: a woman who has noticed a little of
      // everything reads lower than one who has noticed one thing, but never as low as a single serious concern.
      var p = 0;
      p += snapPoints(a, 'hair_recent', { dry_brittle: 12, oily_heavy: 8, weaker_thinner: 18 });
      p += snapPoints(a, 'scalp_texture_changes', { dry_itchy: 4, oily_quickly: 4, texture_changed: 4 }, 8);
      p += snapPoints(a, 'hair_loss_signs', { more_shedding: 5, hairline_volume_changed: 5, breaks_brushing: 5, less_fullness: 5 }, 15);
      p += snapPoints(a, 'mens_hair_recent', { thinning_temples: 12, noticeable_loss: 20, dry_brittle_lacks: 10 });
      p += snapPoints(a, 'skin_now', { sometimes_dry_tired: 10, frequently_sensitive: 16, more_mature: 10 });
      p += snapPoints(a, 'skin_concerns', { dehydrated_temporary: 4, dry_skin: 4, wrinkles: 4, imperfections: 4 }, 12);
      p += snapPoints(a, 'mens_skin_now', { oily_breakouts: 10, dry_rough_irritated: 12, aging_lines: 8 });
      p += snapPoints(a, 'mens_skin_concerns', { oiliness_breakouts: 4, dryness_irritation: 4, aging_fine_lines: 4 }, 8);
      p += snapPoints(a, 'nails_recent', { break_split: 14, soft_thin: 14, slow_uneven: 10 });
      return 82 - Math.min(p, 34);
    },
    'Women’s Health': function(a) {
      var s = 82;
      s -= snapPoints(a, 'pms_cycle_discomfort', { mild: 10, stronger: 22 });
      s -= snapPoints(a, 'pms_symptoms', { pms_mood: 3, pms_bloating: 3, pms_cramps: 3 }, 9);
      s -= snapPoints(a, 'hormonal_breakouts', { sometimes: 6, often_every_cycle: 14 });
      s -= snapPoints(a, 'cycles_regular', { sometimes_irregular: 8, often_unpredictable: 16 });
      s -= snapPoints(a, 'period_flow', { heavy: 8, very_heavy: 16 });
      return s;
    },
    'Men’s Health': function(a) {
      var s = 82;
      s -= snapPoints(a, 'mens_health_combined', { low_drive: 12, low_testosterone: 12, fertility_focus: 10, urinary_night: 12, prostate_concern: 14 }, 30);
      if (snapHas(a, 'hormone_changes_challenges', 'low_testosterone')) s -= 10;
      return s;
    },
    'Hormonal Balance': function(a) {
      var s = 82;
      s -= snapPoints(a, 'hormone_changes_challenges', { pms_cycle: 8, peri_meno: 8, hormonal_acne_irregular: 8, postpartum_breastfeeding: 8, low_testosterone: 8, autoimmune_thyroid_hashi_graves: 8 }, 24);
      s -= snapPoints(a, 'menopause_stage', { late_peri: 6, post_menopause: 6 });
      s -= snapPoints(a, 'menopause_symptoms', { meno_flushes: 3, meno_sleep: 3, meno_mood: 3, meno_fog: 3, meno_joints: 3, meno_energy: 3 }, 12);
      return s;
    }
  };

  /* The questions behind each card, for "Based on your answers to:". Only the ones she was actually asked and answered
     are shown. An entry "key:value" counts only when that value was ticked (the menopause symptom list stands in for
     several baselines). Texts are the quiz's own titles. */
  var SNAPSHOT_QUESTIONS = {
    'Energy Levels': [['energy_day', 'How would you rate your energy levels throughout the day?'], ['baseline_energy', 'How’s your energy most days?'], ['menopause_symptoms:meno_energy', 'Which of these do you notice at this stage?'], ['tiredness_sleep_related', 'Does your tiredness feel connected to your sleep?'], ['energy_causes', 'What do you think is behind it?'], ['caffeine_daily', 'How much caffeine do you consume daily?']],
    'Sleep Quality': [['sleep_quality', 'How would you describe your sleep quality?'], ['baseline_sleep', 'And how’s your sleep been lately?'], ['menopause_symptoms:meno_sleep', 'Which of these do you notice at this stage?'], ['wake_refreshed', 'How refreshed do you feel on waking?'], ['sleep_causes', 'What do you think is behind it?']],
    'Nutrient Coverage': [['fruits_veg_per_day', 'How many portions of fruit and vegetables do you eat a day?'], ['whole_grains_per_day', 'How often do you eat whole grains?'], ['nuts_seeds_legumes_per_week', 'How often do you eat nuts, seeds or legumes?'], ['fish_seafood_per_week', 'How often do you eat oily fish?'], ['dairy_calcium_foods_per_day', 'How often do you eat dairy or other calcium-rich foods?'], ['sunshine_20min', 'In the brighter months, how often do you get 20 minutes of sun on bare skin?'], ['sun_exposure_coming_months', 'Will you be spending time in the sun over the coming months?'], ['special_diet', 'Do you follow a special diet?']],
    'Digestion': [['digestion_most_days', 'How does your digestion feel most days?'], ['baseline_digestion', 'How does your digestion feel most days?'], ['bloated_after_meals', 'After meals, do you feel heavy or bloated?'], ['bowel_regular', 'How regular are your bowel movements?'], ['bowel_pattern', 'Which sounds more like you?'], ['digestion_causes', 'What do you think is behind it?'], ['antibiotics_last_2y', 'Have you taken antibiotics in the last 2 years?']],
    'Stress & Mood': [['stress_levels_lately', 'How are your stress levels lately?'], ['baseline_stress', 'How are your stress levels?'], ['menopause_symptoms:meno_mood', 'Which of these do you notice at this stage?'], ['mood_dips', 'Do you feel your mood dips frequently?'], ['stress_causes', 'What’s adding to the stress?']],
    'Immunity': [['colds_frequency', 'How often do you catch cold or get sick?'], ['baseline_immunity', 'How are you with colds and bugs?'], ['illness_recovery_time', 'When you’re unwell, how long till you feel yourself again?'], ['smoking', 'Do you smoke?']],
    'Muscles & Recovery': [['activity_level_weeks', 'How active are you most weeks?'], ['exercise_type', 'What kind of movement is part of your week?'], ['recovery_soreness', 'Do you feel sore or slow to recover after workouts?'], ['recovery_slowdowns', 'What slows you down after training?'], ['joints_feel', 'How do your joints feel most days?'], ['menopause_symptoms:meno_joints', 'Which of these do you notice at this stage?'], ['protein_intake', 'How much protein do you eat?']],
    'Heart': [['heart_daily_signs', 'Do any of these feel familiar?'], ['heart_fitness', 'How would you describe your overall cardiovascular fitness?']],
    'Focus & Brain': [['focus_through_day', 'How’s your focus through the day?'], ['baseline_focus', 'And how’s your focus through the day?'], ['focus_feels_like', 'What does it feel like?']],
    'Eye Health': [['eye_comfort', 'How do your eyes feel by end of day?'], ['screen_time_hours', 'Roughly how many hours a day do you spend on screens?'], ['baseline_screen_time', 'Roughly how many hours a day do you spend on screens?']],
    'Bones & Joints': [['joints_feel', 'How do your joints feel most days?'], ['menopause_symptoms:meno_joints', 'Which of these do you notice at this stage?'], ['joint_areas', 'Where do you feel it most?'], ['calcium_from_food', 'Do you get much calcium from food (dairy/greens)?'], ['bone_concerns', 'Is there anything about your bones we should know?']],
    'Hair, Skin & Nails': [['hair_recent', 'How would you describe your hair recently?'], ['mens_hair_recent', 'How would you describe your hair recently?'], ['scalp_texture_changes', 'Have you noticed any changes in your scalp or texture?'], ['hair_loss_signs', 'Have you noticed any signs of hair loss or thinning?'], ['skin_now', 'How would you describe your skin now?'], ['mens_skin_now', 'How would you describe your skin?'], ['skin_concerns', 'Do you have any concerns about your skin?'], ['mens_skin_concerns', 'Any specific skin concerns?'], ['nails_recent', 'How would you describe your nails recently?']],
    'Women’s Health': [['pms_cycle_discomfort', 'Do you experience PMS symptoms or cycle discomfort?'], ['pms_symptoms', 'Which of these do you notice?'], ['hormonal_breakouts', 'Do you experience hormonal breakouts?'], ['cycles_regular', 'Are your cycles regular?'], ['period_flow', 'How would you describe your periods?']],
    'Men’s Health': [['mens_health_combined', 'Any of these ring true?'], ['hormone_changes_challenges:low_testosterone', 'Are you currently experiencing any hormone-related changes or challenges?']],
    'Hormonal Balance': [['hormone_changes_challenges', 'Are you currently experiencing any hormone-related changes or challenges?'], ['menopause_stage', 'Where are you in the menopause transition?'], ['menopause_symptoms', 'Which of these do you notice at this stage?'], ['hormonal_support_current', 'Do you currently take any form of hormonal support?']]
  };

  function snapshotQuestionsText(label, answers) {
    var seen = {}, out = [];
    (SNAPSHOT_QUESTIONS[label] || []).forEach(function(entry) {
      var parts = entry[0].split(':'), key = parts[0], value = parts[1];
      var ok = value ? snapHas(answers, key, value) : snapAnswered(answers, key);
      if (ok && !seen[entry[1]]) { seen[entry[1]] = true; out.push(entry[1]); }
    });
    return out.join(' · ');
  }

  /* Longevity has no state to score ("Looking good" about a wish would be nonsense), so it is a note like the life
     stages: it names what she asked us to look after. */
  function getHealthyAgeingNote(answers) {
    var words = { mind: 'your mind', heart: 'your heart', bones: 'your bones', muscles: 'your muscles and strength', sleep: 'your sleep', vitality: 'your long-term vitality' };
    var picked = snapList(answers.longevity_focus).filter(function(v) { return words[v]; }).map(function(v) { return words[v]; });
    var text;
    if (picked.length) {
      var list = picked.length === 1 ? picked[0] : picked.slice(0, -1).join(', ') + ' and ' + picked[picked.length - 1];
      text = 'You told us you would like to look after ' + list + ' as the years go by, so we’ve given ' + (picked.length === 1 ? 'that' : 'those') + ' extra weight in your plan. Healthy ageing is mostly consistent basics, and your plan is built around them.';
    } else {
      text = 'You chose Healthy Ageing and told us you are mainly after the basics, so we’ve kept your plan simple and built it on the rest of your answers. Healthy ageing is mostly consistent basics.';
    }
    return { title: 'Healthy Ageing', text: text, questions: snapAnswered(answers, 'longevity_focus') ? 'What would you most like to look after as the years go by?' : '' };
  }

  var SNAPSHOT_META = {
    'Energy Levels': {
      desc: '<strong>Energy</strong> is about how steady you feel throughout the day, both physically and mentally. Lower scores often come down to nutrient gaps, poor sleep, or ongoing stress wearing you down over time. Finding the cause (whether it’s iron, B-vitamins, or stress support) can make a noticeable difference in how you feel.',
      barLabels: ['Low', 'High']
    },
    'Sleep Quality': {
      desc: '<strong>Sleep quality</strong> looks at how well you actually rest, from how easily you fall asleep to whether you wake up feeling refreshed. In Scandinavia, seasonal light changes can throw off your sleep rhythm more than you’d expect. Poor sleep adds up, affecting mood, immunity, and energy over time.',
      barLabels: ['Poor', 'Excellent']
    },
    'Nutrient Coverage': {
      desc: '<strong>Nutrient coverage</strong> shows how well your diet provides the vitamins and minerals your body needs. In Nordic climates, gaps in vitamin D, omega-3, and key minerals are common, even if you eat well overall. These small gaps can affect your energy, mood, and health more than you’d think.',
      barLabels: ['Gaps', 'Complete']
    },
    'Digestion': {
      desc: '<strong>Digestion</strong> is about how well your body breaks down food and absorbs nutrients from it. Bloating, irregularity, and food sensitivities can signal that your gut is out of balance. This matters because your gut affects everything from how well you absorb nutrients to your immune function and mood.',
      barLabels: ['Sensitive', 'Smooth']
    },
    'Stress & Mood': {
      desc: '<strong>Stress and mood</strong> show how you handle daily pressure and how it affects you physically. Ongoing stress uses up magnesium, B-vitamins, and other nutrients your nervous system relies on. The right nutritional support can help with both how you feel during the day and how well you sleep.',
      barLabels: ['Strained', 'Balanced']
    },
    'Immunity': {
      desc: '<strong>Immunity</strong> is about how well your body fights off illness and how quickly you recover. If you catch colds often, take a long time to get over them, or feel run down in winter, your immune system could use some support. Vitamin D, zinc, and vitamin C are particularly important during long Nordic winters.',
      barLabels: ['Vulnerable', 'Resilient']
    },
    'Muscles & Recovery': {
      desc: '<strong>Muscles and recovery</strong> is about how well your body bounces back after movement. Lingering soreness, cramps, achy joints or a slow recovery can all be signs your muscles and connective tissue are working harder than they would like. Magnesium contributes to normal muscle function, and protein matters alongside it.',
      barLabels: ['Slow', 'Fast']
    },
    'Heart': {
      desc: '<strong>Heart</strong> brings together the everyday signs you notice, what runs in your family and how fit you feel. It is a reading of your answers rather than a measurement, and it never replaces a check-up. Omega-3 (EPA and DHA) contributes to the normal function of the heart, which is why we weigh it here.',
      barLabels: ['Watchful', 'Strong']
    },
    'Focus & Brain': {
      desc: '<strong>Focus and brain</strong> looks at how steady your concentration feels through the day, from a foggy start to an afternoon fade. Sleep, stress, screens and what you eat all play a part. DHA contributes to the maintenance of normal brain function, and several B vitamins contribute to normal psychological function.',
      barLabels: ['Foggy', 'Sharp']
    },
    'Eye Health': {
      desc: '<strong>Eye health</strong> looks at how your eyes feel by the end of the day and how many hours you spend on screens. Long screen days are a common reason eyes feel tired or dry, and a change in your vision is always worth an eye test.',
      barLabels: ['Strained', 'Comfortable']
    },
    'Bones & Joints': {
      desc: '<strong>Bones and joints</strong> covers how freely you move and anything in your background that matters for your bones, such as a family history, a broken bone or the years after menopause. Vitamin D, magnesium and calcium each contribute to the maintenance of normal bones, and vitamin C contributes to normal collagen formation for the normal function of cartilage.',
      barLabels: ['Stiff', 'Free']
    },
    'Hair, Skin & Nails': {
      desc: '<strong>Hair, skin and nails</strong> shows how you describe each of them right now. Biotin contributes to the maintenance of normal hair and skin, zinc to the maintenance of normal hair, skin and nails, and vitamin C to normal collagen formation for the normal function of skin.',
      barLabels: ['Needs care', 'Thriving']
    },
    'Women’s Health': {
      desc: '<strong>Women’s health</strong> follows your cycle: PMS, breakouts, how regular your cycles are and how heavy your periods are. Patterns across several months tell us more than one hard week. Vitamin B6 contributes to the regulation of hormonal activity, and magnesium to normal psychological function.',
      barLabels: ['Disrupted', 'Steady']
    },
    'Men’s Health': {
      desc: '<strong>Men’s health</strong> looks at drive, testosterone, fertility, night-time trips to the bathroom and prostate comfort, the things men tend to notice first. If any of it has lasted a while, a blood test with your doctor will tell you far more than a quiz can. Zinc contributes to the maintenance of normal testosterone levels in the blood.',
      barLabels: ['Off balance', 'Steady']
    },
    'Hormonal Balance': {
      desc: '<strong>Hormonal balance</strong> brings together the changes you are noticing, from your cycle to perimenopause and menopause, and any hormonal support you take. These stages are normal and the symptoms often overlap, so we read them together. Vitamin B6 contributes to the regulation of hormonal activity.',
      barLabels: ['Shifting', 'Settled']
    }
  };

  /* All the cards for one set of answers (no DOM), so it can be tested on its own. */
  function computeSnapshotCategories(answers) {
    var focusAreas = Array.isArray(answers.focus_areas) ? answers.focus_areas : [];
    var labels = [];
    focusAreas.forEach(function(area) {
      var label = SNAPSHOT_AREA_CARD[area];
      if (label && labels.indexOf(label) === -1) labels.push(label);
    });
    // A baseline area she did not pick, only when its baseline answer flags something
    SNAPSHOT_BASELINE_CARDS.forEach(function(label) {
      if (labels.indexOf(label) !== -1 || !SNAPSHOT_SCORES[label]) return;
      if (Math.max(15, Math.min(90, SNAPSHOT_SCORES[label](answers))) < 65) labels.push(label);
    });
    SNAPSHOT_GENERAL.forEach(function(label) { if (labels.indexOf(label) === -1) labels.push(label); });

    var categories = labels.map(function(label) {
      if (label === 'Healthy Ageing') {
        var note = getHealthyAgeingNote(answers);
        return { label: note.title, isNote: true, noteDesc: note.text, noteQuestions: note.questions, score: 100, status: 'note', statusLabel: 'Keep in mind' };
      }
      var raw = SNAPSHOT_SCORES[label] ? SNAPSHOT_SCORES[label](answers) : 50;
      var score = Math.max(15, Math.min(90, raw));
      var status = score >= 65 ? 'solid' : score >= 40 ? 'watching' : 'support';
      var statusLabel = status === 'solid' ? 'Looking good' : status === 'watching' ? 'Worth watching' : 'Could use support';
      return {
        label: label, score: score, status: status, statusLabel: statusLabel,
        personalLine: getPersonalLine(label, answers),
        questionsText: snapshotQuestionsText(label, answers)
      };
    });

    // A card with no answers behind it would say "Looking good" about nothing (a woman over 65 is not asked the cycle
    // questions, say), so it is left out. Nutrient Coverage is always answered and always stays.
    categories = categories.filter(function(c) { return c.isNote || c.questionsText || c.label === 'Nutrient Coverage'; });

    // A life stage or circumstance worth naming plainly goes first, same accordion design as every other row (David's
    // call, mirroring the check-in page): no visual distinction, not scored.
    var notableNote = getNotableSituationNote(answers);
    if (notableNote) {
      categories.unshift({
        label: notableNote.title, isNote: true, noteDesc: notableNote.text, noteQuestions: notableNote.questions,
        score: 100, status: 'note', statusLabel: 'Keep in mind'
      });
    }
    return categories;
  }

  function buildHealthSnapshot(answers, name) {
    var categories = computeSnapshotCategories(answers);

    // Store categories globally for navigation
    window._snapshotCategories = categories;

    // Render markers (each includes hidden mobile accordion detail)
    var markersHtml = categories.map(function(cat, i) {
      var meta = SNAPSHOT_META[cat.label] || { desc: cat.noteDesc || '', barLabels: ['', ''], questions: cat.noteQuestions || '' };

      var mobileDetailHtml =
        '<div class="pp-snapshot-marker__detail">' +
          '<div class="pp-snapshot-marker__detail-heading">' + cat.label.toUpperCase() + ': ' + String(cat.statusLabel || '').toUpperCase() + '</div>' +
          '<div class="pp-snapshot-marker__detail-bar-wrap">' +
            '<div class="pp-snapshot-marker__detail-bar-track">' +
              '<div class="pp-snapshot-marker__detail-bar-fill" data-score="' + cat.score + '"></div>' +
            '</div>' +
            '<div class="pp-snapshot-marker__detail-bar-labels"><span>' + meta.barLabels[0] + '</span><span>' + meta.barLabels[1] + '</span></div>' +
          '</div>' +
          '<div class="pp-snapshot-marker__detail-desc">' + meta.desc + (cat.personalLine ? ' ' + cat.personalLine : '') + '</div>' +
        '</div>';

      return '<div class="pp-snapshot-marker" data-index="' + i + '" onclick="handleSnapshotMarkerClick(' + i + ')">' +
        '<div class="pp-snapshot-marker__label">' + cat.label + '</div>' +
        '<div class="pp-snapshot-marker__bar">' +
          '<div class="pp-snapshot-marker__bar-track">' +
            '<div class="pp-snapshot-marker__bar-fill" data-score="' + cat.score + '"></div>' +
          '</div>' +
        '</div>' +
        // Words, not a number. The score still drives the bar fill, but it is a
        // composite of quiz answers rather than a measurement, so presenting it
        // as "46/100" claims a precision the quiz cannot support.
        '<div class="pp-snapshot-marker__score">' + String(cat.statusLabel || '') + '</div>' +
        '<span class="pp-snapshot-marker__arrow"></span>' +
        mobileDetailHtml +
      '</div>';
    }).join('');

    // Render desktop expanded cards (hidden by default)
    var dotsHtml = categories.map(function(_, i) {
      return '<span class="pp-snapshot-expanded__dot" data-index="' + i + '" onclick="openSnapshotCard(' + i + ')"></span>';
    }).join('');

    var expandedCardsHtml = categories.map(function(cat, i) {
      var meta = SNAPSHOT_META[cat.label] || { desc: cat.noteDesc || '', barLabels: ['', ''], questions: cat.noteQuestions || '' };
      // The questions she actually answered for this card (notes carry their own).
      var questionsText = cat.questionsText || meta.questions || '';

      return '<div class="pp-snapshot-expanded" data-card-index="' + i + '">' +
        '<button class="pp-snapshot-expanded__back" onclick="closeSnapshotCard()">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>' +
          'Back to Overview' +
        '</button>' +
        '<div class="pp-snapshot-expanded__card">' +
          '<div class="pp-snapshot-expanded__heading">' + cat.label.toUpperCase() + ': ' + String(cat.statusLabel || '').toUpperCase() + '</div>' +
          '<div class="pp-snapshot-expanded__bar-wrap">' +
            '<div class="pp-snapshot-expanded__bar-track">' +
              '<div class="pp-snapshot-expanded__bar-fill" data-score="' + cat.score + '"></div>' +
            '</div>' +
            '<div class="pp-snapshot-expanded__bar-labels"><span>' + meta.barLabels[0] + '</span><span>' + meta.barLabels[1] + '</span></div>' +
          '</div>' +
          '<div class="pp-snapshot-expanded__desc">' + meta.desc + (cat.personalLine ? ' ' + cat.personalLine : '') + '</div>' +
          // Skipped when a row has no source questions, so the label is never left hanging over nothing.
          (questionsText
            ? '<div class="pp-snapshot-expanded__questions-label">Based on your answers to:</div>' +
              '<div class="pp-snapshot-expanded__questions">' + questionsText + '</div>'
            : '') +
        '</div>' +
        '<div class="pp-snapshot-expanded__nav">' +
          '<button class="pp-snapshot-expanded__nav-btn" onclick="navigateSnapshotCard(-1)">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 6l-6 6 6 6"/></svg>' +
          '</button>' +
          '<div class="pp-snapshot-expanded__dots">' + dotsHtml + '</div>' +
          '<button class="pp-snapshot-expanded__nav-btn" onclick="navigateSnapshotCard(1)">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';
    }).join('');

    var displayName = name || 'You';

    document.getElementById('healthSnapshotState').innerHTML =
      '<div class="pp-snapshot">' +
        '<div class="pp-snapshot__image">' +
          '<picture>' +
            '<source media="(max-width: 768px)" srcset="https://cdn.shopify.com/s/files/1/0913/4447/0345/files/pp-results-health-snapshot-mobile.webp?v=1777543701">' +
            '<source media="(max-width: 1024px)" srcset="https://cdn.shopify.com/s/files/1/0913/4447/0345/files/pp-results-health-snapshot-tablet.webp?v=1777543701">' +
            '<img src="https://cdn.shopify.com/s/files/1/0913/4447/0345/files/pp-results-health-snapshot-desktop.webp?v=1777543701" alt="Personalized supplement sachets on a cream surface - PersonaPath custom daily nutrition plan" loading="eager" fetchpriority="high">' +
          '</picture>' +
        '</div>' +
        '<div class="pp-snapshot__content">' +
          '<h2 class="pp-snapshot__title">' + displayName + ', your results are in.</h2>' +
          '<p class="pp-snapshot__subtitle">Based on your answers, your body needs targeted support in these key areas.<br>Your formulas will reflect these exact needs.</p>' +
          '<div class="pp-snapshot__markers" id="snapshotMarkersList">' + markersHtml + '</div>' +
          '<div id="snapshotExpandedArea">' + expandedCardsHtml + '</div>' +
          '<div class="pp-phase-btn-wrap">' +
            '<button class="pp-phase-btn" id="snapshotContinueBtn" type="button">' +
              '<span>Continue</span>' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h12"/><path d="M13 6l6 6-6 6"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';

    // First category opens by default so the page doesn't load as a wall of
    // closed rows. Mobile/tablet only — above 900px handleSnapshotMarkerClick
    // routes to the desktop card overlay instead of this inline accordion.
    if (window.innerWidth <= 900 && categories.length) {
      toggleSnapshotAccordion(0);
    }

    // Animate progress bars with stagger
    requestAnimationFrame(function() {
      var fills = document.querySelectorAll('.pp-snapshot-marker__bar-fill');
      fills.forEach(function(fill, i) {
        setTimeout(function() {
          fill.style.width = fill.getAttribute('data-score') + '%';
        }, 120 * i);
      });
    });
  }

  /* ── Snapshot card interaction ── */
  var _snapshotActiveCard = -1;

  function handleSnapshotMarkerClick(index) {
    if (window.innerWidth <= 900) {
      toggleSnapshotAccordion(index);
    } else {
      openSnapshotCard(index);
    }
  }

  function toggleSnapshotAccordion(index) {
    var marker = document.querySelector('.pp-snapshot-marker[data-index="' + index + '"]');
    if (!marker) return;
    var wasExpanded = marker.classList.contains('is-expanded');

    // Close all markers
    document.querySelectorAll('.pp-snapshot-marker.is-expanded').forEach(function(m) {
      m.classList.remove('is-expanded');
    });

    if (!wasExpanded) {
      marker.classList.add('is-expanded');
      // Animate the detail bar
      var barFill = marker.querySelector('.pp-snapshot-marker__detail-bar-fill');
      if (barFill) {
        barFill.style.width = '0';
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            barFill.style.width = barFill.getAttribute('data-score') + '%';
          });
        });
      }
    }
  }

  function openSnapshotCard(index) {
    var cats = window._snapshotCategories;
    if (!cats || index < 0 || index >= cats.length) return;
    _snapshotActiveCard = index;

    // Hide markers, show expanded
    var markersList = document.getElementById('snapshotMarkersList');
    if (markersList) markersList.style.display = 'none';

    // Hide all expanded cards, show target
    var allCards = document.querySelectorAll('.pp-snapshot-expanded');
    allCards.forEach(function(c) { c.classList.remove('is-active'); });
    var target = document.querySelector('.pp-snapshot-expanded[data-card-index="' + index + '"]');
    if (target) {
      target.classList.add('is-active');
      // Animate the expanded bar
      var barFill = target.querySelector('.pp-snapshot-expanded__bar-fill');
      if (barFill) {
        barFill.style.width = '0';
        requestAnimationFrame(function() {
          barFill.style.width = barFill.getAttribute('data-score') + '%';
        });
      }
    }

    // Update dot navigation
    var dots = document.querySelectorAll('.pp-snapshot-expanded__dot');
    dots.forEach(function(d) {
      d.classList.toggle('is-active', parseInt(d.getAttribute('data-index')) === index);
    });
  }

  function closeSnapshotCard() {
    _snapshotActiveCard = -1;
    var allCards = document.querySelectorAll('.pp-snapshot-expanded');
    allCards.forEach(function(c) { c.classList.remove('is-active'); });
    var markersList = document.getElementById('snapshotMarkersList');
    if (markersList) markersList.style.display = '';
  }

  function navigateSnapshotCard(direction) {
    var cats = window._snapshotCategories;
    if (!cats || !cats.length) return;
    var next = _snapshotActiveCard + direction;
    if (next < 0) next = cats.length - 1;
    if (next >= cats.length) next = 0;
    openSnapshotCard(next);
  }


  

  function getIngredientImage(key) {
    if (INGREDIENT_OVERRIDES[key]) return INGREDIENT_OVERRIDES[key];
    if (INGREDIENT_DATA[key]) return INGREDIENT_DATA[key].image;
    var normalized = (key || '').toLowerCase().trim();
    if (INGREDIENT_OVERRIDES[normalized]) return INGREDIENT_OVERRIDES[normalized];
    var foundHandle = INGREDIENT_BY_NAME[normalized];
    if (foundHandle && INGREDIENT_OVERRIDES[foundHandle]) return INGREDIENT_OVERRIDES[foundHandle];
    if (foundHandle && INGREDIENT_DATA[foundHandle]) return INGREDIENT_DATA[foundHandle].image;
    return '';
  }


  // ═══════════════════════════════════
  // PRODUCT DATA (fetched from server)
  // ═══════════════════════════════════
  // These are populated by fetchProductData() before rendering.
  // Single source of truth lives in the Cloudflare Worker.

  var PRODUCT_DATA_URL = WORKER_URL.replace('/api/quiz-results', '/api/product-data');
  var PRODUCT_NUTRIENTS = {};
  var NUTRIENT_DETAIL = {};
  var PRODUCT_WHY = {};

  /* ════════════════════════════════════════════════════════════════
     "See why it's here" modal
     A panel docked to the left on desktop, a bottom sheet on mobile. Built lazily per product, cached after the first open.

     Where each tab gets its content:
       Description  the worker's pre-written paragraph (p.copy.full), the plain description and the interaction notes
       the rest     the product's OWN content, fetched as an HTML fragment from templates/product.pp-modal.liquid, which
                    reads the same product metafields the product page reads. Edit a value in Shopify and the product
                    page and this modal change together; nothing is copied into this file or into the worker.
     A product with no storefront page (DRAFT) answers 404 for the fragment. The modal then keeps the Description tab and
     falls back to the worker's nutrient list, and hides the tabs it has nothing for.

     Per-card button registers via registerWhyModalData() during card render. window.ppOpenWhyModal(productId) builds on
     first call, caches, and opens.
     ════════════════════════════════════════════════════════════════ */

  var WHY_MODAL_CACHE = {};
  var WHY_MODAL_DATA = {};
  var WHY_FRAGMENTS = {};

  // Tab key -> label key in PP_WHY_MODAL_I18N. The key is also the data-pp-part name the fragment uses ('why' has none).
  var WHY_TABS = [
    { key: 'why', label: 'tab_description' },
    { key: 'benefits', label: 'tab_benefits' },
    { key: 'nutrients', label: 'tab_nutrients' },
    { key: 'usage', label: 'tab_usage' },
    { key: 'studies', label: 'tab_research' },
    { key: 'faq', label: 'tab_faq' }
  ];

  function whyT(shortKey) {
    var i18n = window.PP_WHY_MODAL_I18N || {};
    return i18n[shortKey] != null ? i18n[shortKey] : shortKey;
  }

  function whyEscapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function registerWhyModalData(p, results, focusAreas, warnings) {
    if (!p || !p.id) return;
    WHY_MODAL_DATA[p.id] = {
      p: p,
      results: results,
      focusAreas: focusAreas || [],
      warnings: getDisplayWarnings(warnings, p.id)
    };
  }

  // ── Content that does not need the product page ──────────────────

  // Description tab: why it is here, then (once the fragment arrives) the plain description, then the interaction notes.
  function renderWhyPanel(p, focusAreas, warnings) {
    // The paragraph is composed by the worker from pre-written parts and arrives as p.copy.full. The hand-written product
    // copy is the fallback for a card the worker sent without copy.
    var purpose = p.copy && p.copy.full ? p.copy.full : '';
    if (!purpose && typeof getConversationalWhy === 'function') {
      purpose = getConversationalWhy(p.id, focusAreas || [], p.reason && p.reason.indication);
    }
    if (!purpose && Array.isArray(p.reasons) && p.reasons[0]) {
      purpose = (typeof p.reasons[0] === 'string') ? p.reasons[0] : (p.reasons[0].reason || '');
    }

    var purposeHtml = String(purpose || '').split(/\n{2,}/).filter(function(part) { return part.trim(); }).map(function(part) {
      return '<p>' + whyEscapeHtml(part.trim()) + '</p>';
    }).join('');

    var sectionA = purposeHtml
      ? '<h3 class="pp-why-panel__heading">' + whyEscapeHtml(whyT('why_heading')) + '</h3>' +
        '<div class="pp-why-purpose">' + purposeHtml + '</div>'
      : '';

    // A guidance card (a need we cannot meet) adds what to look for on a label elsewhere, right under the guidance.
    var guideHtml = '';
    if (p.__guide && Array.isArray(p.__guide.points) && p.__guide.points.length) {
      guideHtml = '<h3 class="pp-why-panel__heading">' + whyEscapeHtml((window.PP_YML_I18N && window.PP_YML_I18N.look_for) || 'What to look for') + '</h3>' +
        '<ul class="pp-why-guide">' + p.__guide.points.map(function(pt) { return '<li>' + whyEscapeHtml(pt) + '</li>'; }).join('') + '</ul>';
    }
    sectionA += guideHtml;

    // Filled by applyWhyFragment(). Hidden until then so a product with no description leaves no gap.
    var sectionB = '<div class="pp-why-about" data-why-slot="about" hidden></div>';

    // Interaction notes, shown only when the user declared the medication or
    // health condition that produced them. Sits below the description so the
    // reader gets what the product is for before what to keep in mind.
    var notes = Array.isArray(warnings) ? warnings : [];
    var sectionC = notes.length
      ? '<h3 class="pp-why-panel__heading">' + whyEscapeHtml(whyT('interaction_heading')) + '</h3>' +
        '<div class="pp-why-note">' +
          notes.map(function(w) {
            return '<p class="pp-why-note__item">' + whyEscapeHtml(w.message) + '</p>';
          }).join('') +
        '</div>'
      : '';

    return sectionA + sectionB + sectionC;
  }

  // Fallback for the Nutrients tab when the product page has nothing to show (or cannot be reached): the worker's list.
  function renderNutrientsPanel(p) {
    var nutrients = (typeof PRODUCT_NUTRIENTS !== 'undefined' && PRODUCT_NUTRIENTS[p.id]) || [];
    if (nutrients.length === 0) return '';

    return nutrients.map(function(n) {
      var detail = (typeof NUTRIENT_DETAIL !== 'undefined' && NUTRIENT_DETAIL[n.name]) || {};
      var img = (typeof getIngredientImage === 'function')
        ? (getIngredientImage(n.metaHandle) || getIngredientImage(n.matchName || ''))
        : '';
      var nrv = (detail.nrv && detail.nrv !== '—') ? detail.nrv : '';
      var areas = (detail.areas || []).slice(0, 4);
      var tagsHtml = areas.map(function(a) {
        return '<span class="pp-why-nutrient__tag">' + whyEscapeHtml(a) + '</span>';
      }).join('');

      return '<div class="pp-why-nutrient">' +
        '<div class="pp-why-nutrient__row">' +
          '<div class="pp-why-nutrient__icon">' + (img ? '<img src="' + whyEscapeHtml(img) + '" alt="' + whyEscapeHtml(n.name) + '">' : '') + '</div>' +
          '<div class="pp-why-nutrient__info">' +
            '<div class="pp-why-nutrient__name">' + whyEscapeHtml(n.name) + '</div>' +
            (detail.form ? '<div class="pp-why-nutrient__sub">' + whyEscapeHtml(detail.form) + '</div>' : '') +
          '</div>' +
          '<div class="pp-why-nutrient__values">' +
            (detail.dose ? '<span class="pp-why-nutrient__qty">' + whyEscapeHtml(detail.dose) + '</span>' : '') +
            (nrv ? '<span>' + whyEscapeHtml(nrv) + '</span>' : '') +
          '</div>' +
        '</div>' +
        (n.shortReason ? '<div class="pp-why-nutrient__reason">' + whyEscapeHtml(n.shortReason) + '</div>' : '') +
        (tagsHtml ? '<div class="pp-why-nutrient__tags">' + tagsHtml + '</div>' : '') +
      '</div>';
    }).join('');
  }

  // The little outlined pills under the rating: the areas the product's nutrients are for, same source as the card's pill.
  function whyAreaPills(p) {
    // The same tags as on the card, so the card and its panel agree; the nutrient areas below are the fallback.
    var tags = cardTagLabels(p);
    if (tags && tags.length) return tags;
    var nutrients = (typeof PRODUCT_NUTRIENTS !== 'undefined' && PRODUCT_NUTRIENTS[p.id]) || [];
    var seen = {};
    var out = [];
    nutrients.forEach(function(n) {
      var detail = (typeof NUTRIENT_DETAIL !== 'undefined' && NUTRIENT_DETAIL[n.name]) || {};
      (detail.areas || []).forEach(function(a) {
        var k = String(a).toLowerCase();
        if (!seen[k] && out.length < 3) { seen[k] = true; out.push(a); }
      });
    });
    return out;
  }

  // ── The product page's own content ───────────────────────────────

  // Resolves to the fragment element, or null (DRAFT product = 404, offline, anything else). Never rejects, and a repeat call
  // (hover prefetch, then the real open) shares the one request.
  function loadWhyFragment(handle) {
    if (!handle) return Promise.resolve(null);
    if (WHY_FRAGMENTS[handle]) return WHY_FRAGMENTS[handle];
    WHY_FRAGMENTS[handle] = fetch(SHOPIFY_ROOT + 'products/' + encodeURIComponent(handle) + '?view=pp-modal', { credentials: 'same-origin' })
      .then(function(res) { return res.ok ? res.text() : null; })
      .then(function(html) {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pp-frag]');
      })
      .catch(function() { return null; });
    return WHY_FRAGMENTS[handle];
  }

  // Same steps the product page runs (snippets/pp-product-details.liquid): drop a leading "Ingredients:" style label from the
  // copy, then turn the nutritional information list into the Active ingredients table.
  function renderWhyNutrition(panel) {
    var N = window.PersonaPathNutrition;
    if (!N) return;
    panel.querySelectorAll('[data-pp-strip-leading-label]').forEach(function(copy) {
      var label = copy.getAttribute('data-pp-strip-leading-label');
      var defaultLabel = copy.getAttribute('data-pp-default-label');
      copy.innerHTML = N.stripLeadingLabelHtml(copy.innerHTML, label);
      if (defaultLabel && defaultLabel !== label) {
        copy.innerHTML = N.stripLeadingLabelHtml(copy.innerHTML, defaultLabel);
      }
    });
    var source = panel.querySelector('[data-pp-nutrition-source]');
    var target = panel.querySelector('[data-pp-nutrition-table]');
    if (!source || !target) return;
    try {
      N.renderNutritionTable(source, target);
    } catch (err) {
      source.hidden = false;
      target.hidden = true;
    }
  }

  // The FAQ answers are measured and animated by height (pp-modal-helper.js). An item that starts open needs its height set
  // once the panel is actually visible, because a hidden panel measures 0.
  function sizeWhyOpenFaqs(panel) {
    panel.querySelectorAll('.ppm__faq-item.is-open .ppm__faq-a').forEach(function(a) {
      a.style.height = a.scrollHeight + 'px';
    });
  }

  // The product's rich text keeps every typed line break as a <br>, so a blank line is two of them in a row and a paragraph often
  // starts or ends with a stray one. Both read as large gaps in a small panel.
  var WHY_EDGE_BREAKS = /^(?:\s|<br\s*\/?>)+|(?:\s|<br\s*\/?>)+$/gi;

  // Keep the author's line breaks but trim the edges and cap a run at one blank line.
  // How to use tab: same boxes as the product page accordion (snippets/pp-product-details.liquid). Fills the dose stat from
  // the first text and cuts each text into boxes; the last box of a text is the blue note.
  function whyBuildUsage(panel) {
    var num = '(?:\\d+(?:[.,]\\d+)?|one|two|three|four|five|six|en|et|to|tre|fire|fem|seks)';
    var unit = '(?:capsules?|kapsler|kapsel|tablets?|tabletter|tablet|sachets?|breve|softgels?|soft gels?|drops?|dråber|g|mg)';
    var words = { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', en: '1', et: '1', to: '2', tre: '3', fire: '4', fem: '5', seks: '6' };
    var amount = num + '\\s*(?:to|til|-)?\\s*(?:\\d+(?:[.,]\\d+)?\\s*)?' + unit;
    var patterns = [
      new RegExp('(?:take|use|consume|tag|brug|indtag)\\s*(?:up to\\s*)?(' + amount + ')', 'i'),
      new RegExp('(' + amount + ')\\s*(?:per day|daily|each day|a day|dagligt|om dagen|per dag)', 'i')
    ];
    var copies = panel.querySelectorAll('[data-pp-usage-copy]');
    var dose = panel.querySelector('[data-pp-usage-dose]');
    if (dose && copies.length) {
      var text = copies[0].textContent || '';
      for (var i = 0; i < patterns.length; i++) {
        var m = text.match(patterns[i]);
        if (m && m[1]) {
          dose.textContent = m[1].replace(/\b(one|two|three|four|five|six|en|et|to|tre|fire|fem|seks)\b/gi, function(w) { return words[w.toLowerCase()]; }).replace(/\s+/g, ' ').trim();
          break;
        }
      }
    }
    copies.forEach(function(copy) {
      var holder = document.createElement('div');
      holder.innerHTML = copy.innerHTML;
      var wrap = document.createElement('div');
      wrap.className = 'pp-wm-usage__segments';
      var rich = holder.querySelector('.metafield-rich_text_field');
      Array.prototype.forEach.call((rich || holder).children, function(el) {
        if (!(el.textContent || '').replace(/\u00a0/g, ' ').trim()) return;
        var seg = document.createElement('div');
        seg.className = 'pp-wm-usage__segment';
        seg.innerHTML = el.tagName === 'P' ? el.innerHTML : el.outerHTML;
        wrap.appendChild(seg);
      });
      if (wrap.children.length) copy.replaceChildren(wrap);
    });
  }

  function whyTidyRichText(root) {
    root.querySelectorAll('p').forEach(function(p) {
      if (p.closest('[data-pp-nutrition-source]')) return; // the nutrition parser reads that markup as it is
      var html = p.innerHTML.replace(WHY_EDGE_BREAKS, '').replace(/(?:\s*<br\s*\/?>){3,}/gi, '<br><br>');
      if (html !== p.innerHTML) p.innerHTML = html;
    });
  }

  // For running prose (the description): a blank line becomes a real paragraph break, so the spacing is ours.
  function whySplitParagraphs(root) {
    root.querySelectorAll('p').forEach(function(p) {
      var chunks = p.innerHTML.split(/(?:\s*<br\s*\/?>\s*){2,}|(?:\s*\n){2,}\s*/i).map(function(c) {
        return c.replace(WHY_EDGE_BREAKS, '');
      }).filter(Boolean);
      if (chunks.length < 2) { p.innerHTML = chunks[0] || ''; return; }
      chunks.forEach(function(html) {
        var np = document.createElement('p');
        np.innerHTML = html;
        p.parentNode.insertBefore(np, p);
      });
      p.remove();
    });
  }

  // The study references hold their links as plain text. Make them clickable, and show only the first few of a long list
  // (one nutrient has 50+) behind a "Show all" toggle, as the product page's ingredient modal does.
  var WHY_STUDIES_PREVIEW = 5;
  function whyEnhanceStudies(panel) {
    var walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function(node) {
      if (!node.parentNode || (node.parentNode.closest && node.parentNode.closest('a'))) return;
      var text = node.nodeValue;
      if (!/https?:\/\//.test(text)) return;
      var re = /https?:\/\/[^\s<>"]+/g;
      var frag = document.createDocumentFragment();
      var last = 0;
      var m;
      while ((m = re.exec(text))) {
        var url = m[0].replace(/[.,;:)\]]+$/, ''); // closing punctuation belongs to the sentence, not the link
        frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        var a = document.createElement('a');
        a.href = url;
        a.textContent = url;
        frag.appendChild(a);
        last = m.index + url.length;
        re.lastIndex = last;
      }
      frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });

    panel.querySelectorAll('.pp-wm-studies').forEach(function(group) {
      var list = group.querySelector('.pp-wm-studies__list');
      var count = list ? list.querySelectorAll('p').length : 0;
      if (count <= WHY_STUDIES_PREVIEW) return;
      var showAll = whyT('studies_show_all').replace('%n%', count);
      list.classList.add('is-collapsed');
      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'pp-wm-studies__toggle';
      toggle.textContent = showAll;
      toggle.addEventListener('click', function() {
        var collapsed = list.classList.toggle('is-collapsed');
        toggle.textContent = collapsed ? showAll : whyT('studies_show_less');
      });
      group.appendChild(toggle);
    });
  }

  function applyWhyFragment(modalEl, frag, p) {
    var parts = {};
    if (frag) {
      frag.querySelectorAll('[data-pp-part]').forEach(function(el) {
        parts[el.getAttribute('data-pp-part')] = el;
      });
    }
    var aboutSource = frag ? frag.getAttribute('data-about-source') : '';
    var productName = (p.product && p.product.name) || '';

    // No fragment means no product page to send anyone to (a DRAFT product answers 404), so the name and image stop being links.
    if (!frag) {
      modalEl.querySelectorAll('[data-why-product-link]').forEach(function(a) {
        ['href', 'target', 'rel', 'title', 'aria-label'].forEach(function(attr) { a.removeAttribute(attr); });
        a.classList.add('is-unlinked');
      });
    }

    // Header: rating and serving, as the product page shows them.
    var ratingSlot = modalEl.querySelector('[data-why-slot="rating"]');
    if (ratingSlot && parts.rating) {
      ratingSlot.innerHTML = parts.rating.innerHTML;
      var rw = ratingSlot.querySelector('.pp-rw');
      // On the product page this link scrolls to the reviews section. There is none here.
      if (rw) { rw.removeAttribute('href'); rw.removeAttribute('onclick'); }
    }
    var servingSlot = modalEl.querySelector('[data-why-slot="serving"]');
    if (servingSlot && parts.serving) servingSlot.textContent = parts.serving.textContent;

    // Description tab: the plain description, below the "why".
    var aboutSlot = modalEl.querySelector('[data-why-slot="about"]');
    if (aboutSlot && parts.about) {
      aboutSlot.innerHTML =
        '<h3 class="pp-why-panel__heading">' + whyEscapeHtml(whyT('about_heading').replace('%name%', productName)) + '</h3>' +
        '<div class="pp-why-about__text">' + parts.about.innerHTML + '</div>';
      whySplitParagraphs(aboutSlot.querySelector('.pp-why-about__text'));
      aboutSlot.hidden = false;
    }

    // The other tabs. A tab with nothing behind it is hidden rather than left empty.
    ['benefits', 'nutrients', 'usage', 'studies', 'faq'].forEach(function(key) {
      var panel = modalEl.querySelector('[data-why-panel="' + key + '"]');
      var tab = modalEl.querySelector('[data-why-tab="' + key + '"]');
      if (!panel || !tab) return;
      var html = parts[key] ? parts[key].innerHTML : '';

      // The description already shows the answer to "What does this supplement do?", so the FAQ does not repeat it.
      if (key === 'faq' && html && aboutSource === 'what_does_this_supplement_do') {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var first = holder.querySelector('.ppm__faq-item');
        if (first) first.remove();
        var next = holder.querySelector('.ppm__faq-item');
        if (next) { next.classList.add('is-open'); html = holder.innerHTML; } else { html = ''; }
      }

      // (a guidance card has no product of ours behind it, so there is no nutrient list of ours to fall back on)
      if (!html && key === 'nutrients' && !p.__guide) html = renderNutrientsPanel(p);

      panel.innerHTML = html;
      tab.hidden = !html;
      panel.setAttribute('data-why-ready', html ? '1' : '0');
      if (html) whyTidyRichText(panel);
      if (html && key === 'usage') whyBuildUsage(panel);
      if (key === 'nutrients' && html) renderWhyNutrition(panel);
      if (key === 'studies' && html) whyEnhanceStudies(panel);
      if (html) {
        panel.querySelectorAll('a[href]').forEach(function(a) {
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener');
        });
      }
      if (key === 'faq' && panel.classList.contains('is-active')) sizeWhyOpenFaqs(panel);
    });

    // The shopper may be reading a tab that turned out to have nothing.
    var active = modalEl.querySelector('.pp-why-tab.is-active');
    if (active && active.hidden) selectWhyTab(modalEl, 'why');
  }

  // ── Building and driving the modal ───────────────────────────────

  function selectWhyTab(modalEl, key) {
    var activeTab = null;
    modalEl.querySelectorAll('.pp-why-tab').forEach(function(b) {
      var on = b.getAttribute('data-why-tab') === key;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) activeTab = b;
    });
    modalEl.querySelectorAll('.pp-why-panel').forEach(function(panel) {
      var match = panel.getAttribute('data-why-panel') === key;
      panel.classList.toggle('is-active', match);
      if (match && key === 'faq') sizeWhyOpenFaqs(panel);
    });
    var body = modalEl.querySelector('[data-ppm-body]');
    if (body) body.scrollTop = 0;
    // Six tabs do not fit a phone: keep the chosen one in view.
    var strip = modalEl.querySelector('.pp-why-modal__tabs');
    if (activeTab && strip && strip.scrollWidth > strip.clientWidth) {
      strip.scrollTo({ left: activeTab.offsetLeft - (strip.clientWidth - activeTab.offsetWidth) / 2, behavior: 'smooth' });
    }
  }

  function buildWhyModal(p, results, focusAreas, warnings) {
    var handle = (p.product && p.product.handle) || '';
    var productName = (p.product && p.product.name) || p.id;
    var recImg = p.__guide ? (p.__image || '') : (typeof PP_REC_IMAGES !== 'undefined' && PP_REC_IMAGES[handle])
      ? PP_REC_IMAGES[handle]
      : ((typeof PP_TEST_IMG !== 'undefined' && PP_TEST_IMG) ? PP_TEST_IMG : (typeof getProductImage === 'function' ? getProductImage(handle) : ''));

    var modalJar = p.__guide ? null : ppJarFor(handle);
    if (modalJar) recImg = modalJar.src;

    var ariaTitleTemplate = whyT('aria_title');
    var ariaTitle = ariaTitleTemplate.replace('{{ product }}', productName);

    var el = document.createElement('div');
    el.className = 'pp-why-modal';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('aria-label', ariaTitle);
    el.setAttribute('tabindex', '-1');
    el.setAttribute('data-lenis-prevent', '');

    var panelIdPrefix = 'ppWhyPanel_' + String(p.id).replace(/[^a-zA-Z0-9]/g, '_');

    var closeSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6l-12 12"/></svg>';

    var pillsHtml = whyAreaPills(p).map(function(a) {
      return '<span class="pp-why-pill">' + whyEscapeHtml(a) + '</span>';
    }).join('');

    var tabsHtml = WHY_TABS.map(function(t, i) {
      return '<button type="button" class="pp-why-tab' + (i === 0 ? ' is-active' : '') + '" role="tab" aria-selected="' + (i === 0 ? 'true' : 'false') +
        '" aria-controls="' + panelIdPrefix + '_' + t.key + '" data-why-tab="' + t.key + '">' + whyEscapeHtml(whyT(t.label)) + '</button>';
    }).join('');

    var loadingHtml = '<div class="pp-why-loading" role="status">' + whyEscapeHtml(whyT('loading')) + '</div>';
    var panelsHtml = WHY_TABS.map(function(t, i) {
      return '<div class="pp-why-panel' + (i === 0 ? ' is-active' : '') + '" role="tabpanel" id="' + panelIdPrefix + '_' + t.key + '" data-why-panel="' + t.key + '">' +
        (i === 0 ? renderWhyPanel(p, focusAreas, warnings) : loadingHtml) +
      '</div>';
    }).join('');

    // The image and the name open the product's own page in a new tab. The name is the link a screen reader announces; the image is the
    // same link, so it is taken out of the tab order and the accessibility tree rather than being read twice. A product with no
    // storefront page (DRAFT) loses its link in applyWhyFragment(), because the page would only be a 404.
    var productUrl = handle ? SHOPIFY_ROOT + 'products/' + encodeURIComponent(handle) : '';
    var productLinkLabel = whyT('view_product').replace('%name%', productName);
    var linkAttrs = productUrl
      ? ' href="' + whyEscapeHtml(productUrl) + '" target="_blank" rel="noopener" title="' + whyEscapeHtml(productLinkLabel) + '"'
      : '';
    var externalSvg = '<svg class="pp-why-modal__ext" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17L17 7M8 7h9v9"/></svg>';
    // The footer's "See product" button of the "Good to know" row. A guidance card has no product of its own in the panel (no name or
    // picture link), but its button opens the page of the product whose picture it shows.
    var nameWords = String(productName).split(' ');
    var nameLast = nameWords.pop();
    var nameWithArrow = (nameWords.length ? whyEscapeHtml(nameWords.join(' ')) + ' ' : '') +
      '<span class="pp-why-modal__name-tail">' + whyEscapeHtml(nameLast) + externalSvg + '</span>';
    var seeHandle = p.__guide ? (p.__guide.imageHandle || '') : handle;
    var seeAttrs = seeHandle ? ' href="' + whyEscapeHtml(SHOPIFY_ROOT + 'products/' + encodeURIComponent(seeHandle)) + '" target="_blank" rel="noopener"' : '';

    el.innerHTML =
      '<div class="pp-why-modal__grab" data-why-drag><span class="pp-why-modal__grabbar"></span></div>' +
      '<button type="button" class="pp-why-modal__close" data-ppm-close aria-label="' + whyEscapeHtml(whyT('close')) + '">' + closeSvg + '</button>' +
      '<div class="pp-why-modal__head" data-why-drag>' +
        '<a class="pp-why-modal__thumb" data-why-product-link' + linkAttrs + ' tabindex="-1" aria-hidden="true">' +
          (recImg ? '<img src="' + whyEscapeHtml(recImg) + '"' + (modalJar ? ' data-pp-jar="' + whyEscapeHtml(handle) + '" crossorigin="anonymous"' : '') + ' alt="" loading="lazy">' : '') +
        '</a>' +
        '<div class="pp-why-modal__id">' +
          // the new-window arrow stays with the last word of the title (nowrap), so a long name can never leave it on a line of its own
          '<h2 class="pp-why-modal__name"><a class="pp-why-modal__name-link" data-why-product-link' + linkAttrs +
            ' aria-label="' + whyEscapeHtml(productLinkLabel) + '">' + nameWithArrow + '</a></h2>' +
          '<div class="pp-why-modal__rating" data-why-slot="rating"></div>' +
          '<div class="pp-why-modal__serving" data-why-slot="serving"></div>' +
          (pillsHtml ? '<div class="pp-why-modal__pills">' + pillsHtml + '</div>' : '') +
        '</div>' +
      '</div>' +
      '<div class="pp-why-modal__tabs" role="tablist">' + tabsHtml + '</div>' +
      '<div class="pp-why-modal__body" data-ppm-body data-lenis-prevent>' + panelsHtml + '</div>' +
      // (a card of the row with nothing to open gets no footer)
      ((p.__row && !seeHandle) ? '' : '<div class="pp-why-modal__foot">' + (p.__row
        // a card of the "we thought you'd like to know" row: the same button as on the card, a link to the product's page in a new window
        ? '<a class="pp-why-modal__add pp-why-modal__see pp-see-btn"' + (p.__guide ? '' : ' data-why-product-link') + seeAttrs + '>' +
            whyEscapeHtml((window.PP_YML_I18N && window.PP_YML_I18N.see_product) || 'See product') + ' ' + externalSvg + '</a>'
        // Same hooks as the card's button, so the one delegated click handler adds and removes, and syncAddButtonsFor()
        // keeps this and the card in step. data-pp-modal-add only changes the label (price, no plus sign).
        : '<button type="button" class="pp-add-btn pp-why-modal__add" data-pp-modal-add data-handle="' + whyEscapeHtml(handle) +
          '" data-product-id="' + whyEscapeHtml(String(p.id)) + '"></button>') +
      '</div>');

    // Tab navigation
    el.addEventListener('click', function(e) {
      var tab = e.target.closest ? e.target.closest('[data-why-tab]') : null;
      if (!tab) return;
      selectWhyTab(el, tab.getAttribute('data-why-tab'));
    });

    return el;
  }

  // The footer button reads "Add · price" or "✓ Added · price" from the cart, and the price from Shopify's product data.
  // Called on every open, because the cart may have changed since the modal was built.
  function refreshWhyAddButton(modalEl) {
    var btn = modalEl.querySelector('[data-pp-modal-add]');
    if (!btn || btn.dataset.ppBusy) return;
    var handle = btn.getAttribute('data-handle');
    var price = getCachedPriceKr(handle);
    if (price) btn.setAttribute('data-pp-price', price); else btn.removeAttribute('data-pp-price');
    if (PP.Cart.has(handle)) setButtonAdded(btn); else setButtonIdle(btn);
  }

  function attachWhyFocusTrap(modalEl) {
    modalEl.addEventListener('keydown', function(e) {
      if (e.key !== 'Tab') return;
      if (!modalEl.classList.contains('is-open')) return;
      var focusable = Array.prototype.filter.call(modalEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ), function(node) { return !node.disabled && node.offsetParent !== null; });
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === modalEl)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Observe class changes so when the helper or any code removes
    // is-open, we restore focus to whatever triggered the open.
    var obs = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.attributeName === 'class' && !modalEl.classList.contains('is-open')) {
          var trigger = modalEl.__whyTrigger;
          if (trigger && typeof trigger.focus === 'function') {
            trigger.focus();
          }
        }
      });
    });
    obs.observe(modalEl, { attributes: true, attributeFilter: ['class'] });
  }

  // Phone only: pull the sheet down by its handle or header to dismiss it, the way the quiz's info sheet works. Bound to those two
  // surfaces and not the whole sheet, so a touch that scrolls a long tab never reads as a dismiss.
  function initWhySheetDrag(modalEl) {
    var MOBILE = '(max-width: 768px)';
    var THRESHOLD_PX = 110;
    var VELOCITY_PX_PER_MS = 0.65;
    var TAP_SLOP_PX = 8;
    var dragging = false;
    var moved = false;
    var suppressClickUntil = 0;
    var startY = 0;
    var lastY = 0;
    var startTime = 0;

    function start(y) {
      if (!modalEl.classList.contains('is-open')) return false;
      if (!window.matchMedia(MOBILE).matches) return false;
      dragging = true;
      moved = false;
      startY = y;
      lastY = y;
      startTime = performance.now();
      modalEl.style.transition = 'none';
      modalEl.style.willChange = 'transform';
      return true;
    }
    function move(y, surface, pointerId) {
      if (!dragging) return;
      lastY = y;
      // The product image and name in the header are links, so a press that has not travelled is a tap and must stay one. The
      // pointer is claimed (so the pull keeps tracking the finger) only once it has actually moved, never on the press itself:
      // a captured pointer sends the click to the header instead of the link.
      if (!moved && Math.abs(y - startY) > TAP_SLOP_PX) {
        moved = true;
        if (surface && pointerId != null) { try { surface.setPointerCapture(pointerId); } catch (err) { /* not capturable */ } }
      }
      var dy = Math.max(0, y - startY);
      modalEl.style.transform = 'translateY(' + dy + 'px)';
      var overlay = document.querySelector('.pp-why-overlay');
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - dy / 360));
    }
    function end() {
      if (!dragging) return;
      dragging = false;
      // Letting go of a pull over the image or name must not also open the link under the finger.
      if (moved) suppressClickUntil = performance.now() + 400;
      var dy = Math.max(0, lastY - startY);
      var v = dy / Math.max(1, performance.now() - startTime);
      modalEl.style.transition = '';
      modalEl.style.transform = '';
      modalEl.style.willChange = '';
      var overlay = document.querySelector('.pp-why-overlay');
      if (overlay) overlay.style.opacity = '';
      if (dy > THRESHOLD_PX || v > VELOCITY_PX_PER_MS) closeWhyModal(modalEl);
    }

    modalEl.querySelectorAll('[data-why-drag]').forEach(function(surface) {
      surface.addEventListener('click', function(e) {
        if (performance.now() < suppressClickUntil) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      if ('PointerEvent' in window) {
        surface.addEventListener('pointerdown', function(e) {
          if (e.button !== undefined && e.button !== 0) return;
          start(e.clientY);
        }, { passive: true });
        surface.addEventListener('pointermove', function(e) { move(e.clientY, surface, e.pointerId); }, { passive: true });
        surface.addEventListener('pointerup', end, { passive: true });
        surface.addEventListener('pointercancel', end, { passive: true });
      } else {
        surface.addEventListener('touchstart', function(e) { if (e.touches && e.touches[0]) start(e.touches[0].clientY); }, { passive: true });
        surface.addEventListener('touchmove', function(e) { if (e.touches && e.touches[0]) move(e.touches[0].clientY); }, { passive: true });
        surface.addEventListener('touchend', end, { passive: true });
        surface.addEventListener('touchcancel', end, { passive: true });
      }
    });
  }

  function getWhyOverlay() {
    var overlay = document.querySelector('.pp-why-overlay');
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'pp-why-overlay';
    overlay.addEventListener('click', function() {
      var open = document.querySelector('.pp-why-modal.is-open');
      if (open) closeWhyModal(open);
    });
    document.body.appendChild(overlay);
    return overlay;
  }

  function closeWhyModal(modalEl) {
    if (!modalEl || !modalEl.classList.contains('is-open')) return;
    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
    var overlay = document.querySelector('.pp-why-overlay');
    if (overlay) overlay.classList.remove('is-open');
    if (window.ppModalHelper) window.ppModalHelper.unlockBodyMobile();
  }

  // Escape key: close why-modal if open
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.pp-why-modal.is-open');
    if (open) { e.preventDefault(); closeWhyModal(open); }
  });

  // Start the fetch before the click: by the time a hover or a touch becomes a tap, the product's content is usually here.
  function prefetchWhyFromEvent(e) {
    var card = e.target && e.target.closest ? e.target.closest('.pp-rec-card[data-handle]') : null;
    if (card) loadWhyFragment(card.getAttribute('data-handle'));
  }
  document.addEventListener('mouseover', prefetchWhyFromEvent, { passive: true });
  document.addEventListener('touchstart', prefetchWhyFromEvent, { passive: true });

  window.ppOpenWhyModal = function(productId) {
    var triggerEl = document.activeElement;
    var modalEl = WHY_MODAL_CACHE[productId];
    if (!modalEl) {
      var data = WHY_MODAL_DATA[productId];
      if (!data) {
        console.warn('[PersonaPath] No why-modal data registered for product', productId);
        return;
      }
      modalEl = buildWhyModal(data.p, data.results, data.focusAreas, data.warnings);
      WHY_MODAL_CACHE[productId] = modalEl;
      document.body.appendChild(modalEl);
      attachWhyFocusTrap(modalEl);
      initWhySheetDrag(modalEl);

      // Close button click
      modalEl.addEventListener('click', function(e) {
        if (e.target.closest('.pp-why-modal__close')) {
          closeWhyModal(modalEl);
        }
      });

      var handle = (data.p.product && data.p.product.handle) || '';
      loadWhyFragment(handle).then(function(frag) { applyWhyFragment(modalEl, frag, data.p); });
    }
    modalEl.__whyTrigger = triggerEl;
    selectWhyTab(modalEl, 'why');
    refreshWhyAddButton(modalEl);
    var overlay = getWhyOverlay();
    overlay.classList.add('is-open');
    modalEl.classList.add('is-open');
    modalEl.setAttribute('aria-hidden', 'false');
    if (window.ppModalHelper) window.ppModalHelper.lockBodyMobile();
    // Move focus into the dialog so keyboard and screen-reader users start inside it. The dialog itself takes it, not the close
    // button, which would show a focus ring the moment a mouse or finger opens the panel.
    requestAnimationFrame(function() { modalEl.focus({ preventScroll: true }); });
  };


  async function fetchProductData() {
    try {
      var res = await fetch(PRODUCT_DATA_URL);
      if (!res.ok) throw new Error('Product data fetch failed');
      var data = await res.json();
      PRODUCT_NUTRIENTS = data.productNutrients || {};
      NUTRIENT_DETAIL = data.nutrientDetail || {};
      PRODUCT_WHY = data.productWhy || {};
    } catch(err) {
      console.error('Failed to fetch product data:', err);
      // Rendering will still work, just without nutrient details
    }
  }


  // "Hair, Skin & Nails" is one focus area ("beauty"), but the product copy is written for each of its three
  // parts: hair, skin and nails. Which parts she cares about is her beauty_gateway answer (hair, skin, nails or
  // all; men's two tiles write the same answer). Until 2026-10-09 the lookup below matched "hair", "skin" and
  // "nails" but never "beauty", so no Beauty shopper could reach that copy. The parts replace "beauty" in place.
  // With no gateway answer (a very old quiz) "beauty" stays as it is and she gets the product's default copy.
  var BEAUTY_PARTS = ['hair', 'skin', 'nails'];
  function expandBeautyAreas(areas, gateway) {
    if (!Array.isArray(areas) || areas.indexOf('beauty') < 0) return areas;
    var picked = Array.isArray(gateway) ? gateway : (gateway ? [gateway] : []);
    if (picked.length === 0) return areas;
    var parts = picked.indexOf('all') >= 0 ? BEAUTY_PARTS : BEAUTY_PARTS.filter(function(p) { return picked.indexOf(p) >= 0; });
    var out = [];
    areas.forEach(function(a) {
      if (a === 'beauty') { parts.forEach(function(p) { if (out.indexOf(p) < 0) out.push(p); }); }
      else if (out.indexOf(a) < 0) out.push(a);
    });
    return out;
  }
  function onlyBeautyParts(areas) {
    return (Array.isArray(areas) ? areas : []).filter(function(a) { return BEAUTY_PARTS.indexOf(a) >= 0; });
  }
  // The part of Hair, Skin & Nails that brought THIS product into her plan, from the algorithm's reason. Skin+
  // reads as hair copy for a hair reason even if she also picked skin.
  function beautyPartOf(indication) {
    if (!indication) return '';
    if (/^(hair_|scalp_)/.test(indication)) return 'hair';
    if (/^nail_/.test(indication)) return 'nails';
    if (/^skin_/.test(indication)) return 'skin';
    return '';
  }

  function getConversationalWhy(productId, focusAreas, reasonIndication) {
    var texts = PRODUCT_WHY[productId];
    if (!texts) return '';
    var areas = Array.isArray(focusAreas) ? focusAreas : [];
    var part = beautyPartOf(reasonIndication);
    if (part && areas.indexOf(part) >= 0 && texts[part]) return texts[part];
    // Check focus areas for a specific match
    for (var i = 0; i < areas.length; i++) {
      var key = areas[i];
      // Map focus area keys to why-text keys
      var mapped = {
        heart: 'heart', sleep: 'sleep', energy: 'energy', immunity: 'immunity',
        bones_joints: 'bones_joints', skin: 'skin', hair: 'hair', nails: 'nails',
        digestion: 'digestion', brain: 'brain', vision: 'vision', stress: 'stress',
        sport: 'sport', mood_stress: 'stress', womens_health: 'womens_health', hormonal_balance: 'hormonal_balance'
      };
      var whyKey = mapped[key];
      if (whyKey && texts[whyKey]) return texts[whyKey];
    }
    // None of her focus areas has text for this product. A Longevity shopper is the case: she is asked what she would like
    // to look after, and the answer states the priority of an area she did not pick (her focus area is just "longevity"), so
    // the reason that brought the product in names the area. Without this, magnesium read "your answers about sleep, stress,
    // or muscle recovery" on a card that was there for her bones.
    var byReason = {
      bone_health_maintenance_priority: 'bones_joints', brain_focus_priority_stated: 'brain', heart_focus_priority_stated: 'heart',
      sport_focus_priority_stated: 'sport', sleep_focus_priority_stated: 'sleep'
    };
    var reasonKey = byReason[reasonIndication];
    if (reasonKey && texts[reasonKey]) return texts[reasonKey];
    return texts._default || '';
  }

  // ═══════════════════════════════════
  // NUTRIENTS SCREEN BUILDER
  // ═══════════════════════════════════

  function buildNutrientsScreen(algoResults, name) {
    var allNutrients = [];
    var seen = {};

    var allRecProducts = []
      .concat((algoResults.recommendations && algoResults.recommendations.main) || [])
      .concat((algoResults.recommendations && algoResults.recommendations.secondary) || []);

    allRecProducts.forEach(function(p) {
      var nutrients = PRODUCT_NUTRIENTS[p.id] || [];
      var label = 'Your Plan';
      nutrients.forEach(function(n) {
        var key = n.name + '|' + label;
        if (seen[key]) return;
        seen[key] = true;
        allNutrients.push({
          name: n.name,
          metaHandle: n.metaHandle,
          matchName: n.matchName || '',
          shortReason: n.shortReason || '',
          tierName: label,
          productName: p.product.name
        });
      });
    });

    if (allNutrients.length === 0) {
      document.getElementById('nutrientsState').innerHTML =
        '<div class="pp-nutrients">' +
          '<h2 class="pp-nutrients__title">Your Nutrients</h2>' +
          '<p class="pp-nutrients__subtitle">Based on your answers, no specific nutrients stood out. Keep up the great work!</p>' +
          '<div class="pp-phase-btn-wrap">' +
            '<button class="pp-phase-btn" id="nutrientsContinueBtn" type="button">' +
              '<span>See Your Plan</span>' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h12"/><path d="M13 6l6 6-6 6"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>';
      // floating nav handles navigation now
      return;
    }

    var tilesHtml = allNutrients.map(function(n) {
      var img = getIngredientImage(n.metaHandle) || getIngredientImage(n.matchName || '');
      var imgHtml = img
        ? '<img src="' + img + '" alt="' + n.name + '" loading="lazy">'
        : '<div class="pp-nutrient-placeholder"></div>';

      return '<div class="pp-nutrient-tile">' +
        '<div class="pp-nutrient-tile__img">' + imgHtml + '</div>' +
        '<div class="pp-nutrient-tile__name">' + n.name + '</div>' +
        (n.shortReason ? '<div class="pp-nutrient-tile__reason">' + n.shortReason + '</div>' : '') +
        '<div class="pp-nutrient-tile__divider"></div>' +
        '<div class="pp-nutrient-tile__tier">Included in your:<br>' + n.tierName + '</div>' +
      '</div>';
    }).join('');

    var displayName = name || '';

    document.getElementById('nutrientsState').innerHTML =
      '<div class="pp-nutrients">' +
        '<div class="pp-nutrients__hero">' +
          '<h2 class="pp-nutrients__title">Each product is made with ingredients tailored to your needs.</h2>' +
          '<p class="pp-nutrients__subtitle">Selected based on your answers. No guesswork, no filler. Just what your body told us it needs.</p>' +
        '</div>' +
        '<div class="pp-nutrients__label">' + (displayName ? displayName + '\u2019s ingredients include:' : 'Your ingredients include:') + '</div>' +
        '<div class="pp-nutrients__grid">' + tilesHtml + '</div>' +
        '<div class="pp-phase-btn-wrap">' +
          '<button class="pp-phase-btn" id="nutrientsContinueBtn" type="button">' +
            '<span>See Your Plan</span>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h12"/><path d="M13 6l6 6-6 6"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';

    // floating nav handles navigation now
  }

  // ═══════════════════════════════════
  // PLAN TYPE SWITCHER
  // ═══════════════════════════════════

  function retakeQuiz() {
    localStorage.removeItem('quizResults');
    window.location.href = SHOPIFY_ROOT + 'pages/supplement-quiz';
  }

  var _lastAnswers = null;
  var _lastName = null;


  /* Two ways in (2026-10-30, David): straight after the quiz the full sequence plays (analysing page with testimonials, snapshot, final results); a reload, a saved link
     or the returning visitor's "See my results" go straight to the FINAL results, with a back button to the snapshot. The quiz sets sessionStorage.ppResultsFlow when
     it hands over; it is read ONCE here and removed, so a reload during the sequence also lands on the final results. */
  var PP_FRESH_FROM_QUIZ = false;
  try {
    PP_FRESH_FROM_QUIZ = sessionStorage.getItem('ppResultsFlow') === '1';
    if (PP_FRESH_FROM_QUIZ) sessionStorage.removeItem('ppResultsFlow');
  } catch (e) { PP_FRESH_FROM_QUIZ = false; }

  /* Direct view: no testimonials page while the plan loads, just the spinner. */
  function ppHideAnalysingForDirectView() {
    var st = document.createElement('style');
    st.textContent = '#transitionState .pp-transition__title-row .pp-transition__title,#transitionState .pp-transition__subtitle,#transitionState .pp-testimonials-feed,#transitionState .pp-testimonials-disclaimer{display:none !important;}#transitionState{min-height:60vh;display:flex;align-items:center;justify-content:center;}';
    document.head.appendChild(st);
  }

  function backToSnapshot() {
    var header = document.getElementById('ppCustomHeader');
    if (header) header.classList.remove('is-visible');
    var transHeader = document.getElementById('ppTransHeader');
    if (transHeader) transHeader.style.display = '';
    document.getElementById('ppFloatBack').style.display = 'none';
    transitionPhase('resultsContent', 'healthSnapshotState', function() {
      currentPhase = 'healthSnapshotState';
      setFloatNav('See My Results', showFinalResults, null);
    });
  }

  async function init() {
    var quizResults = loadResults();
    if (!quizResults) return;
    if (!PP_FRESH_FROM_QUIZ) ppHideAnalysingForDirectView();

    // Earlier versions kept AI-written card text and a plan summary in this browser. The words are pre-written now,
    // so those entries are never read again; clear them once so they do not sit there for good.
    try {
      Object.keys(localStorage).forEach(function(k) {
        if (k === 'ppAiSummary' || k.indexOf('ppAiDescV1_') === 0) localStorage.removeItem(k);
      });
    } catch (e) { /* storage blocked: nothing to clear */ }

    // Phase 2a: POST snapshot once per page load. Guard prevents double-fire
    // on any internal re-render. Never awaited — must not block page render.
    if (!window.__pp_snapshot_posted) {
      window.__pp_snapshot_posted = true;
      ppPostQuizSnapshot(quizResults);
    }

    var name = quizResults.name || '';
    var answers = quizResults.answers || {};

    // Set transition title with name
    var titleEl = document.getElementById('transitionTitle');
    if (titleEl) {
      titleEl.textContent = name
        ? name + ', we\'re analyzing your answers...'
        : 'We\'re analyzing your answers...';
    }

    // Pick and render testimonials
    var testimonials = pickTransitionTestimonials(answers);
    var feed = document.getElementById('testimonialsFeed');
    if (feed) {
      feed.innerHTML = testimonials.map(function(t, i) {
        var safeQuote = (t.quote || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/"/g,'&quot;');
        var safeAuthor = (t.author || '').replace(/&/g,'&amp;').replace(/</g,'&lt;');
        return '<div class="pp-testimonial-card" id="ppTCard' + i + '">'
          + '<div class="pp-testimonial-quote">\u201C' + safeQuote + '\u201D</div>'
          + '<div class="pp-testimonial-author">\u2013 ' + safeAuthor + '</div>'
          + '</div>';
      }).join('');

      // Stagger fade-ins (first at 2s, then every 2s)
      testimonials.forEach(function(_, i) {
        setTimeout(function() {
          var card = document.getElementById('ppTCard' + i);
          if (card) card.classList.add('is-visible');
        }, 2000 + (i * 2000));
      });
    }

    // Fetch algorithm results + product data in parallel
    var results;
    try {
      var [algoResult, _] = await Promise.all([
        fetchQuizResults(answers),
        fetchProductData()
      ]);
      results = algoResult;
    } catch(err) {
      console.error('API error:', err);
      document.getElementById('transitionState').innerHTML =
        '<p style="padding:60px 20px;">Something went wrong. Please <a href="/pages/supplement-quiz">retake the quiz</a>.</p>';
      return;
    }

    if (!results.recommendations || !Array.isArray(results.recommendations.main)) {
      console.error('[PersonaPath] Worker response missing recommendations.main');
      var errEl = document.getElementById('transitionState');
      if (errEl) {
        errEl.textContent = '';
        var errP = document.createElement('p');
        errP.style.padding = '60px 20px';
        errP.appendChild(document.createTextNode('Something went wrong. Please '));
        var errA = document.createElement('a');
        errA.href = '/pages/supplement-quiz';
        errA.textContent = 'retake the quiz';
        errP.appendChild(errA);
        errP.appendChild(document.createTextNode('.'));
        errEl.appendChild(errP);
      }
      return;
    }

    // Map flat fields to nested .product shape if needed (worker may return either depending on version).
    ['main', 'secondary'].forEach(function(key) {
      results.recommendations[key] = results.recommendations[key].map(function(p) {
        if (!p.product) {
          p.product = {
            name: p.name,
            handle: p.handle,
            price: p.price,
            format: p.format,
            vegan: p.vegan,
            vegetarian: p.vegetarian,
            pregnancySafe: p.pregnancySafe,
            containsFish: p.containsFish,
          };
        }
        if (p.reasons && p.reasons.length > 0 && typeof p.reasons[0] === 'string') {
          p.reasons = p.reasons.map(function(r) {
            return typeof r === 'string' ? { reason: r } : r;
          });
        }
        return p;
      });
    });

    quizResults.recommendation = results;
    localStorage.setItem('quizResults', JSON.stringify(quizResults));

    // Prefetch Shopify product data for images and pricing
    var allHandles = []
      .concat(results.recommendations.main.map(function(p) { return p.product.handle; }))
      .concat(results.recommendations.secondary.map(function(p) { return p.product.handle; }));

    await prefetchProducts(allHandles);

    _lastAnswers = answers;
    _lastName = name;

    renderResultsPage(results, answers, name, results.warnings);

    // Sync button states with the live cart (catches stale localStorage,
    // items added/removed in other tabs, or items already in cart from a
    // Sync button states against live cart after render completes
    syncRecCardButtons();

    // Re-sync when the user switches back to this tab (catches cross-tab adds/removes)
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') syncRecCardButtons();
    });

    // Re-sync when the cart drawer mutates the cart (item removed via drawer UI)
    document.addEventListener('cart:updated', function () {
      syncRecCardButtons();
    });

    if (window.ppTrack) {
      var mainHandles = (Array.isArray(results.recommendations.main) ? results.recommendations.main : []).map(function(p) { return p.product.handle; });
      var secondaryHandles = (Array.isArray(results.recommendations.secondary) ? results.recommendations.secondary : []).map(function(p) { return p.product.handle; });

      /* Record which group each recommendation landed in, so the add-to-cart
         event further down can say whether a Main or an Optional item was the
         one taken. Until now every add looked identical in PostHog, which made
         the most useful question about this page unanswerable: does the
         algorithm's primary recommendation actually persuade anyone, or do
         people only buy the cheap optional extras? Display groupings only —
         see CLAUDE.md, there is no bundle price behind either group. */
      PP_REC_GROUP = {};
      mainHandles.forEach(function (h, i) { PP_REC_GROUP[h] = { group: 'main', position: i + 1 }; });
      secondaryHandles.forEach(function (h, i) { PP_REC_GROUP[h] = { group: 'secondary', position: i + 1 }; });

      ppTrack('results_shown', {
        main_products: mainHandles,
        secondary_products: secondaryHandles,
        main_count: mainHandles.length,
        secondary_count: secondaryHandles.length,
        total_products: mainHandles.length + secondaryHandles.length,
        has_email: !!quizResults.email
      });
    }

    // Stop spinner and enable button once everything is ready
    var spinner = document.getElementById('transitionSpinner');
    if (spinner) spinner.classList.add('is-done');

    // Show floating next button and wire it up
    var floatNext = document.getElementById('ppFloatNext');
    var nextBtn = document.getElementById('ppNextBtn');
    var nextLabel = document.getElementById('ppNextLabel');
    if (!PP_FRESH_FROM_QUIZ) {
      // Direct view: build the snapshot behind the scenes (the back button needs it) and go straight to the final results.
      buildHealthSnapshot(answers, name);
      buildNutrientsScreen(results, name);
      currentPhase = 'transitionState';
      showFinalResults();
    } else if (floatNext && nextBtn && nextLabel) {
      floatNext.style.display = '';
      nextLabel.textContent = 'See My Results';
      nextBtn.disabled = false;
      nextBtn.onclick = function() {
        buildHealthSnapshot(answers, name);
        buildNutrientsScreen(results, name);
        showHealthSnapshot();
      };
    }

    sendResultsToIntegrations(quizResults, results);

  }
  // ═══════════════════════════════════
  // DEV: RESULTS-PAGE IMAGE GALLERY  (?image_gallery=1)
  // ═══════════════════════════════════
  // Renders every product as a real .pp-rec-card in wrapping rows so the
  // results-page images (custom.results_page_image metafield, then featured
  // image, then generic pill) can be QA'd without taking the quiz. Fully gated
  // by the URL param — zero effect on normal visitors. Reload after changing a
  // metafield image to preview it (PP_REC_IMAGES is re-rendered from Liquid).
  async function renderImageGallery() {
    var products = Array.isArray(window.PP_GALLERY_PRODUCTS) ? window.PP_GALLERY_PRODUCTS.slice() : [];
    products.sort(function(a, b) { return (a.handle || '').localeCompare(b.handle || ''); });

    var style = document.createElement('style');
    style.textContent =
      '#ppImgGal{position:fixed;inset:0;overflow:auto;z-index:2147483000;background:#F5F3EF;padding:0 24px 140px 160px;-webkit-overflow-scrolling:touch;}' +
      '#ppImgGal .pp-imggal-bar{position:sticky;top:0;margin:0 -24px 44px -160px;padding:14px 24px;background:#1A1A1A;color:#fff;font:600 13px/1.45 system-ui,-apple-system,sans-serif;z-index:5;}' +
      '#ppImgGal .pp-imggal-bar code{background:rgba(255,255,255,.16);padding:1px 6px;border-radius:4px;font-size:12px;}' +
      '#ppImgGal .pp-imggal-grid{display:flex;flex-wrap:wrap;gap:60px 140px;align-items:flex-start;}' +
      '#ppImgGal .pp-imggal-item{width:280px;position:relative;}' +
      '#ppImgGal .pp-imggal-cap{font:600 11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;color:#3D6B50;margin-bottom:8px;word-break:break-all;min-height:34px;}' +
      '#ppImgGal .pp-imggal-fallback{color:#C4965A;}' +
      '@media(max-width:900px){#ppImgGal{padding-left:150px;}}';
    document.head.appendChild(style);

    var root = document.createElement('div');
    root.id = 'ppImgGal';
    var bar = document.createElement('div');
    bar.className = 'pp-imggal-bar';
    bar.innerHTML = 'DEV · Results-page image gallery — ' + products.length + ' products. ' +
      'Each card shows its <code>custom.results_page_image</code> metafield, then featured image, then the generic pill. ' +
      'Change an image in the product admin and reload to preview it.';
    root.appendChild(bar);
    var grid = document.createElement('div');
    grid.className = 'pp-imggal-grid';
    root.appendChild(grid);
    document.body.appendChild(root);

    // Prefetch product data so prices render (images already come from PP_REC_IMAGES inline).
    try { await prefetchProducts(products.map(function(p) { return p.handle; })); } catch (e) { /* prices optional */ }

    var results = { recommendations: { main: [], secondary: [] } };
    products.forEach(function(gp) {
      var item = document.createElement('div');
      item.className = 'pp-imggal-item';

      // Caption: handle + current image filename, flagged when it's only a fallback.
      var url = (window.PP_REC_IMAGES && PP_REC_IMAGES[gp.handle]) || '';
      var fname = url ? url.split('?')[0].split('/').pop() : '';
      var isDedicated = fname.indexOf('rec-') === 0 || fname.indexOf('collection-feature') === 0;
      var cap = document.createElement('div');
      cap.className = 'pp-imggal-cap';
      cap.appendChild(document.createTextNode(gp.handle));
      cap.appendChild(document.createElement('br'));
      if (fname && isDedicated) {
        cap.appendChild(document.createTextNode(fname));
      } else {
        var flag = document.createElement('span');
        flag.className = 'pp-imggal-fallback';
        flag.textContent = fname ? (fname + ' · fallback') : 'no image';
        cap.appendChild(flag);
      }
      item.appendChild(cap);

      // Real production rec-card.
      var p = { id: gp.id, product: { handle: gp.handle, name: gp.name, price: gp.price }, reasons: [] };
      var holder = document.createElement('div');
      try {
        holder.innerHTML = buildRecCard(p, [], results, []);
      } catch (e) {
        holder.innerHTML = '<div class="pp-rec-card"><div class="pp-rec-card__image"><img src="' +
          (url || (window.PP_TEST_IMG || '')) + '" alt="" loading="lazy"></div>' +
          '<div class="pp-rec-card__content"><div class="pp-rec-card__name"></div></div></div>';
        var nm = holder.querySelector('.pp-rec-card__name'); if (nm) nm.textContent = gp.name;
      }
      if (holder.firstChild) item.appendChild(holder.firstChild);
      grid.appendChild(item);
    });

    try { syncRecCardButtons(); } catch (e) { /* non-fatal */ }
  }

  document.addEventListener('DOMContentLoaded', function() {
    try {
      if (new URLSearchParams(window.location.search).get('image_gallery') === '1') {
        renderImageGallery();
        return; // skip normal quiz-driven init (and its no-results redirect)
      }
    } catch (e) { /* fall through to normal init */ }

    init();

    var logoBtn = document.getElementById('ppHeaderLogo');
    if (logoBtn) {
      logoBtn.addEventListener('click', function(e) {
        e.preventDefault();
        window.location.href = SHOPIFY_ROOT;
      });
    }

    // Was a dedicated "Luk"/exit text button on the right; removed on
    // 2026-10-06 (David's call) in favour of the quiz's pattern, where the
    // logo itself is the exit control. ppTransExit is now the logo span —
    // it carries role="button"/tabindex in the markup, so Enter/Space need
    // wiring here too since a span gets none of a real button's key handling.
    var exitBtn = document.getElementById('ppTransExit');
    if (exitBtn) {
      var confirmExit = function() {
        if (confirm('Are you sure you want to leave? Your results will still be saved.')) {
          window.location.href = SHOPIFY_ROOT;
        }
      };
      exitBtn.addEventListener('click', confirmExit);
      exitBtn.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          confirmExit();
        }
      });
    }

    // Force-hide any remaining theme sections the CSS might miss
    document.querySelectorAll('[class*="shopify-section"], [class*="ai-header"]').forEach(function(el) {
      var id = (el.id || '').toLowerCase();
      var cls = (typeof el.className === 'string' ? el.className : '').toLowerCase();
      if (
        id.includes('header') || id.includes('footer') ||
        id.includes('announcement') || id.includes('newsletter') ||
        cls.includes('header-group') || cls.includes('footer-group') ||
        cls.includes('group-header') || cls.includes('group-footer') ||
        cls.includes('ai-header')
      ) {
        el.style.display = 'none';
      }
    });

    // Delegated click handler for all .pp-add-btn buttons on the page.
    // Replaces individual onclick attributes — handles rec cards, plan-view
    // buttons, and any future pp-add-btn regardless of render order.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.pp-add-btn[data-handle]');
      if (!btn || btn.disabled || btn.dataset.ppBusy) return;
      e.stopPropagation();
      var handle = btn.getAttribute('data-handle');
      var productId = btn.getAttribute('data-product-id');
      if (!handle) return;
      addProductToCart(handle, productId, btn);
    });
  });
